/* MIDI grid. MIDI files carry note data, not audio, so this parses each file
   with @tonejs/midi and plays it back through a selectable Tone.js voice.
   At rest, each card shows a compact overview of the whole file. Once a
   card is playing, the roll switches to a scrolling DAW-style view: notes
   approach a fixed playhead position, light up while sounding, then fade
   behind it, with the currently-sounding chord named live -- small, in the
   corner, on the inline card; large and centered on the Story Mode canvas.
   A Story Mode overlay mirrors whatever's currently playing into a fixed
   1080x1920 canvas sized for Instagram Stories/Reels.
   Loops once started, tuned to restart right at the last note's actual end
   with no padded silence. Has an explicit play/pause button in the footer
   that does NOT sit over the roll itself. Tap the roll to scrub.

   Every voice routes through one shared master limiter rather than
   straight to the output, so individual voice levels can sit hot for
   loudness without hard digital clipping when notes stack into chords.

   Voices:
   - sine / triangle: real single recordings of a pure sine/triangle tone,
     played through a pool of looping Tone.Players (not Tone.Sampler) so a
     held note can sustain past the underlying file's own length, each
     shaped by its own AmplitudeEnvelope for a proper attack/decay/sustain/
     release. The recordings themselves are an exact integer number of
     wave cycles with no fade at the edges, so the loop point is
     mathematically seamless. Pool is sized generously (32 voices) so
     dense/legato passages don't run out of available voices and drop
     notes.
   - rhodes: real Rhodes recordings (Keyscape), 10 notes across the
     keyboard and 4 velocity tiers per note (soft/mid/hard/max) -- four
     separate Tone.Samplers, with a small router that picks which one
     actually fires based on how hard each note was hit. Levels were
     peak-normalized with a single uniform gain applied across all 40
     files. Shares an auto-panner for gentle stereo movement.
   - strings: a real recording of the user's own EastWest Hollywood
     Strings legato patch (con sordino, close mics, their own reverb/mix
     chain baked in), 10 notes across the keyboard, single dynamic layer
     (velocity has no audible effect on this particular patch -- its
     dynamics are driven by CC1/expression, which was set to max and
     printed into the recording, so one layer is correct here rather than
     a gap).
   - pluck: fast FM transient over a plain oscillator.
   - horns: sawtooth-based, with vibrato that eases in and drifts.
   - eerie:*: real one-shots from the EERIE kit, pitched across the keyboard.

   Note-name mapping for both Rhodes and Strings sits one octave BELOW the
   source filenames' own labels: FL Studio's piano roll displays middle C
   (MIDI note 60) as "C5", one octave higher than the scientific pitch
   notation Tone.js uses (where MIDI 60 is C4) -- so a file FL labeled "C3"
   is actually scientific C2.

   Every Sampler/Player-based voice is wrapped so a trigger call landing
   before its buffer has finished loading fails quietly instead of
   throwing -- an uncaught error inside the note-scheduling setTimeout can
   otherwise leave that voice silent for the rest of playback rather than
   just skipping the one early note.

   Transport: each MIDI file's own tempo is read from its header (falls back
   to 120 if the file has no explicit tempo). Switching to a different card
   snaps the tempo display back to THAT file's native BPM, but the pitch
   offset is a standing preference and persists across files rather than
   resetting -- it only changes when you adjust it yourself. Tempo changes
   scale note timing by (targetBPM / fileBPM); pitch changes shift every
   note by a semitone offset before it's converted to frequency.

   Playback scheduling waits for Tone's AudioContext to actually resolve as
   "started" before scheduling notes, guarded by an epoch counter so a
   slower, older scheduling call can't fire after a newer one has already
   taken over.

   Tone.js and @tonejs/midi are loaded lazily, once, only if a MIDI card is
   actually on the page. */
(function () {
  var THEMES = {
    cyber: {
      lit: '#b79bff',
      dim: 'rgba(183,155,255,0.22)',
      upcoming: 'rgba(183,155,255,0.55)',
      active: '#f2eeff',
      backdrop: 'rgba(5,6,4,0.94)'
    },
    amber: {
      lit: '#ffb300',
      dim: 'rgba(255,179,0,0.16)',
      upcoming: 'rgba(255,179,0,0.45)',
      active: '#fff6e0',
      backdrop: 'rgba(8,7,5,0.94)'
    },
    acid: {
      lit: '#00e676',
      dim: 'rgba(0,230,118,0.16)',
      upcoming: 'rgba(0,230,118,0.45)',
      active: '#e8fdf5',
      backdrop: 'rgba(4,7,5,0.94)'
    },
    sunset: {
      lit: '#ff007f',
      dim: 'rgba(255,0,127,0.16)',
      upcoming: 'rgba(255,0,127,0.45)',
      active: '#ffe3f1',
      backdrop: 'rgba(7,4,7,0.94)'
    },
    ice: {
      lit: '#ffffff',
      dim: 'rgba(255,255,255,0.18)',
      upcoming: 'rgba(255,255,255,0.45)',
      active: '#e3f2fd',
      backdrop: 'rgba(4,5,7,0.95)'
    },
    sketchbook: {
      lit: '#1a1a1a',
      dim: 'rgba(26,26,26,0.12)',
      upcoming: 'rgba(26,26,26,0.35)',
      active: '#000000',
      backdrop: 'rgba(244,241,234,0.97)'
    },
    watercolor: {
      lit: 'multi',
      dim: 'rgba(0,0,0,0.08)',
      upcoming: 'rgba(0,0,0,0.22)',
      active: '#000000',
      backdrop: 'rgba(240,237,230,0.97)'
    },
    soul: {
      lit: 'multi',
      dim: 'rgba(255,255,255,0.08)',
      upcoming: 'rgba(255,255,255,0.22)',
      active: '#ffffff',
      backdrop: 'rgba(15,16,14,0.97)'
    }
  };
  var currentThemeName = 'cyber';
  var currentFormat = 'classic';
  var currentCurvesMode = 0; // Legacy master curves mode placeholder
  var showNoteCurves = false;
  var showLaneCurves = false;
  var showFlightPath = false;
  var showPlayhead = true;
  // The second stroke pass on each note deliberately overruns the corners to
  // read as a hand-drawn pen stroke. Toggleable because it is a strong look.
  var showNoteEdges = false;
  // Staff lines, brace, clefs and barlines - the engraved furniture behind the
  // notes in Ink Sketch. Off leaves the notes floating on bare paper.
  var showStaveLines = true;
  // Draws the piece as a circle rather than a line: as the end approaches, the
  // opening is already scrolling in ahead of the playhead, so the wrap has no
  // visible seam and one pass can be recorded as a perfect loop.
  var seamlessLoop = false;
  // Background media sits behind the note canvas in the story frame.
  // bgObjectUrl is tracked separately so a picked File can be revoked.
  var bgMediaOn = false;
  var bgMediaUrl = '';
  var bgMediaType = '';
  var bgObjectUrl = '';
  var bgStartSec = 0;
  // 'always' runs the background continuously; 'transport' ties it to play/pause.
  var bgPlayMode = 'always';
  var ytPlayer = null;
  var ytApiLoading = false;
  var ytVideoId = '';
  var applyAutomationToVoice = false;
  var currentChordStyle = 'classic';
  // Chord read-out timing. 'realtime' names exactly what is sounding this
  // frame. 'settled' widens the window either side of the playhead so a
  // humanised, rolled or broken chord is named by what it is going to be
  // rather than by whichever note happened to land first: measured on a real
  // file, a chord starting at 1595ms was not fully voiced until about 1700ms.
  var chordMode = 'realtime';
  var CHORD_SETTLE_SEC = 0.12;
  // Settled widens the window around the playhead so a rolled or humanised
  // chord is named by what it resolves to. The lead is deliberately 0: pushing
  // the read-out ahead of the playhead made the name change before the notes
  // sounded, which reads as wrong even though it is technically knowable.
  var CHORD_LEAD_SEC = 0;
  var showMidiNotes = true;
  var jitterNotes = false;
  var intensityBias = 1.0;
  var complexityRate = 1.0;
  var visualizerLayout = 'disabled';
  var storyCcCanvasEl = null;
  var sidebarCcCanvasEl = null;

  function hexToRgba(hex, alpha) {
    var c = hex.replace('#', '');
    var r = parseInt(c.substring(0, 2), 16);
    var g = parseInt(c.substring(2, 4), 16);
    var b = parseInt(c.substring(4, 6), 16);
    return 'rgba(' + r + ',' + g + ',' + b + ',' + alpha + ')';
  }

  // card._tracks only holds tracks that carry notes, so its array positions do
  // not line up with MIDI track indices: a leading tempo/meta track with no
  // notes shifts everything. Indexing by position therefore muted the wrong
  // track, or none at all, on any multi-track file. Match the stored trackIdx.
  function isTrackEnabled(card, trackIdx) {
    if (!card || !card._tracks) { return true; }
    for (var i = 0; i < card._tracks.length; i++) {
      if (card._tracks[i].trackIdx === trackIdx) { return card._tracks[i].enabled !== false; }
    }
    return true;
  }

  function getCCAutomationValue(card, trackIdx, t) {
    if (!card || !card._tracks || trackIdx === undefined) return null;
    
    var targetTrack = null;
    for (var i = 0; i < card._tracks.length; i++) {
      if (card._tracks[i].trackIdx === trackIdx) {
        targetTrack = card._tracks[i];
        break;
      }
    }
    if (!targetTrack || !targetTrack.controlChanges) return null;

    var ccEvents = targetTrack.controlChanges[11] || targetTrack.controlChanges['11'];
    if (!ccEvents || ccEvents.length === 0) {
      ccEvents = targetTrack.controlChanges[1] || targetTrack.controlChanges['1'];
    }
    if (!ccEvents || ccEvents.length === 0) return null;

    // Linear search for surrounding CC events
    if (t <= ccEvents[0].time) {
      var rawVal = ccEvents[0].value;
      return rawVal > 1.0 ? rawVal / 127.0 : rawVal;
    }
    if (t >= ccEvents[ccEvents.length - 1].time) {
      var rawVal = ccEvents[ccEvents.length - 1].value;
      return rawVal > 1.0 ? rawVal / 127.0 : rawVal;
    }

    for (var i = 0; i < ccEvents.length - 1; i++) {
      var e1 = ccEvents[i];
      var e2 = ccEvents[i + 1];
      if (t >= e1.time && t <= e2.time) {
        var frac = (t - e1.time) / (e2.time - e1.time);
        var rawVal1 = e1.value;
        var rawVal2 = e2.value;
        var normVal1 = rawVal1 > 1.0 ? rawVal1 / 127.0 : rawVal1;
        var normVal2 = rawVal2 > 1.0 ? rawVal2 / 127.0 : rawVal2;
        return normVal1 + (normVal2 - normVal1) * frac;
      }
    }
    return null;
  }

  var libsPromise = null;

  var SCROLL_WINDOW_SEC = 5;
  var PLAYHEAD_FRAC = 0.28;

  function loadLibs() {
    if (libsPromise) { return libsPromise; }
    libsPromise = new Promise(function (resolve, reject) {
      var s1 = document.createElement('script');
      s1.src = 'https://cdnjs.cloudflare.com/ajax/libs/tone/14.8.49/Tone.js';
      s1.onload = function () {
        var s2 = document.createElement('script');
        s2.src = 'https://cdn.jsdelivr.net/npm/@tonejs/midi@2.0.28/build/Midi.js';
        s2.onload = function () { resolve(); };
        s2.onerror = reject;
        document.head.appendChild(s2);
      };
      s1.onerror = reject;
      document.head.appendChild(s1);
    });
    return libsPromise;
  }

  function fmt(sec) {
    if (!sec || !isFinite(sec)) { return '0:00'; }
    var m = Math.floor(sec / 60), s = Math.floor(sec % 60);
    return m + ':' + (s < 10 ? '0' : '') + s;
  }

  function sizeCanvas(cv) {
    var dpr = window.devicePixelRatio || 1;
    cv.width = Math.round((cv.clientWidth || 300) * dpr);
    cv.height = Math.round((cv.clientHeight || 86) * dpr);
  }

  /* ---- chord naming ----
     Looks at whichever notes are actually sounding at this instant. For
     dense/stacked voicings, requires only that a template's chord tones
     are ALL present somewhere in the sounding notes -- extra notes beyond
     that (tensions, doubled octaves, passing tones from overlapping lines)
     are allowed rather than causing a failed match, so a big cluster still
     gets named off whatever clean triad/7th is sitting inside it. Prefers
     the richest template that fully fits, then the one whose root matches
     the actual bass note, then the one that leaves the fewest notes
     unexplained. Falls back to just listing note names if nothing full
     template fits at all. */
  var NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
  var CHORD_TEMPLATES = [
    { name: 'maj9', ivs: [0, 2, 4, 7, 11] },
    { name: '9', ivs: [0, 2, 4, 7, 10] },
    { name: 'm9', ivs: [0, 2, 3, 7, 10] },
    { name: 'maj7', ivs: [0, 4, 7, 11] },
    { name: '7', ivs: [0, 4, 7, 10] },
    { name: 'm7', ivs: [0, 3, 7, 10] },
    { name: 'm7b5', ivs: [0, 3, 6, 10] },
    { name: 'dim7', ivs: [0, 3, 6, 9] },
    { name: '6', ivs: [0, 4, 7, 9] },
    { name: 'm6', ivs: [0, 3, 7, 9] },
    { name: 'add9', ivs: [0, 2, 4, 7] },
    { name: 'madd9', ivs: [0, 2, 3, 7] },
    { name: '', ivs: [0, 4, 7] },
    { name: 'm', ivs: [0, 3, 7] },
    { name: 'dim', ivs: [0, 3, 6] },
    { name: 'aug', ivs: [0, 4, 8] },
    { name: 'sus2', ivs: [0, 2, 7] },
    { name: 'sus4', ivs: [0, 5, 7] },
    { name: '5', ivs: [0, 7] }
  ];

  function detectChord(midiNotes) {
    if (!midiNotes.length) { return ''; }
    var pcs = [];
    midiNotes.forEach(function (m) {
      var pc = ((m % 12) + 12) % 12;
      if (pcs.indexOf(pc) === -1) { pcs.push(pc); }
    });
    if (pcs.length === 1) { return NOTE_NAMES[pcs[0]]; }
    var bassPc = ((Math.min.apply(null, midiNotes) % 12) + 12) % 12;
    var best = null;
    for (var r = 0; r < 12; r++) {
      for (var t = 0; t < CHORD_TEMPLATES.length; t++) {
        var tmpl = CHORD_TEMPLATES[t];
        var templatePcs = tmpl.ivs.map(function (iv) { return (r + iv) % 12; });
        var allPresent = true;
        for (var k = 0; k < templatePcs.length; k++) { if (pcs.indexOf(templatePcs[k]) === -1) { allPresent = false; break; } }
        if (!allPresent) { continue; }
        var extras = pcs.length - templatePcs.length;
        var candidate = { root: r, name: tmpl.name, size: tmpl.ivs.length, extras: extras, bassMatch: (r === bassPc) };
        if (!best
          || candidate.size > best.size
          || (candidate.size === best.size && candidate.bassMatch && !best.bassMatch)
          || (candidate.size === best.size && candidate.bassMatch === best.bassMatch && candidate.extras < best.extras)) {
          best = candidate;
        }
      }
    }
    if (best) { return NOTE_NAMES[best.root] + best.name; }
    return pcs.map(function (pc) { return NOTE_NAMES[pc]; }).join('·');
  }

  function drawOverviewOn(cv, card, progress) {
    var notes = card._notes;
    if (!cv) { return; }
    var g = cv.getContext('2d'), w = cv.width, h = cv.height;
    if (!notes || !notes.length) {
      if (card._loading) {
        var isLight = (currentThemeName === 'sketchbook' || currentThemeName === 'watercolor');
        g.clearRect(0, 0, w, h);
        var time = (performance.now() / 1000);
        var alpha = 0.15 + 0.1 * Math.sin(time * 3.5);
        g.fillStyle = isLight ? 'rgba(0,0,0,' + alpha + ')' : 'rgba(255,255,255,' + alpha + ')';
        var lineH = Math.max(2, h * 0.045);
        g.fillRect(w * 0.1, h * 0.3, w * 0.4, lineH);
        g.fillRect(w * 0.2, h * 0.5, w * 0.6, lineH);
        g.fillRect(w * 0.15, h * 0.7, w * 0.5, lineH);
        requestAnimationFrame(function() {
          if (card._loading) { draw(card, 0); }
        });
      }
      return;
    }
    var dur = card._dur || 1;
    var minPitch = card._minPitch, maxPitch = card._maxPitch;
    var span = Math.max(1, maxPitch - minPitch);
    var playedX = progress ? progress * w : -1;
    g.clearRect(0, 0, w, h);
    var padTop = h * 0.08, rollH = h * 0.84;
    var barH = Math.max(2, h * 0.045);
    var theme = THEMES[currentThemeName];
    for (var i = 0; i < notes.length; i++) {
      var n = notes[i];
      var x = (n.time / dur) * w;
      var nw = Math.max(2, (n.duration / dur) * w - 1);
      var yFrac = 1 - ((n.midi - minPitch) / span);
      var y = padTop + yFrac * rollH - barH / 2;
      var played = x < playedX;
      var color;
      if (theme.lit === 'multi') {
        var palette = currentThemeName === 'soul' ? ['#d89a27', '#4a7b82', '#b93b26', '#e0d8c3'] : ['#2b5c8f', '#a9332a', '#caa230', '#34383a'];
        var baseHex = palette[n.midi % palette.length];
        color = played ? baseHex : hexToRgba(baseHex, 0.18);
      } else {
        color = played ? theme.lit : theme.dim;
      }
      drawOrganicNote(g, x, y, nw, barH, color, null, n.time * 7.7 + n.midi * 3.1);
    }
    if (playedX >= 0 && playedX <= w) {
      g.fillStyle = theme.lit;
      g.fillRect(playedX, 0, Math.max(1.5, w / 260), h);
    }
  }

  function drawOrganicNote(g, x, y, w, h, color, solidBg, seedVal) {
    var barH = h;
    var wbl = Math.min(2.0, Math.max(0.8, barH * 0.16));
    var seed = (jitterNotes || !seedVal) ? (Math.sin(x * 12.9898 + y * 78.233) * 43758.5453) : seedVal;
    var seededRandom = function () {
      var r = Math.sin(seed++) * 10000;
      return r - Math.floor(r);
    };
    var tlX = x + (seededRandom() - 0.5) * wbl;
    var tlY = y + (seededRandom() - 0.5) * wbl;
    var trX = x + w + (seededRandom() - 0.5) * wbl;
    var trY = y + (seededRandom() - 0.5) * wbl;
    var brX = x + w + (seededRandom() - 0.5) * wbl;
    var brY = y + h + (seededRandom() - 0.5) * wbl;
    var blX = x + (seededRandom() - 0.5) * wbl;
    var blY = y + h + (seededRandom() - 0.5) * wbl;
    var topMidX = (tlX + trX) / 2 + (seededRandom() - 0.5) * (wbl * 0.6);
    var topMidY = (tlY + trY) / 2 + (seededRandom() - 0.5) * (wbl * 0.6);
    var rightMidX = (trX + brX) / 2 + (seededRandom() - 0.5) * (wbl * 0.6);
    var rightMidY = (trY + brY) / 2 + (seededRandom() - 0.5) * (wbl * 0.6);
    var bottomMidX = (blX + brX) / 2 + (seededRandom() - 0.5) * (wbl * 0.6);
    var bottomMidY = (blY + brY) / 2 + (seededRandom() - 0.5) * (wbl * 0.6);
    var leftMidX = (tlX + blX) / 2 + (seededRandom() - 0.5) * (wbl * 0.6);
    var leftMidY = (tlY + blY) / 2 + (seededRandom() - 0.5) * (wbl * 0.6);
    g.beginPath();
    g.moveTo(tlX, tlY);
    g.quadraticCurveTo(topMidX, topMidY, trX, trY);
    g.quadraticCurveTo(rightMidX, rightMidY, brX, brY);
    g.quadraticCurveTo(bottomMidX, bottomMidY, blX, blY);
    g.quadraticCurveTo(leftMidX, leftMidY, tlX, tlY);
    if (solidBg) {
      g.fillStyle = solidBg;
      g.fill();
    }
    g.fillStyle = color;
    g.fill();
    if (showNoteEdges) {
      var overshoot = wbl * 1.6;
      g.beginPath();
      g.moveTo(tlX - seededRandom() * overshoot, tlY + (seededRandom() - 0.5) * 0.4);
      g.quadraticCurveTo(topMidX, topMidY, trX + seededRandom() * overshoot, trY + (seededRandom() - 0.5) * 0.4);
      g.moveTo(trX + (seededRandom() - 0.5) * 0.4, trY - seededRandom() * overshoot);
      g.quadraticCurveTo(rightMidX, rightMidY, brX + (seededRandom() - 0.5) * 0.4, brY + seededRandom() * overshoot);
      g.moveTo(brX + seededRandom() * overshoot, brY + (seededRandom() - 0.5) * 0.4);
      g.quadraticCurveTo(bottomMidX, bottomMidY, blX - seededRandom() * overshoot, blY + (seededRandom() - 0.5) * 0.4);
      g.moveTo(blX + (seededRandom() - 0.5) * 0.4, blY + seededRandom() * overshoot);
      g.quadraticCurveTo(leftMidX, leftMidY, tlX + (seededRandom() - 0.5) * 0.4, tlY - seededRandom() * overshoot);
      g.strokeStyle = color;
      g.lineWidth = 1.0;
      g.stroke();
    }
  }

  function formatChordRealBook(chordText) {
    if (!chordText) return '';
    return chordText
      .replace(/maj9/i, 'maj9')
      .replace(/maj7/i, 'maj7')
      .replace(/m7b5/i, '-7(b5)')
      .replace(/m7/i, '-7')
      .replace(/m9/i, '-9')
      .replace(/madd9/i, '-(add9)')
      .replace(/m6/i, '-6')
      .replace(/\bm\b/g, '-')
      .replace(/dim7/i, 'dim7')
      .replace(/dim/i, 'o')
      .replace(/aug/i, '+');
  }

  function getBowingEnvelopeValue(t, dur, isViolin) {
    if (t < 0) return 0;
    if (dur <= 0.4) {
      var att = isViolin ? 0.04 : 0.02;
      if (t <= att) {
        return t / att;
      } else if (t <= dur) {
        return 1.0;
      } else if (t <= dur + 0.4) {
        var releaseFrac = (t - dur) / 0.4;
        return Math.max(0, 1.0 - releaseFrac);
      }
      return 0;
    }

    var attTime = Math.min(0.3, dur * 0.15);
    var midTime = dur * 0.5;
    var lateTime = dur * 0.85;
    var relTime = isViolin ? 0.6 : 0.8;

    if (t <= attTime) {
      // Swell from 0.35 to 1.0
      var frac = t / attTime;
      return 0.35 + 0.65 * frac;
    } else if (t <= midTime) {
      // Dip from 1.0 to 0.52
      var frac = (t - attTime) / (midTime - attTime);
      return 1.0 - 0.48 * frac;
    } else if (t <= lateTime) {
      // Swell from 0.52 to 0.98
      var frac = (t - midTime) / (lateTime - midTime);
      return 0.52 + 0.46 * frac;
    } else if (t <= dur) {
      // Eases from 0.98 to 0.88
      var frac = (t - lateTime) / (dur - lateTime);
      return 0.98 - 0.10 * frac;
    } else if (t <= dur + relTime) {
      // Fade out from 0.88 to 0
      var frac = (t - dur) / relTime;
      return Math.max(0, 0.88 * (1.0 - frac));
    }
    return 0;
  }

  function getGlobalCCValuesAt(card, T) {
    var bpm = card._bpm || 120;
    var activeNotes = [];
    if (card._notes) {
      card._notes.forEach(function (n) {
        if (!isTrackEnabled(card, n.trackIdx)) { return; }
        // Include notes within their active window + release tail
        var relTimeVal = n.role === 'BASS' ? 0.8 : (n.role === 'MELODY' ? 0.6 : 0.7);
        if (T >= n.time && T <= n.time + n.duration + relTimeVal) {
          activeNotes.push(n);
        }
      });
    }
    
    var maxMelody = 0;
    var maxHarmony = 0;
    var maxBass = 0;
    
    activeNotes.forEach(function (n) {
      var t = T - n.time;
      var scaledT = t * complexityRate;
      var val = getProceduralNoteEnvelope(n, scaledT, bpm);
      if (n.role === 'MELODY') {
        if (val > maxMelody) maxMelody = val;
      } else if (n.role === 'BASS') {
        if (val > maxBass) maxBass = val;
      } else {
        if (val > maxHarmony) maxHarmony = val;
      }
    });
    
    return {
      melody: Math.min(1.0, maxMelody * intensityBias),
      harmony: Math.min(1.0, maxHarmony * intensityBias),
      bass: Math.min(1.0, maxBass * intensityBias)
    };
  }

  function resizeCanvasToDisplaySize(canvas) {
    var dpr = window.devicePixelRatio || 1;
    var rect = canvas.getBoundingClientRect();
    var targetW = Math.round(rect.width * dpr);
    var targetH = Math.round(rect.height * dpr);
    if (canvas.width !== targetW || canvas.height !== targetH) {
      canvas.width = targetW;
      canvas.height = targetH;
    }
  }

  function drawCCVisualizer(cv, card, elapsedSec, isOverlay) {
    resizeCanvasToDisplaySize(cv);
    var g = cv.getContext('2d'), w = cv.width, h = cv.height;
    var drawY = 0, drawH = h;
    
    if (isOverlay === 'full') {
      drawY = h * 0.08;
      drawH = h * 0.84;
    } else if (isOverlay) {
      drawY = h * 0.82;
      drawH = h * 0.18;
    }
    
    if (!isOverlay) {
      var theme = THEMES[currentThemeName];
      var isInk = (currentFormat === 'ink');
      // With background media active the canvas stays transparent so the image
      // or video behind it shows through instead of the theme backdrop.
      if (bgMediaOn && bgMediaUrl) {
        g.clearRect(0, 0, w, h);
      } else {
        var bg = isInk ? '#faf7f2' : (theme.backdrop || '#07090a');
        g.fillStyle = bg;
        g.fillRect(0, 0, w, h);
      }
      
      g.strokeStyle = isInk ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.06)';
      g.lineWidth = 1;
      [0.25, 0.5, 0.75].forEach(function (frac) {
        g.beginPath();
        g.moveTo(0, h * frac);
        g.lineTo(w, h * frac);
        g.stroke();
      });
    }
    
    var pps = w / SCROLL_WINDOW_SEC;
    var playheadX = w * PLAYHEAD_FRAC;
    var scrollStart = elapsedSec - (playheadX / pps);
    var scrollEnd = scrollStart + (w / pps);
    
    var visibleNotes = [];
    if (card && card._notes) {
      card._notes.forEach(function (n) {
        if (!isTrackEnabled(card, n.trackIdx)) { return; }
        var relTimeVal = n.role === 'BASS' ? 0.8 : (n.role === 'MELODY' ? 0.6 : 0.7);
        var noteEnd = n.time + n.duration + relTimeVal;
        if (n.time <= scrollEnd && noteEnd >= scrollStart) {
          visibleNotes.push(n);
        }
      });
    }

    var cardBpm = card ? card._bpm : 120;
    var theme = THEMES[currentThemeName];

    visibleNotes.forEach(function (n) {
      var points = [];
      var stepSec = 0.015; // 15ms step resolution
      var relTimeVal = n.role === 'BASS' ? 0.8 : (n.role === 'MELODY' ? 0.6 : 0.7);
      
      var noteColorHex = '#b79bff';
      if (theme && theme.lit === 'multi') {
        var palette = currentThemeName === 'soul' ? ['#d89a27', '#4a7b82', '#b93b26', '#e0d8c3'] : ['#2b5c8f', '#a9332a', '#caa230', '#34383a'];
        noteColorHex = palette[n.midi % palette.length];
      } else {
        noteColorHex = (theme && theme.active) || '#b79bff';
      }
      
      var strokeCol = hexToRgba(noteColorHex, 0.7);
      if (currentFormat === 'ink') {
        strokeCol = 'rgba(26, 26, 26, 0.35)';
      }
      
      var gridStartT = Math.floor(scrollStart / stepSec) * stepSec;
      var gridEndT = scrollEnd + stepSec;
      for (var t = gridStartT; t <= gridEndT; t += stepSec) {
        var cx = playheadX + (t - elapsedSec) * pps;
        // Height is scaled by Note-Bound Intensity, shading is not. Intensity
        // clips val at 1.0, so at a high setting the whole curve saturated and
        // every stroke went solid. Keep the raw envelope for ink: a point at
        // zero draws as nothing, a point at full draws solid, at any intensity.
        var rawEnv = 0;
        var val = 0;
        if (t >= n.time && t <= n.time + n.duration + relTimeVal) {
          var env = getProceduralNoteEnvelope(n, (t - n.time) * complexityRate, cardBpm);
          // A pencil stroke starts and ends light regardless of how hard the
          // middle is pressed. Taper the ink at both ends of the note so a line
          // that opens at full value still fades in, and fades out as it lifts.
          var noteLen = n.duration + relTimeVal;
          var relT = t - n.time;
          var edgeLen = Math.min(noteLen * 0.3, 0.16);
          var fIn = edgeLen > 0 ? Math.min(1, relT / edgeLen) : 1;
          var fOut = edgeLen > 0 ? Math.min(1, (noteLen - relT) / edgeLen) : 1;
          var edge = Math.max(0, Math.min(fIn, fOut));
          edge = edge * edge * (3 - 2 * edge); // smoothstep, so the fade is not linear
          rawEnv = env * edge;
          val = Math.min(1.0, env * intensityBias);
        }
        var cy = drawY + drawH * 0.95 - (val * drawH * 0.88);
        points.push({ x: cx, y: cy, v: val, a: rawEnv });
      }
      
      if (points.length >= 2) {
        // Same treatment as the flight path: nothing drawn at rest, so there is
        // no line across the bottom, and alpha follows the value.
        var FLOOR = 0.07; // below this, draw nothing at all
        g.lineWidth = 2.2;
        g.lineCap = 'round';
        g.lineJoin = 'round';
        for (var i = 1; i < points.length; i++) {
          var vAvg = (points[i - 1].v + points[i].v) / 2;
          // Ink from the raw envelope, so intensity moves the curve without
          // repainting it. Width tracks it too, the way pencil pressure does.
          var aAvg = (points[i - 1].a + points[i].a) / 2;
          if (aAvg <= FLOOR) { continue; }
          // Linear ink kept the 0-30% range clearly visible. A gamma curve pushes
          // the bottom of the range down hard, so quiet passages read as almost
          // blank paper and only the louder material carries a solid line.
          var alpha = Math.pow(Math.min(1, aAvg), 2.6);
          g.lineWidth = 1.8 * (0.25 + Math.pow(Math.min(1, aAvg), 1.7) * 0.75);
          g.strokeStyle = (currentFormat === 'ink')
            ? 'rgba(26, 26, 26, ' + (alpha * 0.5).toFixed(3) + ')'
            : hexToRgba(noteColorHex, alpha);
          g.beginPath();
          g.moveTo(points[i - 1].x, points[i - 1].y);
          g.lineTo(points[i].x, points[i].y);
          g.stroke();
        }
        
        // Real-time cursor dot intersecting the playhead line
        if (isOverlay !== 'full' && !isOverlay) {
          if (elapsedSec >= n.time && elapsedSec <= n.time + n.duration + relTimeVal) {
            var env = getProceduralNoteEnvelope(n, (elapsedSec - n.time) * complexityRate, cardBpm);
            var val = Math.min(1.0, env * intensityBias);
            var cy = drawY + drawH * 0.95 - (val * drawH * 0.88);
            
            g.fillStyle = strokeCol;
            g.beginPath();
            g.arc(playheadX, cy, 4, 0, Math.PI * 2);
            g.fill();
          }
        }
      }
    });
    
    if (showPlayhead && isOverlay !== 'full' && !isOverlay) {
      g.strokeStyle = '#ffffff';
      g.lineWidth = 1.5;
      g.beginPath();
      g.moveTo(playheadX, 0);
      g.lineTo(playheadX, h);
      g.stroke();
    }
  }
 
  function renderStoryCanvases() {
    if (!storyOpen || !current) return;
    var elapsed = isPlaying ? elapsedNow() : pausedAt;
    
    var hasStacked = (visualizerLayout === 'stacked' || visualizerLayout === 'all');
    var hasOverlay = (visualizerLayout === 'overlay' || visualizerLayout === 'all');
    var hasFullOverlay = (visualizerLayout === 'overlay_full');
    var hasSidebar = (visualizerLayout === 'sidebar' || visualizerLayout === 'all');
    
    if (storyCcCanvasEl) {
      storyCcCanvasEl.style.display = hasStacked ? 'block' : 'none';
    }
    
    if (storyCanvasEl) {
      drawScrollingOn(storyCanvasEl, current, elapsed, true);
      if (hasOverlay) {
        drawCCVisualizer(storyCanvasEl, current, elapsed, true);
      } else if (hasFullOverlay) {
        drawCCVisualizer(storyCanvasEl, current, elapsed, 'full');
      }
    }
    
    if (storyCcCanvasEl && hasStacked) {
      drawCCVisualizer(storyCcCanvasEl, current, elapsed, false);
    }
    
    if (sidebarCcCanvasEl) {
      sidebarCcCanvasEl.style.display = hasSidebar ? 'block' : 'none';
      if (hasSidebar) {
        drawCCVisualizer(sidebarCcCanvasEl, current, elapsed, false);
      }
    }
    
    var hasReadout = (visualizerLayout !== 'disabled');
    var readoutsPanel = storyEl.querySelector('.midg__vis-readouts');
    var slidersPanel = storyEl.querySelector('.midg__vis-sliders');
    if (readoutsPanel) readoutsPanel.style.display = hasReadout ? 'flex' : 'none';
    if (slidersPanel) slidersPanel.style.display = hasReadout ? 'flex' : 'none';
    
    if (hasReadout) {
      var vals = getGlobalCCValuesAt(current, elapsed);
      var elCc1 = storyEl.querySelector('.midg__cc1-val');
      var elCc11 = storyEl.querySelector('.midg__cc11-val');
      var elCc21 = storyEl.querySelector('.midg__cc21-val');
      if (elCc1) elCc1.textContent = Math.round(vals.melody * 127);
      if (elCc11) elCc11.textContent = Math.round(vals.harmony * 127);
      if (elCc21) elCc21.textContent = Math.round(vals.bass * 127);
    }
  }

  function drawScrollingOn(cv, card, elapsedSec, isStory) {
    var notes = card._notes;
    if (!cv || !notes || !notes.length) { return; }
    
    resizeCanvasToDisplaySize(cv);
    
    var g = cv.getContext('2d'), w = cv.width, h = cv.height;
    var minPitch = card._minPitch, maxPitch = card._maxPitch;
    var span = Math.max(1, maxPitch - minPitch);
    g.clearRect(0, 0, w, h);
    var padTop = h * 0.08, rollH = h * 0.84;
    var barH = Math.max(2, h * 0.035);
    var pps = w / SCROLL_WINDOW_SEC;
    var playheadX = w * PLAYHEAD_FRAC;
    var activeMidi = [];
    var chordMidi = [];
    var effectiveFormat = isStory ? currentFormat : 'classic';
    var theme = THEMES[currentThemeName];


    if (isStory && storyEl) {
      var elMs = storyEl.querySelector('.midg__pos-ms');
      var elBar = storyEl.querySelector('.midg__pos-bar');
      if (elMs || elBar) {
        var msVal = Math.floor(elapsedSec * 1000);
        var bpmVal = card._bpm || 120;
        var totalBeats = elapsedSec * (bpmVal / 60);
        var barVal = Math.floor(totalBeats / 4) + 1;
        var beatVal = (totalBeats % 4) + 1;
        
        if (elMs) {
          elMs.textContent = msVal + ' ms';
        }
        if (elBar) {
          elBar.textContent = 'Bar ' + barVal + ' : Beat ' + beatVal.toFixed(2);
        }
      }
    }

    // A flat key is spelled with flats, anything else with sharps.
    var inkUseFlats = false;
    if (card && card._name) {
      try { inkUseFlats = /^[A-G]b/i.test(parseCardName(card._name).key || ''); } catch (e) { inkUseFlats = false; }
    }
    var lineSpacing = Math.max(8, h * 0.012);
    // Ink staves sit lower than centre so the chord mark can occupy the middle
    // of the frame without landing on the notes or the staff lines.
    var yMiddleC = (effectiveFormat === 'ink') ? h * 0.66 : h * 0.55;
    var semitoneSpacing = (4 * lineSpacing) / 13;

    var trebleLines = [64, 67, 71, 74, 77];
    var bassLines = [43, 47, 50, 53, 57];
    var allStaveLines = trebleLines.concat(bassLines);

    var braceTop = yMiddleC - (77 - 60) * semitoneSpacing;
    var braceBottom = yMiddleC - (43 - 60) * semitoneSpacing;

    function getStaveWave(dx, yBase) {
      var paperX = dx + elapsedSec * pps;
      var distFromPlayhead = Math.abs(dx - playheadX);
      var warpFactor = 1.0 + Math.min(3.0, distFromPlayhead / 200);
      var slowWiggle = Math.sin(paperX * 0.002 + yBase * 0.005) * 14.0 * warpFactor;
      var midWiggle = Math.sin(paperX * 0.008 - yBase * 0.003) * 3.5 * warpFactor;
      var tremor = Math.sin(paperX * 0.04 + yBase * 0.01) * 0.8;
      return slowWiggle + midWiggle + tremor;
    }

    if (effectiveFormat === 'ink' && !card._smudges) {
      var paperSmudges = [];
      var seedSmudges = (card._name || 'midi').length + 842;
      var rngSmudge = function () {
        seedSmudges = (seedSmudges * 9301 + 49297) % 233280;
        return seedSmudges / 233280;
      };
      var totalDur = card._dur || 10;
      var numSmudges = Math.ceil(totalDur * 2.8) + 8;
      for (var s = 0; s < numSmudges; s++) {
        var yVal = rngSmudge() * h;
        var timeVal = rngSmudge() * totalDur;
        var sizeVal = 40 + rngSmudge() * 100;
        var typeRoll = rngSmudge();
        var type = 'speck';
        var opacity = 0.01 + rngSmudge() * 0.02;
        var angle = rngSmudge() * Math.PI * 2;
        if (typeRoll < 0.25) {
          type = 'coffee';
          sizeVal = 80 + rngSmudge() * 120;
          opacity = 0.008 + rngSmudge() * 0.012;
        } else if (typeRoll < 0.45) {
          type = 'water-ring';
          sizeVal = 50 + rngSmudge() * 60;
          opacity = 0.008 + rngSmudge() * 0.012;
        } else if (typeRoll < 0.70) {
          type = 'crease';
          sizeVal = 60 + rngSmudge() * 120;
          opacity = 0.012 + rngSmudge() * 0.018;
        } else {
          type = 'speck';
          sizeVal = 2 + rngSmudge() * 5;
          opacity = 0.02 + rngSmudge() * 0.04;
        }
        paperSmudges.push({
          time: timeVal,
          y: yVal,
          size: sizeVal,
          type: type,
          opacity: opacity,
          angle: angle
        });
      }
      card._smudges = paperSmudges;
    }

    if (effectiveFormat === 'ink' && card._smudges) {
      card._smudges.forEach(function (smudge) {
        var sx = playheadX + (smudge.time - elapsedSec) * pps;
        var sy = smudge.y;
        if (sx + smudge.size < 0 || sx - smudge.size > w) { return; }
        if (smudge.type === 'coffee') {
          var grad = g.createRadialGradient(sx, sy, 0, sx, sy, smudge.size);
          grad.addColorStop(0, 'rgba(120, 80, 40, ' + smudge.opacity + ')');
          grad.addColorStop(0.7, 'rgba(120, 80, 40, ' + (smudge.opacity * 0.3) + ')');
          grad.addColorStop(1, 'rgba(120, 80, 40, 0)');
          g.fillStyle = grad;
          g.beginPath();
          var numBPoints = 12;
          for (var p = 0; p <= numBPoints; p++) {
            var angle = (p / numBPoints) * Math.PI * 2;
            var seedB = Math.sin(smudge.time * 12.4 + smudge.y * 98.3 + angle * 7.4) * 832.1;
            var radB = smudge.size * (0.8 + Math.sin(seedB) * 0.2);
            var bx = sx + Math.cos(angle) * radB;
            var by = sy + Math.sin(angle) * radB;
            if (p === 0) g.moveTo(bx, by);
            else g.lineTo(bx, by);
          }
          g.fill();
        } else if (smudge.type === 'water-ring') {
          g.strokeStyle = 'rgba(120, 80, 40, ' + smudge.opacity + ')';
          g.lineWidth = 1.2;
          g.beginPath();
          var numBPoints2 = 24;
          for (var p2 = 0; p2 <= numBPoints2; p2++) {
            var angle2 = (p2 / numBPoints2) * Math.PI * 2;
            var seedB2 = Math.sin(smudge.time * 42.1 + smudge.y * 13.9 + angle2 * 11.2) * 543.2;
            var radB2 = smudge.size * (0.95 + Math.sin(seedB2) * 0.05);
            var bx2 = sx + Math.cos(angle2) * radB2;
            var by2 = sy + Math.sin(angle2) * radB2;
            if (p2 === 0) g.moveTo(bx2, by2);
            else g.lineTo(bx2, by2);
          }
          g.stroke();
        } else if (smudge.type === 'crease') {
          g.strokeStyle = 'rgba(120, 80, 40, ' + smudge.opacity + ')';
          g.lineWidth = 0.8;
          g.beginPath();
          var startX = sx - smudge.size * Math.cos(smudge.angle);
          var startY = sy - smudge.size * Math.sin(smudge.angle);
          var endX = sx + smudge.size * Math.cos(smudge.angle);
          var endY = sy + smudge.size * Math.sin(smudge.angle);
          g.moveTo(startX, startY);
          var ctrlX = sx + Math.sin(smudge.time * 10) * 8;
          var ctrlY = sy + Math.cos(smudge.y * 10) * 8;
          g.quadraticCurveTo(ctrlX, ctrlY, endX, endY);
          g.stroke();
        } else {
          g.fillStyle = 'rgba(40, 30, 20, ' + smudge.opacity + ')';
          g.beginPath();
          var numBPoints3 = 6;
          for (var p3 = 0; p3 <= numBPoints3; p3++) {
            var angle3 = (p3 / numBPoints3) * Math.PI * 2;
            var seedB3 = Math.sin(smudge.time * 92.4 + smudge.y * 3.1 + angle3 * 5.4) * 123.4;
            var radB3 = smudge.size * (0.7 + Math.sin(seedB3) * 0.3);
            var bx3 = sx + Math.cos(angle3) * radB3;
            var by3 = sy + Math.sin(angle3) * radB3;
            if (p3 === 0) g.moveTo(bx3, by3);
            else g.lineTo(bx3, by3);
          }
          g.fill();
        }
      });
    }

    if (effectiveFormat === 'ink' && showStaveLines) {
      g.strokeStyle = 'rgba(26,26,26,0.22)';
      g.lineWidth = 1.8;
      allStaveLines.forEach(function (pitchLine) {
        var yBase = yMiddleC - (pitchLine - 60) * semitoneSpacing;
        g.beginPath();
        for (var dx = 0; dx <= w; dx += 15) {
          var waveY = getStaveWave(dx, yBase);
          var ly = yBase + waveY;
          if (dx === 0) g.moveTo(dx, ly);
          else g.lineTo(dx, ly);
        }
        g.stroke();
      });

      var braceX = Math.max(20, w * 0.035);
      
      // The brace sampled the stave wave at exactly its own x, so it moved in
      // lockstep with the staff lines and read as welded to them, pinned to the
      // exact top and bottom of the stave. Damp that wave and add a slow drift
      // of its own so it floats around the staff instead of tracking it, and
      // let it overhang top and bottom the way a drawn brace does. Same idea as
      // the end repeat barline, which ignores the wave entirely and reads as
      // sitting above the paper.
      var braceOver = lineSpacing * 0.85;
      var braceDrift = function (yBase) {
        var t = performance.now() * 0.00035;
        return getStaveWave(braceX, yBase) * 0.42
             + Math.sin(t + yBase * 0.004) * 4.5
             + Math.cos(t * 0.7 + yBase * 0.002) * 2.2;
      };
      var braceSway = Math.sin(performance.now() * 0.0004) * 2.0;

      var btW = braceTop - braceOver + braceDrift(braceTop);
      var bt20W = braceTop + 20 - braceOver * 0.5 + braceDrift(braceTop + 20);
      var bb20W = braceBottom - 20 + braceOver * 0.5 + braceDrift(braceBottom - 20);
      var bbW = braceBottom + braceOver + braceDrift(braceBottom);
      var btStartW = braceTop - braceOver + braceDrift(braceTop);
      var bbEndW = braceBottom + braceOver + braceDrift(braceBottom);

      g.strokeStyle = 'rgba(26,26,26,0.35)';
      g.lineWidth = 2.5;
      g.beginPath();
      g.moveTo(braceX + 15 + braceSway, btStartW);
      g.quadraticCurveTo(braceX + braceSway, btW, braceX + braceSway, bt20W);
      g.lineTo(braceX + braceSway, bb20W);
      g.quadraticCurveTo(braceX + braceSway, bbW, braceX + 15 + braceSway, bbEndW);
      g.stroke();

      // Draw Treble and Bass Clef symbols in Inkscape mode!
      g.fillStyle = 'rgba(26, 26, 26, 0.7)';
      g.font = '36px serif';
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      
      var yTrebleClef = yMiddleC - (71 - 60) * semitoneSpacing + getStaveWave(braceX + 25, yMiddleC - (71 - 60) * semitoneSpacing);
      g.fillText('\uD834\uDD1E', braceX + 25, yTrebleClef); // Treble Clef 𝄞
      
      var yBassClef = yMiddleC - (47 - 60) * semitoneSpacing + getStaveWave(braceX + 25, yMiddleC - (47 - 60) * semitoneSpacing);
      g.fillText('\uD834\uDD22', braceX + 25, yBassClef); // Bass Clef 𝄢



      var bpm = card._bpm || 120;
      var barDur = 240 / bpm;
      var startSec = elapsedSec - playheadX / pps;
      var endSec = elapsedSec + (w - playheadX) / pps;
      var startBar = Math.floor(startSec / barDur);
      var endBar = Math.ceil(endSec / barDur);
      g.strokeStyle = 'rgba(26,26,26,0.15)';
      g.lineWidth = 1.5;
      for (var b = startBar; b <= endBar; b++) {
        var t_bar = b * barDur;
        var bx4 = playheadX + (t_bar - elapsedSec) * pps;
        g.beginPath();
        var isFirstY = true;
        for (var currY = braceTop; currY <= braceBottom; currY += 15) {
          var wobbleX = Math.sin(currY * 0.015 + (t_bar * 100)) * 1.5;
          if (isFirstY) {
            g.moveTo(bx4 + wobbleX, currY);
            isFirstY = false;
          } else {
            g.lineTo(bx4 + wobbleX, currY);
          }
        }
        g.stroke();
      }

      var xEnd = playheadX + (card._dur - elapsedSec) * pps;
      if (xEnd >= 0 && xEnd <= w) {
        g.strokeStyle = 'rgba(26,26,26,0.35)';
        g.lineWidth = 1.5;
        g.beginPath();
        g.moveTo(xEnd - 6, braceTop);
        g.lineTo(xEnd - 6, braceBottom);
        g.stroke();

        g.lineWidth = 5.0;
        g.beginPath();
        g.moveTo(xEnd - 2, braceTop);
        g.lineTo(xEnd - 2, braceBottom);
        g.stroke();

        g.fillStyle = 'rgba(26,26,26,0.7)';
        var dotRadius = Math.max(2, lineSpacing * 0.12);
        var yTrebleDot1 = yMiddleC - (69 - 60) * semitoneSpacing;
        var yTrebleDot2 = yMiddleC - (72 - 60) * semitoneSpacing;
        var yBassDot1 = yMiddleC - (48 - 60) * semitoneSpacing;
        var yBassDot2 = yMiddleC - (51 - 60) * semitoneSpacing;

        [yTrebleDot1, yTrebleDot2, yBassDot1, yBassDot2].forEach(function (dy) {
          g.beginPath();
          g.arc(xEnd - 14, dy, dotRadius, 0, Math.PI * 2);
          g.fill();
        });
      }

      // Faint paper ruling. This is the else branch of the Ink stave block, so
      // switching staves off used to fall through to here and draw five ruled
      // lines instead of nothing. Gate it on the same toggle.
    } else if (showStaveLines && (currentThemeName === 'sketchbook' || currentThemeName === 'watercolor')) {
      g.strokeStyle = currentThemeName === 'sketchbook' ? 'rgba(26,26,26,0.06)' : 'rgba(0,0,0,0.05)';
      g.lineWidth = 1;
      for (var j = 0; j < 5; j++) {
        var yLine = padTop + (j / 4) * rollH;
        g.beginPath();
        g.moveTo(0, yLine);
        var midX = w / 2;
        var midY = yLine + Math.sin(yLine * 0.05) * 1.5;
        g.quadraticCurveTo(midX, midY, w, yLine);
        g.stroke();
      }
    }

    // Draw global continuous DAW "Flight Path" overlay (overlapping CC1/CC11 curves for all tracks) - rendered behind notes!
    if (isStory && showFlightPath && card._notes && card._notes.length > 0) {
      var scrollStart = elapsedSec - (playheadX / pps);
      var scrollEnd = scrollStart + (w / pps);
      
      var theme = THEMES[currentThemeName];
      var visibleNotes = [];
      card._notes.forEach(function (n) {
        if (!isTrackEnabled(card, n.trackIdx)) { return; }
        var relTimeVal = n.role === 'BASS' ? 0.8 : (n.role === 'MELODY' ? 0.6 : 0.7);
        var noteEnd = n.time + n.duration + relTimeVal;
        if (n.time <= scrollEnd && noteEnd >= scrollStart) {
          visibleNotes.push(n);
        }
      });

      var cardBpm = card ? card._bpm : 120;

      visibleNotes.forEach(function (n) {
        var points = [];
        var stepSec = 0.015; // 15ms step resolution
        var relTimeVal = n.role === 'BASS' ? 0.8 : (n.role === 'MELODY' ? 0.6 : 0.7);
        
        var noteColorHex = '#b79bff';
        if (theme && theme.lit === 'multi') {
          var palette = currentThemeName === 'soul' ? ['#d89a27', '#4a7b82', '#b93b26', '#e0d8c3'] : ['#2b5c8f', '#a9332a', '#caa230', '#34383a'];
          noteColorHex = palette[n.midi % palette.length];
        } else {
          noteColorHex = (theme && theme.active) || '#b79bff';
        }
        
        var strokeCol = hexToRgba(noteColorHex, 0.45);
        var fillCol = hexToRgba(noteColorHex, 0.03);
        if (effectiveFormat === 'ink') {
          strokeCol = 'rgba(26, 26, 26, 0.2)';
          fillCol = 'rgba(26, 26, 26, 0.01)';
        }
        
        var gridStartT = Math.floor(scrollStart / stepSec) * stepSec;
        var gridEndT = scrollEnd + stepSec;
        for (var t = gridStartT; t <= gridEndT; t += stepSec) {
          var cx = playheadX + (t - elapsedSec) * pps;
          // Height is scaled by Note-Bound Intensity, shading is not. Intensity
          // clips val at 1.0, so at a high setting the whole curve saturated and
          // every stroke went solid. Keep the raw envelope for ink: a point at
          // zero draws as nothing, a point at full draws solid, at any intensity.
          var rawEnv = 0;
          var val = 0;
          if (t >= n.time && t <= n.time + n.duration + relTimeVal) {
            var env = getProceduralNoteEnvelope(n, (t - n.time) * complexityRate, cardBpm);
            // A pencil stroke starts and ends light regardless of how hard the
            // middle is pressed. Taper the ink at both ends of the note so a line
            // that opens at full value still fades in, and fades out as it lifts.
            var noteLen = n.duration + relTimeVal;
            var relT = t - n.time;
            var edgeLen = Math.min(noteLen * 0.3, 0.16);
            var fIn = edgeLen > 0 ? Math.min(1, relT / edgeLen) : 1;
            var fOut = edgeLen > 0 ? Math.min(1, (noteLen - relT) / edgeLen) : 1;
            var edge = Math.max(0, Math.min(fIn, fOut));
            edge = edge * edge * (3 - 2 * edge); // smoothstep, so the fade is not linear
            rawEnv = env * edge;
            val = Math.min(1.0, env * intensityBias);
          }
          var cy = padTop + rollH - (val * rollH);
          points.push({ x: cx, y: cy, v: val, a: rawEnv });
        }
        
        if (points.length >= 2) {
          // No baseline fill and no flat run along the bottom. Segments at rest
          // are simply not drawn, so the curve floats instead of sitting in a
          // lane, and alpha tracks the value so quiet reads faint and loud reads
          // solid: the dynamic is visible as well as audible.
          var FLOOR = 0.07; // below this, draw nothing at all
          g.lineWidth = 1.8;
          g.lineCap = 'round';
          g.lineJoin = 'round';
          for (var i = 1; i < points.length; i++) {
            var vAvg = (points[i - 1].v + points[i].v) / 2;
            // Ink from the raw envelope, so intensity moves the curve without
            // repainting it. Width tracks it too, the way pencil pressure does.
            var aAvg = (points[i - 1].a + points[i].a) / 2;
            if (aAvg <= FLOOR) { continue; }
            // Linear ink kept the 0-30% range clearly visible. A gamma curve pushes
            // the bottom of the range down hard, so quiet passages read as almost
            // blank paper and only the louder material carries a solid line.
            var alpha = Math.pow(Math.min(1, aAvg), 2.6);
            g.lineWidth = 2.2 * (0.25 + Math.pow(Math.min(1, aAvg), 1.7) * 0.75);
            g.strokeStyle = (effectiveFormat === 'ink')
              ? 'rgba(26, 26, 26, ' + (alpha * 0.5).toFixed(3) + ')'
              : hexToRgba(noteColorHex, alpha * 0.75);
            g.beginPath();
            g.moveTo(points[i - 1].x, points[i - 1].y);
            g.lineTo(points[i].x, points[i].y);
            g.stroke();
          }
        }
      });
    }

    // With the loop on, the piece is also drawn one length earlier and one
    // length later. Only the copies that actually fall in the visible window are
    // built, so this is a handful of objects per frame rather than three times
    // the score. Copies are marked so they stay out of the chord read-out and
    // the active-note highlight, which follow the audio, not the picture.
    var drawNotes = notes;
    if (seamlessLoop && card._dur > 0) {
      var loopLen = card._dur;
      var winStart = scrollStart - 1;
      var winEnd = scrollStart + (w / pps) + 1;
      drawNotes = notes.slice();
      for (var wi = 0; wi < notes.length; wi++) {
        var wn = notes[wi];
        for (var wd = -1; wd <= 1; wd += 2) {
          var wt = wn.time + wd * loopLen;
          if (wt + wn.duration < winStart || wt > winEnd) { continue; }
          drawNotes.push({
            time: wt, duration: wn.duration, midi: wn.midi, velocity: wn.velocity,
            trackIdx: wn.trackIdx, role: wn.role, isHighest: wn.isHighest, _wrapped: true
          });
        }
      }
    }
    for (var i = 0; i < drawNotes.length; i++) {
      var n = drawNotes[i];
      var x = playheadX + (n.time - elapsedSec) * pps;
      var nw = Math.max(2, n.duration * pps - 1);
      var end = n.time + n.duration;
      var isActive = (n.time <= elapsedSec && end >= elapsedSec);
      if (isActive && !n._wrapped) { activeMidi.push(n.midi); }
      if (n._wrapped) { /* picture only, never the chord read-out */ }
      else if (chordMode === 'settled') {
        var lookT = elapsedSec + CHORD_LEAD_SEC;
        if (n.time <= lookT + CHORD_SETTLE_SEC && end >= lookT - CHORD_SETTLE_SEC) {
          chordMidi.push(n.midi);
        }
      } else if (isActive) {
        chordMidi.push(n.midi);
      }
      if (x + nw < 0 || x > w) { continue; }

      var yBaseNote, y;
      if (effectiveFormat === 'ink') {
        yBaseNote = yMiddleC - (n.midi - 60) * semitoneSpacing;
      } else {
        var yFrac = 1 - ((n.midi - minPitch) / span);
        yBaseNote = padTop + yFrac * rollH;
      }
      y = yBaseNote - barH / 2;

      // Draw dynamic bowing expression automation curve behind/underneath the strings note
      if (isStory && currentVoices && currentVoices.indexOf('strings') !== -1 && showLaneCurves) {
        var relTimeVal = n.isHighest ? 0.6 : 0.8;
        var laneH = barH * 2.3;
        var stepPx = 6;
        var totalWidth = (n.duration + relTimeVal) * pps;
        
        // Calculate stave wiggle offset for ink format stability
        var yBaseLine = yBaseNote;
        if (effectiveFormat === 'ink') {
          var closestStaveLine = allStaveLines[0];
          var minDiff = 9999;
          allStaveLines.forEach(function (pitchLine) {
            var diff = Math.abs(pitchLine - n.midi);
            if (diff < minDiff) {
              minDiff = diff;
              closestStaveLine = pitchLine;
            }
          });
          yBaseLine = yMiddleC - (closestStaveLine - 60) * semitoneSpacing;
        }

        // Get wiggled baseline function
        function getWiggledY(cx) {
          if (effectiveFormat === 'ink') {
            return yBaseNote + getStaveWave(cx, yBaseLine);
          }
          return yBaseNote;
        }

        // Bottom of the note's lane (0% volume baseline)
        var y_bottom_offset = barH * 0.8;
        
        g.beginPath();
        g.moveTo(x, getWiggledY(x) + y_bottom_offset);
        
        // Trace top boundary curve from left to right
        var cardBpm = card ? card._bpm : 120;
        for (var px = 0; px <= totalWidth; px += stepPx) {
          var t = px / pps;
          var midiTime = n.time + t;
          var envVal = getCCAutomationValue(card, n.trackIdx, midiTime);
          if (envVal === null) {
            envVal = getProceduralNoteEnvelope(n, t, cardBpm);
          }
          var cx = x + px;
          g.lineTo(cx, getWiggledY(cx) + y_bottom_offset - (envVal * laneH));
        }
        
        // Trace bottom boundary from right to left
        for (var px = Math.floor(totalWidth); px >= 0; px -= stepPx) {
          var cx = x + px;
          g.lineTo(cx, getWiggledY(cx) + y_bottom_offset);
        }
        
        g.closePath();
        
        // Determine note color dynamically based on the theme palette (multi-colored, lit, etc.)
        var noteColorHex = '#b79bff';
        if (theme.lit === 'multi') {
          var palette = currentThemeName === 'soul' ? ['#d89a27', '#4a7b82', '#b93b26', '#e0d8c3'] : ['#2b5c8f', '#a9332a', '#caa230', '#34383a'];
          noteColorHex = palette[n.midi % palette.length];
        } else {
          noteColorHex = theme.active || '#b79bff';
        }

        var strokeCol = hexToRgba(noteColorHex, 0.45);
        var fillCol = hexToRgba(noteColorHex, 0.12);
        
        if (effectiveFormat === 'ink') {
          strokeCol = 'rgba(26, 26, 26, 0.28)';
          fillCol = 'rgba(26, 26, 26, 0.06)';
        } else if (currentThemeName === 'sketchbook' || currentThemeName === 'watercolor') {
          strokeCol = hexToRgba(noteColorHex, 0.35);
          fillCol = hexToRgba(noteColorHex, 0.08);
        }

        g.fillStyle = fillCol;
        g.fill();
        
        // Draw the dynamic envelope line curve
        // Same ink-follows-value treatment as the other two curve renderers.
        g.lineCap = 'round';
        var lanePrevX = null, lanePrevY = 0, lanePrevV = 0;
        for (var px = 0; px <= totalWidth; px += stepPx) {
          var t = px / pps;
          var midiTime = n.time + t;
          var envVal = getCCAutomationValue(card, n.trackIdx, midiTime);
          if (envVal === null) {
            envVal = getProceduralNoteEnvelope(n, t, cardBpm);
          }
          var cx = x + px;
          var cy = getWiggledY(cx) + y_bottom_offset - (envVal * laneH);
          if (lanePrevX !== null) {
            var laneAvg = (lanePrevV + envVal) / 2;
            // Same taper: light at the start of the stroke, light as it lifts.
            var laneEdge = Math.min(1, Math.min(px, totalWidth - px) / Math.max(1, totalWidth * 0.28));
            laneEdge = laneEdge * laneEdge * (3 - 2 * laneEdge);
            laneAvg = laneAvg * laneEdge;
            if (laneAvg > 0.07) {
              g.strokeStyle = hexToRgba(noteColorHex, Math.pow(Math.min(1, laneAvg), 2.6));
              g.lineWidth = 1.5 * (0.25 + Math.pow(Math.min(1, laneAvg), 1.7) * 0.75);
              g.beginPath();
              g.moveTo(lanePrevX, lanePrevY);
              g.lineTo(cx, cy);
              g.stroke();
            }
          }
          lanePrevX = cx; lanePrevY = cy; lanePrevV = envVal;
        }
      }

      var color;
      if (theme.lit === 'multi') {
        var palette = currentThemeName === 'soul' ? ['#d89a27', '#4a7b82', '#b93b26', '#e0d8c3'] : ['#2b5c8f', '#a9332a', '#caa230', '#34383a'];
        var baseHex = palette[n.midi % palette.length];
        if (isActive) {
          color = theme.active;
        } else if (end < elapsedSec) {
          color = hexToRgba(baseHex, 0.16);
        } else {
          color = hexToRgba(baseHex, 0.55);
        }
      } else {
        if (isActive) { color = theme.active; }
        else if (end < elapsedSec) { color = theme.dim; }
        else { color = theme.upcoming; }
      }

      if (showMidiNotes) {
        if (effectiveFormat === 'ink') {
          var closestStaveLine = allStaveLines[0];
          var minDiff = 9999;
          allStaveLines.forEach(function (pitchLine) {
            var diff = Math.abs(pitchLine - n.midi);
            if (diff < minDiff) {
              minDiff = diff;
              closestStaveLine = pitchLine;
            }
          });

          var yBaseLine = yMiddleC - (closestStaveLine - 60) * semitoneSpacing;
          var waveY = getStaveWave(x, yBaseLine);
          y = yBaseNote + waveY;
          var yStave = yBaseLine + waveY;

          var r = Math.max(1.5, barH * 0.15);
          if (Math.round(n.midi) === 60) {
            g.strokeStyle = 'rgba(26,26,26,0.3)';
            g.lineWidth = 1.8;
            g.beginPath();
            g.moveTo(x - r * 1.7, yStave);
            g.lineTo(x + r * 1.7, yStave);
            g.stroke();
          } else if (n.midi >= 81) {
            for (var lp = 81; lp <= n.midi; lp += 3) {
              var ly = yMiddleC - (lp - 60) * semitoneSpacing + waveY;
              g.strokeStyle = 'rgba(26,26,26,0.3)';
              g.lineWidth = 1.8;
              g.beginPath();
              g.moveTo(x - r * 1.7, ly);
              g.lineTo(x + r * 1.7, ly);
              g.stroke();
            }
          } else if (n.midi <= 39) {
            for (var lp2 = 39; lp2 >= n.midi; lp2 -= 3) {
              var ly2 = yMiddleC - (lp2 - 60) * semitoneSpacing + waveY;
              g.strokeStyle = 'rgba(26,26,26,0.3)';
              g.lineWidth = 1.8;
              g.beginPath();
              g.moveTo(x - r * 1.7, ly2);
              g.lineTo(x + r * 1.7, ly2);
              g.stroke();
            }
          }

          g.beginPath();
          g.moveTo(x, y);
          var seedStem = Math.sin(x * 15.1 + y * 93.7) * 2384.5;
          var stemWobble = Math.sin(seedStem) * 1.5;
          g.quadraticCurveTo(x + stemWobble, (y + yStave) / 2, x, yStave);
          g.strokeStyle = color;
          g.lineWidth = 1.0;
          g.stroke();

          // Accidental beside the head, so the pitch is readable off the page.
          // Spelling follows the file's key: a flat key gets flats, everything
          // else gets sharps, which is how it would be engraved.
          var pcInk = ((n.midi % 12) + 12) % 12;
          if (pcInk === 1 || pcInk === 3 || pcInk === 6 || pcInk === 8 || pcInk === 10) {
            g.save();
            g.fillStyle = color;
            g.font = Math.max(10, Math.round(lineSpacing * 1.33)) + 'px "Garamond", "Baskerville", Georgia, serif';
            g.textAlign = 'center';
            g.textBaseline = 'middle';
            g.fillText(inkUseFlats ? '\u266D' : '\u266F', x - r * 2.4, y);
            g.restore();
          }

          g.beginPath();
          var numPoints = 16;
          for (var p4 = 0; p4 <= numPoints; p4++) {
            var angle4 = (p4 / numPoints) * Math.PI * 2;
            var seedCircle = Math.sin(x * 7.4 + y * 42.1 + angle4 * 19.3) * 832.1;
            var radWobble = Math.sin(seedCircle) * 0.4;
            var currR = r + radWobble;
            var cx = x + Math.cos(angle4) * currR;
            var cy = y + Math.sin(angle4) * currR;
            if (p4 === 0) g.moveTo(cx, cy);
            else g.lineTo(cx, cy);
          }

          var noteSeed = Math.sin(n.midi * 12.9898 + n.time * 78.23) * 43758.5453;
          var isFilled = (noteSeed - Math.floor(noteSeed)) < 0.65;

          if (isFilled) {
            g.fillStyle = '#faf7f2'; // Opaque backdrop mask
            g.fill();
            g.fillStyle = isActive ? '#000000' : color;
            g.fill();
            g.strokeStyle = isActive ? '#000000' : color;
            g.lineWidth = isActive ? 2.0 : 1.0;
            g.stroke();
          } else {
            g.fillStyle = '#faf7f2';
            g.fill();
            g.strokeStyle = isActive ? '#000000' : color;
            g.lineWidth = isActive ? 2.5 : 1.2;
            g.stroke();
          }
        } else {
          var solidBg = (effectiveFormat === 'ink') ? '#faf7f2' : (theme.backdrop || '#07090a');
          drawOrganicNote(g, x, y, nw, barH, color, solidBg, n.time * 7.7 + n.midi * 3.1);
        }

        // Draw a thin, clean black/dark line directly inside the note block boundary representing the volume relationship
        if (showNoteCurves) {
          // Alpha follows the envelope value, so the line is solid where the note
          // is loud and fades where it is quiet - same treatment as the flight
          // path and the visualiser, so all three read the dynamic the same way.
          // Envelope alone is near full for most of a note's length, so the line
          // still read as solid. Weight it by the note's velocity as well, so a
          // quiet note draws faint and a loud one draws solid.
          var baseAlpha = 0.85;
          var noteVel = (n.velocity == null) ? 0.7 : n.velocity;
          g.lineWidth = 1.2;
          g.lineCap = 'round';
          var cardBpm = card ? card._bpm : 120;
          var prevX = null, prevY = 0, prevV = 0;
          for (var px = 0; px <= nw; px += 2) {
            var t = px / pps;
            var envVal = getProceduralNoteEnvelope(n, t, cardBpm);
            var cy = y + barH - 1.5 - (envVal * (barH - 3));
            if (prevX !== null) {
              var vAvg = (prevV + envVal) / 2;
              var aa = baseAlpha * Math.min(1, 0.08 + noteVel * vAvg * 0.92);
              g.strokeStyle = 'rgba(0, 0, 0, ' + aa.toFixed(3) + ')';
              g.beginPath();
              g.moveTo(prevX, prevY);
              g.lineTo(x + px, cy);
              g.stroke();
            }
            prevX = x + px; prevY = cy; prevV = envVal;
          }
        }
      }
    }



    if (showPlayhead) {
      if (effectiveFormat === 'ink') {
        g.beginPath();
        var timeMs = performance.now();
        for (var currY2 = 0; currY2 <= h; currY2 += 10) {
          // Half the previous amplitude: the line still breathes, without snaking.
          var wormX = Math.sin(currY2 * 0.008 + timeMs * 0.006) * 1.75
                    + Math.sin(currY2 * 0.025 - timeMs * 0.012) * 0.75;
          var px = playheadX + wormX;
          if (currY2 === 0) g.moveTo(px, currY2);
          else g.lineTo(px, currY2);
        }
        var grad2 = g.createLinearGradient(playheadX, 0, playheadX, h);
        grad2.addColorStop(0, 'rgba(229,57,53,0)');
        grad2.addColorStop(0.12, 'rgba(229,57,53,1)');
        grad2.addColorStop(0.88, 'rgba(229,57,53,1)');
        grad2.addColorStop(1, 'rgba(229,57,53,0)');
        g.strokeStyle = grad2;
        g.lineWidth = 1.5;
        g.stroke();
      } else if (currentThemeName === 'sketchbook') {
        g.beginPath();
        g.moveTo(playheadX, 0);
        var midY2 = h / 2;
        var playheadWobble = 1.2;
        g.quadraticCurveTo(playheadX + playheadWobble, midY2, playheadX, h);
        g.strokeStyle = '#e53935';
        g.lineWidth = 1.5;
        g.stroke();
      } else {
        g.fillStyle = theme.lit;
        g.fillRect(playheadX, 0, Math.max(1.5, w / 260), h);
      }
    }

    var chordText = detectChord(chordMidi.map(function(m) { return m + pitchOffset; }));
    if (!chordText) { return; }
    g.textBaseline = 'middle';
    var isLight = (currentThemeName === 'sketchbook' || currentThemeName === 'watercolor');
    var displayChord = (currentChordStyle === 'realbook') ? formatChordRealBook(chordText) : chordText;
    if (isStory) {
      if (!showChordTranscriber) { return; }
      if (currentChordStyle === 'realbook') {
        var chordY = h * 0.46; // middle of the frame, above the lowered stave
        g.font = 'italic bold 54px "Garamond", "Baskerville", "Georgia", serif';
        g.fillStyle = (isLight || effectiveFormat === 'ink') ? '#000000' : '#ffffff'; // Force black text for Ink Sketch
        g.textAlign = 'center';
        g.fillText(displayChord, w / 2, chordY);
      } else {
        var fontPxS = Math.round(h * 0.045);
        g.font = '700 ' + fontPxS + 'px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
        var textWS = g.measureText(displayChord).width;
        var padXS = fontPxS * 0.6, padYS = fontPxS * 0.4;
        var chipWS = textWS + padXS * 2, chipHS = fontPxS + padYS * 2;
        var chipXS = (w - chipWS) / 2, chipYS = (h - chipHS) / 2;
        // 'classicplain' draws the same layout with no chip behind it, so only
        // the chord text reads on screen.
        if (currentChordStyle !== 'classicplain') {
          g.fillStyle = (isLight || effectiveFormat === 'ink') ? 'rgba(255,255,255,0.92)' : 'rgba(7,9,10,0.78)';
          g.fillRect(chipXS, chipYS, chipWS, chipHS);
        }
        g.fillStyle = (isLight || effectiveFormat === 'ink') ? '#07090a' : '#f2eeff';
        g.textAlign = 'center';
        g.fillText(displayChord, w / 2, chipYS + chipHS / 2 + fontPxS * 0.03);
      }
    } else {
      var fontPx = Math.max(9, Math.round(h * 0.06));
      g.font = '700 ' + fontPx + 'px -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
      var textW = g.measureText(displayChord).width;
      var padX = fontPx * 0.45, padY = fontPx * 0.28;
      var chipH = fontPx + padY * 2;
      var chipX = h * 0.04;
      var chipY = h - chipH - h * 0.03;
      if (currentChordStyle !== 'classicplain') {
        g.fillStyle = (isLight || effectiveFormat === 'ink') ? 'rgba(255,255,255,0.92)' : 'rgba(7,9,10,0.8)';
        g.fillRect(chipX, chipY, textW + padX * 2, chipH);
      }
      g.fillStyle = (isLight || effectiveFormat === 'ink') ? '#07090a' : '#f2eeff';
      g.textAlign = 'left';
      g.fillText(displayChord, chipX + padX, chipY + chipH / 2 + fontPx * 0.03);
    }
  }

  function draw(card, elapsedSec) {
    var inlineCv = card.querySelector('.mid-card__canvas');
    if (current === card && isPlaying) {
      drawScrollingOn(inlineCv, card, elapsedSec, false);
      if (storyOpen) { renderStoryCanvases(); }
    } else {
      var dur = card._dur || 1;
      drawOverviewOn(inlineCv, card, elapsedSec / dur);
    }
  }

  function renderFake(card) {
    var cv = card.querySelector('.mid-card__canvas');
    if (!cv) { return; }
    sizeCanvas(cv);
    var seed = (card.getAttribute('data-fake') || 'x').length;
    var rng = function () { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
    var notes = [];
    var t = 0;
    for (var i = 0; i < 26; i++) {
      t += rng() * 0.35;
      notes.push({ time: t, duration: 0.15 + rng() * 0.3, midi: 52 + Math.floor(rng() * 24) });
    }
    preProcessMidi(notes);
    card._notes = notes;
    card._dur = t + 0.5;
    card._minPitch = 52; card._maxPitch = 76;
    card._bpm = 120;
    draw(card, 0);
  }

  function load(card) {
    if (card._loading || card._notes) { return; }
    card._loading = true;
    var url = card.getAttribute('data-midi-url');
    var cv = card.querySelector('.mid-card__canvas');
    if (!url || !cv) { return; }
    sizeCanvas(cv);
    loadLibs().then(function () {
      return window.Midi.fromUrl(url);
    }).then(function (midi) {
      var notes = [];
      var minPitch = 127, maxPitch = 0;
      var lastEnd = 0;
      card._tracks = [];
      midi.tracks.forEach(function (track, trackIdx) {
        var hasNotes = track.notes && track.notes.length > 0;
        if (hasNotes) {
          card._tracks.push({
            name: track.name || ("Track " + (trackIdx + 1)),
            enabled: true,
            trackIdx: trackIdx,
            controlChanges: track.controlChanges
          });
        }
        track.notes.forEach(function (n) {
          notes.push({ time: n.time, duration: n.duration, midi: n.midi, velocity: n.velocity, trackIdx: trackIdx });
          if (n.midi < minPitch) { minPitch = n.midi; }
          if (n.midi > maxPitch) { maxPitch = n.midi; }
          var end = n.time + n.duration;
          if (end > lastEnd) { lastEnd = end; }
        });
      });
      notes.sort(function (a, b) { return a.time - b.time; });
      preProcessMidi(notes);
      card._notes = notes;
      card._dur = lastEnd || 1;
      card._minPitch = minPitch; card._maxPitch = maxPitch;
      card._bpm = (midi.header.tempos && midi.header.tempos.length) ? Math.round(midi.header.tempos[0].bpm) : 120;
      var fname = card.querySelector('.wf-card__name');
      if (fname && fname.textContent) {
        // Social handle doubles the D; the producer name does not. Catch every
        // spelling (demibby / ddemibby / ddmibby / dmibby) and render it one way.
        fname.textContent = fname.textContent.replace(/d{1,2}e?mibby/gi, 'Demibby');
      }
      card._name = fname ? fname.textContent : 'MIDI';
      var t = card.querySelector('.mid-card__time');
      if (t) { t.textContent = fmt(card._dur); }
      card.classList.add('is-ready');
      draw(card, 0);
      if (current === card) { syncTransportTo(card); updateStoryInfo(card); }
    }).catch(function () {
      renderFake(card);
      if (current === card) { syncTransportTo(card); updateStoryInfo(card); }
    });
  }

  /* ---- voices ---- */
  var voiceCache = {};
  var currentVoices = ['triangle'];
  var eerieUrls = {};
  var CDN = 'https://cdn.shopify.com/s/files/1/0637/0456/3966/files/';

  var SINE_URL = CDN + 'sine-C4.wav?v=1786455986';
  var TRIANGLE_URL = CDN + 'triangle-C4.wav?v=1786455986';
  var TONE_BASE_FREQ = 261.6255653;

  var masterLimiter = null;
  var masterCompressor = null;
  var cleanBus = null;
  var dryCompressedBus = null;
  var stringsBus = null;
  var jmcConv = null;
  var kiriConv = null;
  var JMC_URL = 'https://cdn.shopify.com/s/files/1/0637/0456/3966/files/JMC_IR_ER.wav';
  var KIRI_URL = 'https://cdn.shopify.com/s/files/1/0637/0456/3966/files/Kirishima_Concert_Hall_Track_Reverb_Tail.wav';

  var dryGain = null;
  var jmcSend = null;
  var kiriSend = null;

  function getMasterBus(T, name) {
    if (!masterLimiter) {
      masterLimiter = new T.Limiter(0).toDestination();
      masterCompressor = new T.Compressor({
        threshold: -12,
        ratio: 3,
        attack: 0.015,
        release: 0.12
      }).connect(masterLimiter);

      cleanBus = new T.Gain(1.0).connect(masterLimiter);
      dryCompressedBus = new T.Gain(1.0).connect(masterCompressor);
      stringsBus = new T.Gain(1.0);

      dryGain = new T.Gain(1.0).connect(masterCompressor);
      stringsBus.connect(dryGain);

      var rawCtx = T.context.rawContext || T.context.context || T.context;
      jmcConv = rawCtx.createConvolver();
      jmcSend = new T.Gain(0.40);
      stringsBus.connect(jmcSend);

      T.Buffer.fromUrl(JMC_URL).then(function(buf) {
        jmcConv.buffer = buf.get();
        jmcSend.connect(jmcConv);
        jmcConv.connect(masterCompressor.input);
        console.log("Native JMC Early Reflections IR loaded and connected successfully! Duration: " + buf.duration + "s");
      }).catch(function(e) {
        console.error("Failed to load JMC Early Reflections IR: ", e);
      });

      kiriConv = rawCtx.createConvolver();
      kiriSend = new T.Gain(0.75);
      stringsBus.connect(kiriSend);

      T.Buffer.fromUrl(KIRI_URL).then(function(buf) {
        kiriConv.buffer = buf.get();
        kiriSend.connect(kiriConv);
        kiriConv.connect(masterCompressor.input);
        console.log("Native Kirishima Track Reverb Tail IR loaded and connected successfully! Duration: " + buf.duration + "s");
      }).catch(function(e) {
        console.error("Failed to load Kirishima Track Reverb Tail IR: ", e);
      });
    }

    if (name === 'sine' || name === 'triangle') {
      return cleanBus;
    }
    if (name === 'strings') {
      return stringsBus;
    }
    return dryCompressedBus;
  }

  var RHODES_URLS = {
    soft: {
      'C2': CDN + 'rhodes-C3-soft-nofx.wav',
      'D#3': CDN + 'rhodes-Ds4-soft-nofx.wav',
      'F2': CDN + 'rhodes-F3-soft-nofx.wav',
      'G#3': CDN + 'rhodes-Gs4-soft-nofx.wav',
      'A#2': CDN + 'rhodes-As3-soft-nofx.wav',
      'C#4': CDN + 'rhodes-Cs5-soft-nofx.wav',
      'F#4': CDN + 'rhodes-Fs5-soft-nofx.wav',
      'A5': CDN + 'rhodes-A6-soft-nofx.wav',
      'B4': CDN + 'rhodes-B5-soft-nofx.wav',
      'E5': CDN + 'rhodes-E6-soft-nofx.wav'
    },
    mid: {
      'C2': CDN + 'rhodes-C3-mid-nofx.wav',
      'D#3': CDN + 'rhodes-Ds4-mid-nofx.wav',
      'F2': CDN + 'rhodes-F3-mid-nofx.wav',
      'G#3': CDN + 'rhodes-Gs4-mid-nofx.wav',
      'A#2': CDN + 'rhodes-As3-mid-nofx.wav',
      'C#4': CDN + 'rhodes-Cs5-mid-nofx.wav',
      'F#4': CDN + 'rhodes-Fs5-mid-nofx.wav',
      'A5': CDN + 'rhodes-A6-mid-nofx.wav',
      'B4': CDN + 'rhodes-B5-mid-nofx.wav',
      'E5': CDN + 'rhodes-E6-mid-nofx.wav'
    },
    hard: {
      'C2': CDN + 'rhodes-C3-hard-nofx.wav',
      'D#3': CDN + 'rhodes-Ds4-hard-nofx.wav',
      'F2': CDN + 'rhodes-F3-hard-nofx.wav',
      'G#3': CDN + 'rhodes-Gs4-hard-nofx.wav',
      'A#2': CDN + 'rhodes-As3-hard-nofx.wav',
      'C#4': CDN + 'rhodes-Cs5-hard-nofx.wav',
      'F#4': CDN + 'rhodes-Fs5-hard-nofx.wav',
      'A5': CDN + 'rhodes-A6-hard-nofx.wav',
      'B4': CDN + 'rhodes-B5-hard-nofx.wav',
      'E5': CDN + 'rhodes-E6-hard-nofx.wav'
    },
    max: {
      'C2': CDN + 'rhodes-C3-max-nofx.wav',
      'D#3': CDN + 'rhodes-Ds4-max-nofx.wav',
      'F2': CDN + 'rhodes-F3-max-nofx.wav',
      'G#3': CDN + 'rhodes-Gs4-max-nofx.wav',
      'A#2': CDN + 'rhodes-As3-max-nofx.wav',
      'C#4': CDN + 'rhodes-Cs5-max-nofx.wav',
      'F#4': CDN + 'rhodes-Fs5-max-nofx.wav',
      'A5': CDN + 'rhodes-A6-max-nofx.wav',
      'B4': CDN + 'rhodes-B5-max-nofx.wav',
      'E5': CDN + 'rhodes-E6-max-nofx.wav'
    }
  };
  var RHODES_TIERS = ['soft', 'mid', 'hard', 'max'];

  function tierForVelocity(vel) {
    if (vel < 0.3) { return 'soft'; }
    if (vel < 0.55) { return 'mid'; }
    if (vel < 0.8) { return 'hard'; }
    return 'max';
  }

  var STRINGS_URLS = {
    'C1': CDN + 'EW_PLAY_DS_PATCH_2_C2_127.mp3',
    'F1': CDN + 'EW_PLAY_DS_PATCH_2_F2_127.mp3',
    'A1': CDN + 'EW_PLAY_DS_PATCH_2_A2_127.mp3',
    'C2': CDN + 'EW_PLAY_DS_PATCH_2_C3_127.mp3',
    'F2': CDN + 'EW_PLAY_DS_PATCH_2_F3_127.mp3',
    'A2': CDN + 'EW_PLAY_DS_PATCH_2_A3_127.mp3',
    'D3': CDN + 'EW_PLAY_DS_PATCH_2_D4_127.mp3',
    'G3': CDN + 'EW_PLAY_DS_PATCH_2_G4_127.mp3',
    'B3': CDN + 'EW_PLAY_DS_PATCH_2_B4_127.mp3',
    'E4': CDN + 'EW_PLAY_DS_PATCH_2_E5_127.mp3',
    'A4': CDN + 'EW_PLAY_DS_PATCH_2_A5_127.mp3',
    'D5': CDN + 'EW_PLAY_DS_PATCH_2_D6_127.mp3',
    'G5': CDN + 'EW_PLAY_DS_PATCH_2_G6_127.mp3',
    'B5': CDN + 'EW_PLAY_DS_PATCH_2_B6_127.mp3',
    'E6': CDN + 'EW_PLAY_DS_PATCH_2_E7_127.mp3',
    'A6': CDN + 'EW_PLAY_DS_PATCH_2_A7_127.mp3',
    'D7': CDN + 'EW_PLAY_DS_PATCH_2_D8_127.mp3',
    'F7': CDN + 'EW_PLAY_DS_PATCH_2_F8_127.mp3'
  };

  var VIOLIN_URLS = {
    'G3': CDN + 'Bohemian_Violin_DS_Patch_1_G4_127.mp3',
    'B3': CDN + 'Bohemian_Violin_DS_Patch_1_B4_127.mp3',
    'E4': CDN + 'Bohemian_Violin_DS_Patch_1_E5_127.mp3',
    'A4': CDN + 'Bohemian_Violin_DS_Patch_1_A5_127.mp3',
    'D5': CDN + 'Bohemian_Violin_DS_Patch_1_D6_127.mp3',
    'G5': CDN + 'Bohemian_Violin_DS_Patch_1_G6_127.mp3',
    'B5': CDN + 'Bohemian_Violin_DS_Patch_1_B6_127.mp3',
    'E6': CDN + 'Bohemian_Violin_DS_Patch_1_E7_127.mp3',
    'A6': CDN + 'Bohemian_Violin_DS_Patch_1_A7_127.mp3',
    'D7': CDN + 'Bohemian_Violin_DS_Patch_1_D8_127.mp3',
    'F7': CDN + 'Bohemian_Violin_DS_Patch_1_F8_127.mp3'
  };

      function getProceduralNoteEnvelope(n, t, bpm) {
    if (!n) return 0;
    var dur = n.duration;
    
    // Confine envelope completely within the note duration [0, dur]
    if (t < 0 || t > dur) return 0;

    // Retrieve note-specific volume levels calculated in preProcessMidi
    var startVol = n.envStart !== undefined ? n.envStart : 0.35;
    var peakVol = n.envPeak !== undefined ? n.envPeak : 0.98;
    var dipVol = n.envDip !== undefined ? n.envDip : 0.52;
    var endVol = n.envEnd !== undefined ? n.envEnd : 0.0;

    // A. Dynamic Freeze & Accent Spike for Monophonic Runs
    if (n.inMonophonicRun) {
      var att = 0.020; // 20ms accent spike
      var baseRunVol = peakVol * 0.80;
      var spikeVol = peakVol * 0.85;
      if (t <= att) {
        var frac = Math.sin((t / att) * Math.PI / 2);
        return baseRunVol + (spikeVol - baseRunVol) * frac;
      } else {
        var decayStart = Math.max(att, dur - 0.030);
        if (t <= decayStart) {
          return baseRunVol;
        } else {
          var frac = (t - decayStart) / (dur - decayStart);
          return baseRunVol + (endVol - baseRunVol) * frac;
        }
      }
    }

    // B. Anvil Envelope for Bass Notes (heavy rhythmic foundation, no swells/dips)
    if (n.role === 'BASS') {
      var att = 0.015; // 15ms sharp attack
      var decayStart = Math.max(att, dur - 0.030);
      if (t <= att) {
        var frac = Math.sin((t / att) * Math.PI / 2);
        return startVol + (peakVol - startVol) * frac;
      } else if (t <= decayStart) {
        return peakVol;
      } else {
        var frac = (t - decayStart) / (dur - decayStart);
        return peakVol + (endVol - peakVol) * frac;
      }
    }

    // C. Flat-Top Envelope for Short Stuff & Fast Rhythms (Duration < 0.4s)
    if (dur < 0.40) {
      var att = 0.015; // 15ms instant attack
      var decayStart = Math.max(att, dur - 0.030);
      if (t <= att) {
        var frac = Math.sin((t / att) * Math.PI / 2);
        return startVol + (peakVol - startVol) * frac;
      } else if (t <= decayStart) {
        return peakVol;
      } else {
        var frac = (t - decayStart) / (dur - decayStart);
        return peakVol + (endVol - peakVol) * frac;
      }
    }

    // 1. STACCATO: Short, sudden, punchy attack with rapid decay (Fallback for dur >= 0.4s if articulation set)
    if (n.articulation === 'STACCATO') {
      var att = 0.015; // 15ms instant straight attack!
      var decayStart = dur * 0.45; // Starts decaying early in the short note duration
      if (t <= att) {
        var frac = Math.sin((t / att) * Math.PI / 2); // outward curve
        return startVol + (peakVol - startVol) * frac;
      } else if (t <= decayStart) {
        return peakVol;
      } else {
        // Rapid decay to 0 before the note ends to simulate staccato air gap
        var frac = (t - decayStart) / (dur - decayStart);
        return Math.max(0, peakVol + (endVol - peakVol) * frac);
      }
    }

    // 2. MARCATO: Heavily accented strike that decays quickly to a sustain level
    if (n.articulation === 'MARCATO') {
      var att = 0.025; // 25ms sharp accent strike
      var accentDecayTime = Math.min(0.18, dur * 0.35); // Decays to sustain level quickly
      var sustainVol = peakVol * 0.65; // Sustain body of the note
      
      if (t <= att) {
        var frac = Math.sin((t / att) * Math.PI / 2); // outward curve
        return startVol + (peakVol - startVol) * frac;
      } else if (t <= att + accentDecayTime) {
        var frac = (t - att) / accentDecayTime;
        return peakVol - (peakVol - sustainVol) * frac;
      } else if (t <= dur * 0.85) {
        return sustainVol;
      } else {
        var decayStart = dur * 0.85;
        var frac = (t - decayStart) / (dur - decayStart);
        return Math.max(0, sustainVol + (endVol - sustainVol) * frac);
      }
    }

    // 3. ARC: Rounded dome swell with no dips and smooth connection volume
    if (n.articulation === 'ARC') {
      var halfDur = dur * 0.5;
      if (t <= halfDur) {
        var frac = Math.sin((t / halfDur) * Math.PI / 2); // outward curve
        return startVol + (peakVol - startVol) * frac;
      } else {
        var frac = Math.sin(((dur - t) / halfDur) * Math.PI / 2); // outward decay
        return endVol + (peakVol - endVol) * frac;
      }
    }

    // 4. SUSTAIN: Full expressive cinematic bowing curves
    if (n.isLegato) {
      // Legato sustain: smooth stable transition, no bowing relaxation dip!
      var attTime = Math.min(0.20, dur * 0.20);
      var decayStart = dur * 0.80;
      if (t <= attTime) {
        var frac = Math.sin((t / attTime) * Math.PI / 2); // outward curve
        return startVol + (peakVol - startVol) * frac;
      } else if (t <= decayStart) {
        return peakVol;
      } else {
        var frac = (t - decayStart) / (dur - decayStart);
        return Math.max(0, peakVol + (endVol - peakVol) * frac);
      }
    } else {
      // Non-legato sustain: express bowing with relaxation dip
      var attTime = Math.min(0.15, dur * 0.15); // Quick swell at the start
      var midTime = dur * 0.45;
      var lateTime = dur * 0.80;

      if (t <= attTime) {
        var frac = Math.sin((t / attTime) * Math.PI / 2); // outward curve
        return startVol + (peakVol - startVol) * frac;
      } else if (t <= midTime) {
        var frac = (t - attTime) / (midTime - attTime);
        return peakVol - (peakVol - dipVol) * frac;
      } else if (t <= lateTime) {
        var frac = (t - midTime) / (lateTime - midTime);
        return dipVol + (peakVol - dipVol) * frac;
      } else {
        var decayStartT = lateTime;
        var frac = (t - decayStartT) / (dur - decayStartT);
        return Math.max(0, peakVol + (endVol - peakVol) * frac);
      }
    }
  }

  function preProcessMidi(notes) {
    // 1. Tag notes with their role and calculate overlapping density
    for (var i = 0; i < notes.length; i++) {
      var n = notes[i];
      var isHighest = true;
      var isLowest = true;
      var activeCount = 1;
      
      for (var j = 0; j < notes.length; j++) {
        if (i === j) continue;
        var other = notes[j];
        
        var overlap = (other.time <= n.time && (other.time + other.duration) > n.time)
                   || (n.time <= other.time && (n.time + n.duration) > other.time);
        
        if (overlap) {
          activeCount++;
          if (other.midi > n.midi) {
            isHighest = false;
          }
          if (other.midi < n.midi) {
            isLowest = false;
          }
        }
      }
      
      n.isHighest = isHighest;
      n.isLowest = isLowest;
      n.role = isHighest ? 'MELODY' : (isLowest ? 'BASS' : 'HARMONY');
      n.activeCount = activeCount;
    }

    // 2. Separate notes into voices to setup prevNote / nextNote linkages (strictly per track)
    var voices = [];
    notes.forEach(function (n) {
      var bestVoice = null;
      var minGap = Infinity;
      for (var i = 0; i < voices.length; i++) {
        var v = voices[i];
        var lastNote = v[v.length - 1];
        var gap = n.time - (lastNote.time + lastNote.duration);
        if (n.trackIdx === lastNote.trackIdx && gap >= -0.05 && gap < minGap) {
          bestVoice = v;
          minGap = gap;
        }
      }
      if (bestVoice && minGap < 1.5) {
        bestVoice.push(n);
      } else {
        voices.push([n]);
      }
    });

    voices.forEach(function (v) {
      for (var i = 0; i < v.length; i++) {
        var n = v[i];
        n.prevNote = i > 0 ? v[i - 1] : null;
        n.nextNote = i < v.length - 1 ? v[i + 1] : null;
        
        var gapFromPrev = n.prevNote ? (n.time - (n.prevNote.time + n.prevNote.duration)) : Infinity;
        var gapToNext = n.nextNote ? (n.nextNote.time - (n.time + n.duration)) : Infinity;
        
        n.gapFromPrev = gapFromPrev;
        n.gapToNext = gapToNext;
        n.isLegato = (gapFromPrev <= 0.10); // 100ms legato gap threshold
        
        // 1. Monophonic Run Step-Filter
        n.inMonophonicRun = false;
        if (n.prevNote) {
          var pitchInterval = Math.abs(n.midi - n.prevNote.midi);
          if (gapFromPrev <= 0.150 && pitchInterval <= 4) {
            n.inMonophonicRun = true;
          }
        }
        
        // Classify articulation based on duration (staccato, marcato, arc, sustain)
        if (n.duration <= 0.28) {
          n.articulation = 'STACCATO';
        } else if (n.duration > 0.28 && n.duration <= 0.60) {
          n.articulation = 'MARCATO';
        } else if (n.duration > 0.60 && n.duration <= 1.20) {
          n.articulation = 'ARC';
        } else {
          n.articulation = 'SUSTAIN';
        }
      }
    });

    // 3. Pre-calculate custom, relative, role-based dynamics for each note
    for (var i = 0; i < notes.length; i++) {
      var n = notes[i];
      
      var basePeak = 0.85;
      var baseDip = 0.55;
      var baseStart = 0.35;
      
      if (n.role === 'BASS') {
        // Bass foundation is stable - Anvil Envelope settings
        basePeak = 0.85;
        baseDip = 0.80; // Very little dip/fluctuation
        baseStart = 0.65; // Starts solid
      } else if (n.role === 'MELODY') {
        // Melodic lines soar and climb
        var pitchFactor = 1.0 + (n.midi - 72) * 0.015;
        basePeak = Math.max(0.70, Math.min(1.0, 0.90 * pitchFactor));
        basePeak = Math.max(0.35, Math.min(1.0, basePeak)); // Clamp melody peak
        baseDip = basePeak * 0.52; // Deep bowing relaxation
        baseStart = 0.35;
        
        if (n.isLegato) {
          baseStart = 0.70; // Carry the bow over
        }
      } else {
        // Harmony depth
        var isLonely = n.activeCount <= 2;
        basePeak = isLonely ? 0.80 : 0.65;
        basePeak = Math.max(0.55, Math.min(0.75, basePeak)); // Clamp harmony peak
        baseDip = basePeak * 0.60;
        baseDip = Math.max(0.52, Math.min(0.68, baseDip)); // Clamp harmony dip
        baseStart = 0.30;
        
        if (n.isLegato) {
          baseStart = 0.55;
        }
        baseStart = Math.max(0.50, Math.min(0.60, baseStart)); // Clamp harmony start
      }

      // Check melodic direction relative to previous note
      if (n.prevNote) {
        if (n.midi > n.prevNote.midi) {
          // Climbing pitch: crescendo!
          basePeak = Math.min(1.0, basePeak + 0.08);
          baseDip = Math.min(basePeak, baseDip + 0.05);
        } else if (n.midi < n.prevNote.midi) {
          // Falling pitch: decrescendo!
          basePeak = Math.max(0.55, basePeak - 0.08);
          baseDip = Math.max(0.30, baseDip - 0.05);
        }
      }
      
      // Fast runs require bow pressure - give staccatos and marcatos a volume boost
      if (n.articulation === 'STACCATO' || n.articulation === 'MARCATO') {
        basePeak = Math.min(1.0, basePeak + 0.12);
      }
      
      n.envPeak = basePeak;
      n.envDip = baseDip;
    }

    // 4. Resolve connection volumes (envStart and envEnd) based on neighboring gaps and phrase-aware floors
    for (var i = 0; i < notes.length; i++) {
      var n = notes[i];
      
      // Phrase-Aware Target Volume Floor
      var dynamicFloor = 0.0;
      if (n.isLegato || n.gapToNext <= 0.200) {
        dynamicFloor = 0.65; // Hold 65% of peak volume across the boundary
      } else if (n.role === 'MELODY' && n.gapToNext <= 0.350) {
        dynamicFloor = 0.50; // Melody retains a higher emotional connection floor
      }
      n.dynamicFloor = dynamicFloor;

      if (n.articulation === 'STACCATO') {
        n.envEnd = 0.0; // Staccatos always decay to 0
      } else {
        var rawEnd = (n.role === 'BASS') ? n.envPeak : (n.role === 'MELODY' ? n.envPeak * 0.85 : n.envPeak * 0.75);
        n.envEnd = Math.max(dynamicFloor, rawEnd);
      }
    }

    for (var i = 0; i < notes.length; i++) {
      var n = notes[i];
      
      var defaultStart = 0.35;
      if (n.role === 'BASS') defaultStart = 0.65;
      else if (n.role === 'MELODY') defaultStart = 0.35;
      else defaultStart = 0.30;
      
      if (n.prevNote) {
        if (n.gapFromPrev <= 0.10) {
          // Legato: start exactly at previous note's end volume
          n.envStart = n.prevNote.envEnd;
        } else if (n.gapFromPrev <= 0.20) {
          n.envStart = Math.max(0.65, defaultStart);
        } else if (n.gapFromPrev <= 0.35) {
          n.envStart = Math.max(0.50, defaultStart);
        } else {
          n.envStart = defaultStart;
        }
      } else {
        n.envStart = defaultStart;
      }
    }
  }

    function scheduleGainAutomation(T, gainNode, n, baseVolume, now, dur, rate, attackSec) {
    var gainParam = gainNode.gain;
    gainParam.cancelScheduledValues(now);
    
    if (applyAutomationToVoice) {
      var cardBpm = current ? (current._bpm || 120) : 120;
      var stepTime = 0.040; // real seconds between envelope points

      // dur is real seconds (it is handed straight to triggerAttackRelease).
      // Automation data is indexed in original, base-tempo seconds, so the
      // conversion is original = real * rate. The previous code divided by
      // rate and then multiplied the result back, which sampled the envelope
      // over a window 1/rate^2 too wide: at 40 BPM against a 120 BPM file the
      // curve reached its tail after about a third of the note and the rest
      // of the note played silent.
      var initialEnv = getCCAutomationValue(current, n.trackIdx, n.time);
      if (initialEnv === null) {
        initialEnv = getProceduralNoteEnvelope(n, 0, cardBpm);
      }
      gainParam.setValueAtTime(Math.max(0.001, initialEnv * baseVolume), now);

      var r = stepTime;
      while (r <= dur) {
        var origT = r * rate;
        var envVal = getCCAutomationValue(current, n.trackIdx, n.time + origT);
        if (envVal === null) {
          envVal = getProceduralNoteEnvelope(n, origT, cardBpm);
        }
        gainParam.linearRampToValueAtTime(envVal * baseVolume, now + r);
        r += stepTime;
      }
      gainParam.linearRampToValueAtTime(0.001, now + dur);
      gainParam.linearRampToValueAtTime(0.0, now + dur + 0.15);
    } else {
      var att = attackSec || 0;
      if (att > 0) {
        gainParam.setValueAtTime(0.001, now);
        gainParam.linearRampToValueAtTime(baseVolume, now + att);
      } else {
        gainParam.setValueAtTime(baseVolume, now);
      }
      gainParam.setValueAtTime(baseVolume, now + dur);
      gainParam.linearRampToValueAtTime(0.0, now + dur + 0.5);
    }
  }

  function scheduleStringGainAutomation(T, gainNode, baseVolume, now, dur, rate, isViolin, noteObj, card) {
    var gainParam = gainNode.gain;
    gainParam.cancelScheduledValues(now);
    
    if (!applyAutomationToVoice) {
      gainParam.setValueAtTime(baseVolume, now);
      gainParam.setValueAtTime(baseVolume, now + dur);
      gainParam.linearRampToValueAtTime(0.0, now + dur + 0.15);
      return;
    }

    // Try to get CC events for this note
    var targetTrack = null;
    if (card && card._tracks && noteObj && noteObj.trackIdx !== undefined) {
      for (var i = 0; i < card._tracks.length; i++) {
        if (card._tracks[i].trackIdx === noteObj.trackIdx) {
          targetTrack = card._tracks[i];
          break;
        }
      }
    }

    var ccEvents = null;
    if (targetTrack && targetTrack.controlChanges) {
      ccEvents = targetTrack.controlChanges[11] || targetTrack.controlChanges['11'];
      if (!ccEvents || ccEvents.length === 0) {
        ccEvents = targetTrack.controlChanges[1] || targetTrack.controlChanges['1'];
      }
    }
    // delayT / crossfadeDur / relTime are physical envelope constants in real
    // seconds and must not be scaled by rate. Envelope and CC lookups are
    // indexed in original, base-tempo seconds, so original = real * rate.
    var relTime = isViolin ? 0.6 : 0.8;
    var delayT = 0.25;        // natural attack window before the curve takes over
    var crossfadeDur = 0.05;  // blend window into the curve
    var stepSec = 0.04;       // real seconds between procedural points
    var totalReal = dur + relTime;
    var handoff = delayT + crossfadeDur;
    var cardBpm = card ? card._bpm : 120;

    if (dur <= handoff) {
      // Too short to be worth a curve: hold the level and fade at release.
      gainParam.setValueAtTime(baseVolume, now);
      gainParam.setValueAtTime(baseVolume, now + dur);
      gainParam.linearRampToValueAtTime(0.001, now + totalReal);
      return;
    }

    // 1. Natural attack window: hold the sample at its own level.
    gainParam.setValueAtTime(baseVolume, now);
    gainParam.setValueAtTime(baseVolume, now + delayT);

    if (!ccEvents || ccEvents.length === 0) {
      // 2. Crossfade into the procedural curve.
      var targetCurveVol = baseVolume * getProceduralNoteEnvelope(noteObj, handoff * rate, cardBpm);
      gainParam.linearRampToValueAtTime(targetCurveVol, now + handoff);

      // 3. Run the curve across the whole remaining note, in real time.
      for (var rr = handoff + stepSec; rr <= totalReal; rr += stepSec) {
        var volFrac = getProceduralNoteEnvelope(noteObj, rr * rate, cardBpm);
        gainParam.linearRampToValueAtTime(baseVolume * volFrac, now + rr);
      }
      gainParam.linearRampToValueAtTime(0.001, now + totalReal);
      return;
    }

    // 2. Crossfade into the recorded CC curve.
    var startT = noteObj.time;
    var targetCC = getCCAutomationValue(card, noteObj.trackIdx, startT + handoff * rate);
    var targetVol = (targetCC !== null) ? baseVolume * targetCC : baseVolume;
    gainParam.linearRampToValueAtTime(targetVol, now + handoff);

    // 3. Schedule the remaining CC events, converted to real time.
    ccEvents.forEach(function (e) {
      var relReal = (e.time - startT) / rate;
      if (relReal > handoff && relReal < totalReal) {
        var rawVal = e.value;
        var normVal = rawVal > 1.0 ? rawVal / 127.0 : rawVal;
        gainParam.linearRampToValueAtTime(baseVolume * normVal, now + relReal);
      }
    });

    gainParam.linearRampToValueAtTime(0.001, now + totalReal);
  }

  function buildStringVoicePool(T, size, bus) {
    var pool = [];
    for (var i = 0; i < size; i++) {
      var sGain = new T.Gain(1).connect(bus);
      var vGain = new T.Gain(1).connect(bus);

      var stringsSampler = new T.Sampler({
        urls: STRINGS_URLS,
        volume: 2,
        release: 0.8,
        onerror: function (e) { /* ignore */ }
      }).connect(sGain);

      var violinSampler = new T.Sampler({
        urls: VIOLIN_URLS,
        volume: -14,
        release: 0.8,
        onerror: function (e) { /* ignore */ }
      }).connect(vGain);

      pool.push({
        strings: stringsSampler,
        violin: violinSampler,
        sGain: sGain,
        vGain: vGain,
        lastUsedTime: 0,
        activeEndTime: 0
      });
    }

    return {
      isReady: function () {
        for (var i = 0; i < pool.length; i++) {
          var s = pool[i].strings, v = pool[i].violin;
          if (s && s.loaded === false) { return false; }
          if (v && v.loaded === false) { return false; }
        }
        return true;
      },
      triggerAttackRelease: function (freq, dur, time, vel, n) {
        var now = time || T.now();
        var bestVoice = null;
        var oldestTime = Infinity;
        for (var i = 0; i < size; i++) {
          var v = pool[i];
          if (now >= v.activeEndTime) {
            bestVoice = v;
            break;
          }
          if (v.lastUsedTime < oldestTime) {
            oldestTime = v.lastUsedTime;
            bestVoice = v;
          }
        }

        bestVoice.lastUsedTime = now;
        bestVoice.activeEndTime = now + dur + 0.5;

        var rate = currentBPM / (current._bpm || 120);
        var midiVal = n ? n.midi : 60;
        var pitchScale = 1.0 + (midiVal - 60) * 0.012;
        pitchScale = Math.max(0.4, Math.min(1.6, pitchScale));

        // Force max velocity (highest volume / dynamics) for realistic strings
        var stringVelocity = 1.0;
        var stringsBaseVol = stringVelocity * pitchScale * 0.9;
        var violinBaseVol = stringVelocity * pitchScale * 0.75;

        // Apply dynamic bowing expression curves for realistic, moving strings
        scheduleStringGainAutomation(T, bestVoice.sGain, stringsBaseVol, now, dur, rate, false, n, current);
        try {
          bestVoice.strings.triggerAttackRelease(freq, dur, now);
        } catch (e) {}

        // Trigger the violin (Bohemian Violin) layer only if the pitch falls within its physical range (G3 to F7 / MIDI 55 to 101)
        if (n && n.isHighest && midiVal >= 55 && midiVal <= 101) {
          scheduleStringGainAutomation(T, bestVoice.vGain, violinBaseVol, now, dur, rate, true, n, current);
          try {
            bestVoice.violin.triggerAttackRelease(freq, dur, now);
          } catch (e) {}
        }
      },
      releaseAll: function () {
        pool.forEach(function (v) {
          var now = T.now();
          v.activeEndTime = 0;
          try {
            v.sGain.gain.cancelScheduledValues(now);
            v.sGain.gain.linearRampToValueAtTime(0.001, now + 0.5);
          } catch (e) {}
          try {
            v.vGain.gain.cancelScheduledValues(now);
            v.vGain.gain.linearRampToValueAtTime(0.001, now + 0.5);
          } catch (e) {}
          try { v.strings.releaseAll(); } catch (e) {}
          try { v.violin.releaseAll(); } catch (e) {}
        });
      }
    };
  }

  function safeSampler(sampler) {
    return {
      triggerAttackRelease: function (freq, dur, time, vel) {
        try { sampler.triggerAttackRelease(freq, dur, time, vel); } catch (e) { /* buffer not ready yet; skip this one note */ }
      },
      releaseAll: function () {
        if (sampler.releaseAll) { try { sampler.releaseAll(); } catch (e) { /* ignore */ } }
      }
    };
  }

  function buildRhodesVoice(T, bus) {
    // Rebuilt deliberately plain: the tier samplers feed a mono mixer, the
    // mixer feeds the AutoPanner, and that is the entire chain. AutoPanner
    // settings and per-tier volume are unchanged.
    //
    // The previous version ran every note through one shared gain node and
    // called scheduleGainAutomation on it per note. That function opens with
    // cancelScheduledValues, so on a humanised chord each new note cancelled
    // the envelope of the notes still sounding and stepped the shared gain to
    // a new value. That discontinuity is the click, which is why it fired once
    // per note trigger and got worse the denser the voicing.
    //
    // A single shared gain cannot carry per-note envelopes, and Tone routes a
    // Sampler's notes through one output, so there is no per-note gain to
    // automate without a sampler pool. Dynamics come from the velocity tiers
    // and each sampler's own envelope instead.
    var panner = new T.AutoPanner({ frequency: 1.81, depth: 0.725 }).connect(bus);
    panner.start();
    var monoMixer = new T.Mono().connect(panner);

    var samplers = {};
    RHODES_TIERS.forEach(function (tier) {
      samplers[tier] = new T.Sampler({
        urls: RHODES_URLS[tier],
        volume: -2,
        onerror: function (e) { if (window.console) { console.error('Rhodes ' + tier + ' sample failed to load', e); } }
      }).connect(monoMixer);
    });

    return {
      isReady: function () {
        for (var ti = 0; ti < RHODES_TIERS.length; ti++) {
          var s = samplers[RHODES_TIERS[ti]];
          if (s && s.loaded === false) { return false; }
        }
        return true;
      },
      triggerAttackRelease: function (freq, dur, time, vel) {
        var v = vel == null ? 0.7 : vel;
        var s = samplers[tierForVelocity(v)];
        if (!s) { return; }
        var now = time || T.now();
        try {
          s.triggerAttackRelease(freq, dur, now, v);
        } catch (e) { /* this tier's buffers are not ready yet; skip this note */ }
      },
      releaseAll: function () {
        RHODES_TIERS.forEach(function (tier) {
          var s = samplers[tier];
          if (s && s.releaseAll) { try { s.releaseAll(); } catch (e) { /* ignore */ } }
        });
      }
    };
  }


  function buildLoopedToneVoice(T, url, bus) {
    var poolSize = 32;
    var voices = [];
    for (var i = 0; i < poolSize; i++) {
      var gainNode = new T.Gain(1).connect(bus);
      var env = new T.AmplitudeEnvelope({ attack: 0.008, decay: 0.12, sustain: 0.85, release: 0.35 }).connect(gainNode);
      var player = new T.Player({ url: url, loop: true, fadeOut: 0.02, volume: -14 });
      player.connect(env);
      voices.push({ player: player, env: env, gainNode: gainNode, stopId: null });
    }
    var next = 0;
    return {
      triggerAttackRelease: function (freq, dur, time, vel, n) {
        var v = voices[next];
        next = (next + 1) % voices.length;
        var when = time || T.now();
        if (v.stopId) { clearTimeout(v.stopId); v.stopId = null; }
        if (n) {
          scheduleGainAutomation(T, v.gainNode, n, vel || 0.7, when, dur, rateFor(current), 0.008);
        } else {
          v.gainNode.gain.cancelScheduledValues(when);
          v.gainNode.gain.setValueAtTime(vel || 0.7, when);
        }
        try {
          var hz = typeof freq === 'string' ? parseFloat(freq) : freq;
          v.player.playbackRate = hz / TONE_BASE_FREQ;
          try { v.player.start(when); } catch (e) { /* already looping this slot; keep going from its current position */ }
          v.env.triggerAttackRelease(dur, when, vel);
          var stopDelayMs = Math.max(150, (dur + 0.6) * 1000);
          v.stopId = setTimeout(function () {
            try { v.player.stop(); } catch (e) { /* ignore */ }
          }, stopDelayMs);
        } catch (e) { /* buffer not ready yet; skip this one note */ }
      },
      releaseAll: function () {
        voices.forEach(function (v) {
          try {
            var now = T.now();
            v.gainNode.gain.cancelScheduledValues(now);
            v.gainNode.gain.linearRampToValueAtTime(0.001, now + 0.5);
            v.env.triggerRelease();
          } catch (e) { /* ignore */ }
        });
      }
    };
  }

  function buildPolyVoicePool(T, synthConstructor, synthConfig, poolSize, bus, baseVolume) {
    var pool = [];
    for (var i = 0; i < poolSize; i++) {
      var gainNode = new T.Gain(1).connect(bus);
      var config = Object.assign({}, synthConfig, { volume: baseVolume });
      var synth = new synthConstructor(config).connect(gainNode);
      pool.push({ synth: synth, gainNode: gainNode, activeEndTime: 0, lastUsedTime: 0 });
    }
    var size = poolSize;
    return {
      triggerAttackRelease: function (freq, dur, time, vel, n) {
        var now = time || T.now();
        var bestVoice = null;
        var oldestTime = Infinity;
        for (var i = 0; i < size; i++) {
          var v = pool[i];
          if (now >= v.activeEndTime) {
            bestVoice = v;
            break;
          }
          if (v.lastUsedTime < oldestTime) {
            oldestTime = v.lastUsedTime;
            bestVoice = v;
          }
        }
        bestVoice.activeEndTime = now + dur;
        bestVoice.lastUsedTime = now;
        if (n) {
          scheduleGainAutomation(T, bestVoice.gainNode, n, vel || 0.7, now, dur, rateFor(current), synthConfig.envelope ? synthConfig.envelope.attack : 0.01);
        } else {
          bestVoice.gainNode.gain.cancelScheduledValues(now);
          bestVoice.gainNode.gain.setValueAtTime(vel || 0.7, now);
        }
        try {
          bestVoice.synth.triggerAttackRelease(freq, dur, now);
        } catch (e) {}
      },
      releaseAll: function () {
        pool.forEach(function (v) {
          var now = T.now();
          v.activeEndTime = 0;
          try {
            v.gainNode.gain.cancelScheduledValues(now);
            v.gainNode.gain.linearRampToValueAtTime(0.001, now + 0.5);
            v.synth.releaseAll();
          } catch (e) {}
        });
      }
    };
  }

  function buildVoice(name) {
    var T = window.Tone;
    if (!T) { return null; }
    var bus = getMasterBus(T, name);
    if (name.indexOf('eerie:') === 0) {
      var url = eerieUrls[name];
      if (!url) { return null; }
      var gainNode = new T.Gain(1).connect(bus);
      var sampler = new T.Sampler({
        urls: { C4: url },
        volume: -4,
        onerror: function (e) { if (window.console) { console.error('EERIE sample failed to load', e); } }
      }).connect(gainNode);
      return {
        triggerAttackRelease: function (freq, dur, time, vel, n) {
          var now = time || T.now();
          var v = vel == null ? 0.7 : vel;
          if (n) {
            scheduleGainAutomation(T, gainNode, n, v, now, dur, rateFor(current), 0.01);
          } else {
            gainNode.gain.cancelScheduledValues(now);
            gainNode.gain.setValueAtTime(v, now);
          }
          try { sampler.triggerAttackRelease(freq, dur, now, v); } catch (e) { /* buffer not ready yet; skip this one note */ }
        },
        releaseAll: function () {
          var now = T.now();
          gainNode.gain.cancelScheduledValues(now);
          gainNode.gain.linearRampToValueAtTime(0.001, now + 0.5);
          if (sampler.releaseAll) { try { sampler.releaseAll(); } catch (e) { /* ignore */ } }
        }
      };
    }
    switch (name) {
      case 'sine':
        return buildLoopedToneVoice(T, SINE_URL, bus);
      case 'triangle':
        return buildLoopedToneVoice(T, TRIANGLE_URL, bus);
      case 'rhodes':
        return buildRhodesVoice(T, bus);
      case 'pluck':
        return buildPolyVoicePool(T, T.Synth, {
          oscillator: { type: 'triangle' },
          envelope: { attack: 0.002, decay: 0.28, sustain: 0.01, release: 0.28 }
        }, 16, bus, -4);
      case 'strings':
        return buildStringVoicePool(T, 16, bus);
      case 'horns': {
        var vib2 = new T.Vibrato({ frequency: 5.8, depth: 0, wet: 1 }).connect(bus);
        rampVibratoOnset(vib2, 5.8, 0.09);
        return buildPolyVoicePool(T, T.Synth, {
          oscillator: { type: 'fatsawtooth', count: 2, spread: 14 },
          envelope: { attack: 0.06, decay: 0.2, sustain: 0.7, release: 0.5 }
        }, 16, vib2, -12);
      }
      default:
        return null;
    }
  }

  function rampVibratoOnset(vib, baseFreq, baseDepth) {
    var t = 0;
    setInterval(function () {
      if (!window.Tone) { return; }
      t += 1;
      var wobble = Math.sin(t * 0.35) * 0.35 + 0.65;
      vib.depth.rampTo(baseDepth * wobble, 0.6);
      vib.frequency.rampTo(baseFreq + Math.sin(t * 0.2) * 0.4, 0.6);
    }, 600);
  }

  // Tone throws if a Sampler is triggered before its buffers arrive, and every
  // trigger site swallows that in a bare catch - so notes were silently dropped
  // while samples loaded. Strings is the worst case: about thirty files across
  // the EastWest layer and the violin. This reports readiness so the UI can say
  // 'loading' instead of just playing nothing.
  function voiceReady(name) {
    var v = voiceCache[name];
    if (!v) { return false; }
    if (typeof v.isReady === 'function') { return v.isReady(); }
    return true;
  }

  function getVoice(name) {
    if (name.indexOf('eerie:') === 0 && !eerieUrls[name]) { return null; }
    if (!voiceCache[name]) { voiceCache[name] = buildVoice(name); }
    return voiceCache[name];
  }

  var pitchOffset = 0;
  var detuneCents = 0;
  var currentBPM = 120;
  var PITCH_MIN = -12, PITCH_MAX = 12;
  var DETUNE_MIN = -100, DETUNE_MAX = 100, DETUNE_STEP = 5;
  var BPM_MIN = 40, BPM_MAX = 300, BPM_STEP = 5;
  var widgetRefs = [];

  function updateTransportUI() {
    widgetRefs.forEach(function (widget) {
      var pitchEl = widget.querySelector('.midg__pitch-val');
      var detuneEl = widget.querySelector('.midg__detune-val');
      var bpmEl = widget.querySelector('.midg__bpm-val');
      if (pitchEl) { pitchEl.textContent = (pitchOffset > 0 ? '+' : '') + pitchOffset + ' st'; }
      if (detuneEl) { detuneEl.textContent = (detuneCents > 0 ? '+' : '') + detuneCents + 'c'; }
      if (bpmEl) { bpmEl.textContent = currentBPM + ' BPM'; }
    });
    if (storyOpen && storyEl) {
      var pitchEl = storyEl.querySelector('.midg__pitch-val');
      var detuneEl = storyEl.querySelector('.midg__detune-val');
      var bpmEl = storyEl.querySelector('.midg__bpm-val');
      if (pitchEl) { pitchEl.textContent = (pitchOffset > 0 ? '+' : '') + pitchOffset + ' st'; }
      if (detuneEl) { detuneEl.textContent = (detuneCents > 0 ? '+' : '') + detuneCents + 'c'; }
      if (bpmEl) { bpmEl.textContent = currentBPM + ' BPM'; }
    }
    // The title line prints the tempo, so it has to follow the transport.
    if (storyOpen && current) { renderStoryName(current); }
  }

  function syncTransportTo(card) {
    currentBPM = card._bpm || 120;
    updateTransportUI();
  }

  function rateFor(card) {
    var base = card._bpm || 120;
    return currentBPM / base;
  }

  var passwordUnlocked = false;
  var storyOpen = false;
  var storyEl = null, storyCanvasEl = null, storyNameEl = null, storyFrameEl = null;
  var showChordTranscriber = true;
  var updateSheetHeaderFromInputs = null;

  function updateVoiceUI() {
    var allContainers = [].concat(widgetRefs);
    if (storyEl) { allContainers.push(storyEl); }

    var isInk = (currentFormat === 'ink');
    var isLight = isInk || (currentThemeName === 'sketchbook' || currentThemeName === 'watercolor');

    allContainers.forEach(function (container) {
      container.querySelectorAll('.midg__voice, .midg__sub-voice').forEach(function (btn) {
        var voiceName = btn.getAttribute('data-voice');
        var isBtnActive = (currentVoices.indexOf(voiceName) !== -1);
        if (isBtnActive) {
          btn.classList.add('on');
        } else {
          btn.classList.remove('on');
        }

        if (container === storyEl) {
          if (isLight) {
            btn.style.color = isBtnActive ? '#fff' : '#1a1a1a';
            btn.style.backgroundColor = isBtnActive ? '#1a1a1a' : 'rgba(0,0,0,0.05)';
            btn.style.borderColor = isBtnActive ? '#1a1a1a' : 'rgba(0,0,0,0.12)';
          } else {
            btn.style.color = isBtnActive ? '#07090a' : '#fff';
            btn.style.backgroundColor = isBtnActive ? '#fff' : 'rgba(255,255,255,0.05)';
            btn.style.borderColor = isBtnActive ? '#fff' : 'rgba(255,255,255,0.15)';
          }
        }
      });
    });

    if (storyEl) {
      storyEl.querySelectorAll('.midg__step').forEach(function (sbtn) {
        sbtn.style.color = isLight ? '#1a1a1a' : '#fff';
      });
    }
  }

  function showPasswordModal() {
    if (passwordUnlocked) {
      openStory();
      return;
    }

    var style = document.createElement('style');
    style.textContent =
      '@keyframes midgShake {' +
      '  0%, 100% { transform: translateX(0); }' +
      '  20%, 60% { transform: translateX(-6px); }' +
      '  40%, 80% { transform: translateX(6px); }' +
      '}';
    document.head.appendChild(style);

    var modal = document.createElement('div');
    modal.className = 'midg__pwd-modal';
    modal.style.position = 'fixed';
    modal.style.top = '0';
    modal.style.left = '0';
    modal.style.width = '100vw';
    modal.style.height = '100vh';
    modal.style.backgroundColor = 'rgba(5, 6, 4, 0.96)';
    modal.style.zIndex = '200000';
    modal.style.display = 'flex';
    modal.style.flexDirection = 'column';
    modal.style.alignItems = 'center';
    modal.style.justifyContent = 'center';
    modal.style.fontFamily = '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

    var container = document.createElement('div');
    container.style.width = '320px';
    container.style.padding = '30px';
    container.style.borderRadius = '16px';
    container.style.border = '1px solid #28371d';
    container.style.backgroundColor = '#07090a';
    container.style.textAlign = 'center';
    container.style.boxShadow = '0 10px 30px rgba(0,0,0,0.5)';
    container.style.transition = 'all 0.3s';

    var title = document.createElement('div');
    title.textContent = 'CREATOR SPACE';
    title.style.fontSize = '12px';
    title.style.fontWeight = '700';
    title.style.letterSpacing = '3px';
    title.style.color = '#b79bff';
    title.style.marginBottom = '20px';

    var label = document.createElement('div');
    label.textContent = 'Enter access code to unlock vertical layout';
    label.style.fontSize = '11px';
    label.style.color = 'rgba(230,227,240,0.7)';
    label.style.marginBottom = '20px';

    var input = document.createElement('input');
    input.type = 'text';
    input.placeholder = 'Enter password...';
    input.style.width = '100%';
    input.style.padding = '12px';
    input.style.borderRadius = '8px';
    input.style.border = '1px solid #28371d';
    input.style.backgroundColor = '#11170c';
    input.style.color = '#f2eeff';
    input.style.fontSize = '14px';
    input.style.textAlign = 'center';
    input.style.outline = 'none';
    input.style.marginBottom = '15px';
    input.style.boxSizing = 'border-box';
    input.style.transition = 'border-color 0.2s';

    input.addEventListener('focus', function () {
      input.style.borderColor = '#b79bff';
    });
    input.addEventListener('blur', function () {
      input.style.borderColor = '#28371d';
    });

    var error = document.createElement('div');
    error.style.fontSize = '10px';
    error.style.color = '#ff4d4d';
    error.style.height = '14px';
    error.style.marginBottom = '15px';

    var btnContainer = document.createElement('div');
    btnContainer.style.display = 'flex';
    btnContainer.style.gap = '10px';

    var cancelBtn = document.createElement('button');
    cancelBtn.textContent = 'Cancel';
    cancelBtn.style.flex = '1';
    cancelBtn.style.padding = '10px';
    cancelBtn.style.borderRadius = '6px';
    cancelBtn.style.border = '1px solid #28371d';
    cancelBtn.style.backgroundColor = 'transparent';
    cancelBtn.style.color = 'rgba(230,227,240,0.7)';
    cancelBtn.style.cursor = 'pointer';
    cancelBtn.style.fontSize = '11px';
    cancelBtn.style.fontWeight = '600';

    var submitBtn = document.createElement('button');
    submitBtn.textContent = 'Unlock';
    submitBtn.style.flex = '1';
    submitBtn.style.padding = '10px';
    submitBtn.style.borderRadius = '6px';
    submitBtn.style.border = 'none';
    submitBtn.style.backgroundColor = '#b79bff';
    submitBtn.style.color = '#07090a';
    submitBtn.style.cursor = 'pointer';
    submitBtn.style.fontSize = '11px';
    submitBtn.style.fontWeight = '700';

    btnContainer.appendChild(cancelBtn);
    btnContainer.appendChild(submitBtn);

    container.appendChild(title);
    container.appendChild(label);
    container.appendChild(input);
    container.appendChild(error);
    container.appendChild(btnContainer);
    modal.appendChild(container);
    document.body.appendChild(modal);

    input.focus();

    function checkPassword() {
      var val = input.value.trim().toLowerCase();
      if (val === 'demibby demibby') {
        passwordUnlocked = true;
        document.body.removeChild(modal);
        openStory();
      } else {
        error.textContent = 'Invalid access code';
        input.style.borderColor = '#ff4d4d';
        container.style.animation = 'none';
        container.offsetHeight;
        container.style.animation = 'midgShake 0.4s ease';
      }
    }

    submitBtn.addEventListener('click', checkPassword);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') {
        checkPassword();
      }
    });

    cancelBtn.addEventListener('click', function () {
      document.body.removeChild(modal);
    });
  }

  function buildStoryOverlay() {
    if (storyEl) { return; }
    storyEl = document.createElement('div');
    storyEl.className = 'midg__story';
    storyEl.innerHTML =
      '<button type="button" class="midg__story-close" aria-label="Close">&times;</button>' +
      '<div class="midg__theme-picker" style="position: fixed; top: 24px; left: 24px; display: flex; gap: 8px; z-index: 100000; align-items: center;">' +
        '<span style="font-size: 10px; text-transform: uppercase; letter-spacing: 1.5px; opacity: 0.7; color: #fff; margin-right: 4px; font-weight: 600; font-family: sans-serif;">Theme:</span>' +
        '<button type="button" class="midg__theme-btn" data-theme="cyber" style="width: 20px; height: 20px; border-radius: 50%; border: 2px solid #fff; background: #b79bff; cursor: pointer; padding: 0; box-shadow: 0 4px 10px rgba(0,0,0,0.3); transition: transform 0.2s; outline: none;" title="Neo Cyber"></button>' +
        '<button type="button" class="midg__theme-btn" data-theme="amber" style="width: 20px; height: 20px; border-radius: 50%; border: 2px solid transparent; background: #ffb300; cursor: pointer; padding: 0; transition: transform 0.2s; outline: none;" title="Amber Gold"></button>' +
        '<button type="button" class="midg__theme-btn" data-theme="acid" style="width: 20px; height: 20px; border-radius: 50%; border: 2px solid transparent; background: #00e676; cursor: pointer; padding: 0; transition: transform 0.2s; outline: none;" title="Acid Green"></button>' +
        '<button type="button" class="midg__theme-btn" data-theme="sunset" style="width: 20px; height: 20px; border-radius: 50%; border: 2px solid transparent; background: #ff007f; cursor: pointer; padding: 0; transition: transform 0.2s; outline: none;" title="Sunset Pink"></button>' +
        '<button type="button" class="midg__theme-btn" data-theme="ice" style="width: 20px; height: 20px; border-radius: 50%; border: 2px solid transparent; background: #ffffff; cursor: pointer; padding: 0; transition: transform 0.2s; outline: none;" title="Clean White"></button>' +
        '<button type="button" class="midg__theme-btn" data-theme="sketchbook" style="width: 20px; height: 20px; border-radius: 50%; border: 2px solid transparent; background: #ebdcb9; cursor: pointer; padding: 0; transition: transform 0.2s; outline: none; display: flex; align-items: center; justify-content: center; font-size: 11px; color: #333; font-weight: bold;" title="Sketchbook">&#9998;</button>' +
        '<button type="button" class="midg__theme-btn" data-theme="watercolor" style="width: 20px; height: 20px; border-radius: 50%; border: 2px solid transparent; background: linear-gradient(135deg, #2b5c8f, #a9332a, #caa230); cursor: pointer; padding: 0; transition: transform 0.2s; outline: none;" title="Watercolor"></button>' +
        '<button type="button" class="midg__theme-btn" data-theme="soul" style="width: 20px; height: 20px; border-radius: 50%; border: 2px solid transparent; background: linear-gradient(135deg, #d89a27, #4a7b82, #b93b26); cursor: pointer; padding: 0; transition: transform 0.2s; outline: none;" title="Vintage Soul"></button>' +
      '</div>' +
      '<div class="midg__format-picker" style="position: fixed; top: 24px; right: 90px; display: flex; gap: 8px; z-index: 100000; align-items: center;">' +
        '<span style="font-size: 10px; text-transform: uppercase; letter-spacing: 1.5px; opacity: 0.7; color: #fff; margin-right: 4px; font-weight: 600; font-family: sans-serif;">Format:</span>' +
        '<button type="button" class="midg__format-btn on" data-format="classic" style="background: rgba(255,255,255,0.15); border: 1px solid #fff; color: #fff; padding: 4px 8px; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 1.5px; border-radius: 4px; cursor: pointer; transition: all 0.2s; outline: none; font-family: sans-serif;">Classic</button>' +
        '<button type="button" class="midg__format-btn" data-format="ink" style="background: rgba(255,255,255,0.05); border: 1px solid transparent; color: rgba(255,255,255,0.6); padding: 4px 8px; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 1.5px; border-radius: 4px; cursor: pointer; transition: all 0.2s; outline: none; font-family: sans-serif;">Ink Sketch</button>' +
      '</div>' +
      '<button type="button" class="midg__panel-toggle" aria-label="Show or hide parameters" style="position: fixed; top: 60px; left: 292px; z-index: 100001; width: 30px; height: 30px; border-radius: 6px; border: 1px solid rgba(255,255,255,0.25); background: rgba(0,0,0,0.6); color: #fff; font-size: 15px; line-height: 1; cursor: pointer; padding: 0; display: flex; align-items: center; justify-content: center; backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px); transition: left 0.25s ease;">&#8249;</button>' +
      '<div class="midg__meta-panel" style="position: fixed; top: 60px; left: 24px; z-index: 100000; width: 260px; max-height: 86vh; overflow-y: auto; display: none; flex-direction: column; gap: 6px; font-family: sans-serif; color: #fff; background: rgba(0,0,0,0.5); backdrop-filter: blur(10px); -webkit-backdrop-filter: blur(10px); padding: 12px; border-radius: 8px; border: 1px solid rgba(255,255,255,0.15); box-sizing: border-box; scrollbar-width: thin; scrollbar-color: rgba(255,255,255,0.2) transparent;">' +
        '<div style="font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 4px; opacity: 0.8;">Select MIDI File</div>' +
        '<div class="midg__story-midi-list" style="display: flex; flex-direction: column; gap: 6px; min-height: 140px; max-height: 280px; overflow-y: auto; padding: 4px 0; margin-bottom: 8px; scrollbar-width: thin; flex-shrink: 0;"></div>' +
        '<div class="midg__story-tracks-header" style="font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; opacity: 0.8; border-top: 1px solid rgba(255,255,255,0.15); padding-top: 8px; margin-top: 4px; margin-bottom: 4px; display: none;">MIDI Tracks</div>' +
        '<div class="midg__story-tracks-list" style="display: none; flex-direction: column; gap: 4px; min-height: 90px; max-height: 180px; overflow-y: auto; padding: 4px 0; margin-bottom: 8px; scrollbar-width: thin; flex-shrink: 0;"></div>' +
        '<div class="midg__meta-sheet-section" style="display: flex; flex-direction: column; gap: 6px;">' +
          '<div style="font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 2px; opacity: 0.8; border-bottom: 1px solid rgba(255,255,255,0.15); padding-bottom: 4px;">Sheet Metadata Editor</div>' +
          '<label style="font-size: 8px; text-transform: uppercase; opacity: 0.6; margin-top: 2px; display: block;">Title</label>' +
          '<input type="text" class="midg__input-title" style="background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.15); border-radius: 4px; color: #fff; padding: 4px 8px; font-size: 10px; width: 100%; box-sizing: border-box; outline: none;">' +
          '<label style="font-size: 8px; text-transform: uppercase; opacity: 0.6; margin-top: 2px; display: block;">Subtitle / Key (Optional)</label>' +
          '<input type="text" class="midg__input-subtitle" style="background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.15); border-radius: 4px; color: #fff; padding: 4px 8px; font-size: 10px; width: 100%; box-sizing: border-box; outline: none;">' +
          '<label style="font-size: 8px; text-transform: uppercase; opacity: 0.6; margin-top: 2px; display: block;">Creator / Composer</label>' +
          '<input type="text" class="midg__input-composer" style="background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.15); border-radius: 4px; color: #fff; padding: 4px 8px; font-size: 10px; width: 100%; box-sizing: border-box; outline: none;">' +
          '<label style="font-size: 8px; text-transform: uppercase; opacity: 0.6; margin-top: 2px; display: block;">Arranger / Editor (Optional)</label>' +
          '<input type="text" class="midg__input-editor" placeholder="e.g. Arranged by Demibby Sounds" style="background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.15); border-radius: 4px; color: #fff; padding: 4px 8px; font-size: 10px; width: 100%; box-sizing: border-box; outline: none;">' +
          '<label style="font-size: 8px; text-transform: uppercase; opacity: 0.6; margin-top: 2px; display: block;">Instrument Label</label>' +
          '<input type="text" class="midg__input-instrument" placeholder="e.g. PIANO" style="background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.15); border-radius: 4px; color: #fff; padding: 4px 8px; font-size: 10px; width: 100%; box-sizing: border-box; outline: none;">' +
          '<label style="font-size: 8px; text-transform: uppercase; opacity: 0.6; margin-top: 2px; display: block;">Tempo Mark</label>' +
          '<input type="text" class="midg__input-tempo" placeholder="e.g. Moderato" style="background: rgba(255,255,255,0.08); border: 1px solid rgba(255,255,255,0.15); border-radius: 4px; color: #fff; padding: 4px 8px; font-size: 10px; width: 100%; box-sizing: border-box; outline: none; margin-bottom: 8px;">' +
        '</div>' +
        '<div style="font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; margin-top: 8px; margin-bottom: 4px; opacity: 0.8; border-top: 1px solid rgba(255,255,255,0.15); padding-top: 8px;">Playback Voice</div>' +
        '<div class="midg__voices" style="display: flex; flex-wrap: wrap; gap: 4px; margin-bottom: 4px;">' +
          '<button type="button" class="midg__voice on" data-voice="triangle" style="flex: 1 1 30%; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.15); color: #fff; padding: 4px 8px; font-size: 9px; border-radius: 4px; cursor: pointer; text-align: center; transition: all 0.2s;">Triangle</button>' +
          '<button type="button" class="midg__voice" data-voice="sine" style="flex: 1 1 30%; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.15); color: #fff; padding: 4px 8px; font-size: 9px; border-radius: 4px; cursor: pointer; text-align: center; transition: all 0.2s;">Sine</button>' +
          '<button type="button" class="midg__voice" data-voice="rhodes" style="flex: 1 1 30%; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.15); color: #fff; padding: 4px 8px; font-size: 9px; border-radius: 4px; cursor: pointer; text-align: center; transition: all 0.2s;">Rhodes</button>' +
          '<button type="button" class="midg__voice" data-voice="pluck" style="flex: 1 1 30%; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.15); color: #fff; padding: 4px 8px; font-size: 9px; border-radius: 4px; cursor: pointer; text-align: center; transition: all 0.2s;">Pluck</button>' +
          '<button type="button" class="midg__voice" data-voice="strings" style="flex: 1 1 30%; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.15); color: #fff; padding: 4px 8px; font-size: 9px; border-radius: 4px; cursor: pointer; text-align: center; transition: all 0.2s;">Strings</button>' +
          '<button type="button" class="midg__voice" data-voice="horns" style="flex: 1 1 30%; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.15); color: #fff; padding: 4px 8px; font-size: 9px; border-radius: 4px; cursor: pointer; text-align: center; transition: all 0.2s;">Horns</button>' +
          '<button type="button" class="midg__voice midg__voice--eerie" style="flex: 1 1 100%; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.15); color: #fff; padding: 4px 8px; font-size: 9px; border-radius: 4px; cursor: pointer; text-align: center; transition: all 0.2s;">EERIE Kit &#9662;</button>' +
        '</div>' +
        '<div class="midg__sub" style="display: none; flex-direction: column; gap: 4px; background: rgba(255,255,255,0.04); padding: 6px; border-radius: 4px; margin-bottom: 6px; border: 1px dashed rgba(255,255,255,0.1);">' +
          '<button type="button" class="midg__sub-voice" data-voice="eerie:guitar-rock" data-url="https://cdn.shopify.com/s/files/1/0637/0456/3966/files/GUITAR_-_ROCK.wav?v=1785028796" style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.1); color: #fff; padding: 4px 8px; font-size: 8px; border-radius: 4px; cursor: pointer; text-align: left;">Guitar Rock</button>' +
          '<button type="button" class="midg__sub-voice" data-voice="eerie:bells-checks" data-url="https://cdn.shopify.com/s/files/1/0637/0456/3966/files/BELLS_-_CHECKS.wav?v=1786194399" style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.1); color: #fff; padding: 4px 8px; font-size: 8px; border-radius: 4px; cursor: pointer; text-align: left;">Bells Checks</button>' +
          '<button type="button" class="midg__sub-voice" data-voice="eerie:piano-stylist" data-url="https://cdn.shopify.com/s/files/1/0637/0456/3966/files/PIANO_-_STYLIST.wav?v=1786194393" style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.1); color: #fff; padding: 4px 8px; font-size: 8px; border-radius: 4px; cursor: pointer; text-align: left;">Piano Stylist</button>' +
          '<button type="button" class="midg__sub-voice" data-voice="eerie:synth-pad-zelda" data-url="https://cdn.shopify.com/s/files/1/0637/0456/3966/files/SYNTH_PAD_-_ZELDA.wav?v=1786194393" style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.1); color: #fff; padding: 4px 8px; font-size: 8px; border-radius: 4px; cursor: pointer; text-align: left;">Synth Zelda</button>' +
          '<button type="button" class="midg__sub-voice" data-voice="eerie:synth-drayk" data-url="https://cdn.shopify.com/s/files/1/0637/0456/3966/files/SYNTH_-_DRAYK.wav?v=1786194378" style="background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.1); color: #fff; padding: 4px 8px; font-size: 8px; border-radius: 4px; cursor: pointer; text-align: left;">Synth Drayk</button>' +
        '</div>' +
        '<div style="font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; margin-top: 8px; margin-bottom: 4px; opacity: 0.8; border-top: 1px solid rgba(255,255,255,0.15); padding-top: 8px;">Parameters</div>' +
        '<div style="display: flex; flex-direction: column; gap: 4px;">' +
          '<div style="display: flex; align-items: center; justify-content: space-between; font-size: 9px; background: rgba(255,255,255,0.04); padding: 4px 8px; border-radius: 4px; border: 1px solid rgba(255,255,255,0.1);">' +
            '<span style="opacity: 0.7;">Pitch</span>' +
            '<div style="display: flex; align-items: center; gap: 6px;">' +
              '<button type="button" class="midg__step" data-step="pitch" data-dir="-1" style="background: none; border: none; color: #fff; cursor: pointer; font-size: 11px; font-weight: bold; width: 14px; text-align: center;">&minus;</button>' +
              '<span class="midg__pitch-val" style="font-weight: bold;">0 st</span>' +
              '<button type="button" class="midg__step" data-step="pitch" data-dir="1" style="background: none; border: none; color: #fff; cursor: pointer; font-size: 11px; font-weight: bold; width: 14px; text-align: center;">+</button>' +
            '</div>' +
          '</div>' +
          '<div style="display: flex; align-items: center; justify-content: space-between; font-size: 9px; background: rgba(255,255,255,0.04); padding: 4px 8px; border-radius: 4px; border: 1px solid rgba(255,255,255,0.1);">' +
            '<span style="opacity: 0.7;">Fine</span>' +
            '<div style="display: flex; align-items: center; gap: 6px;">' +
              '<button type="button" class="midg__step" data-step="detune" data-dir="-5" style="background: none; border: none; color: #fff; cursor: pointer; font-size: 11px; font-weight: bold; width: 14px; text-align: center;">&minus;</button>' +
              '<span class="midg__detune-val" style="font-weight: bold;">0c</span>' +
              '<button type="button" class="midg__step" data-step="detune" data-dir="5" style="background: none; border: none; color: #fff; cursor: pointer; font-size: 11px; font-weight: bold; width: 14px; text-align: center;">+</button>' +
            '</div>' +
          '</div>' +
          '<div style="display: flex; align-items: center; justify-content: space-between; font-size: 9px; background: rgba(255,255,255,0.04); padding: 4px 8px; border-radius: 4px; border: 1px solid rgba(255,255,255,0.1);">' +
            '<span style="opacity: 0.7;">Tempo</span>' +
            '<div style="display: flex; align-items: center; gap: 6px;">' +
              '<button type="button" class="midg__step" data-step="tempo" data-dir="-1" style="background: none; border: none; color: #fff; cursor: pointer; font-size: 11px; font-weight: bold; width: 14px; text-align: center;">&minus;</button>' +
              '<span class="midg__bpm-val" style="font-weight: bold;">120 BPM</span>' +
              '<button type="button" class="midg__step" data-step="tempo" data-dir="1" style="background: none; border: none; color: #fff; cursor: pointer; font-size: 11px; font-weight: bold; width: 14px; text-align: center;">+</button>' +
            '</div>' +
          '</div>' +
        '</div>' +
        '<div style="font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; margin-top: 8px; margin-bottom: 4px; opacity: 0.8; border-top: 1px solid rgba(255,255,255,0.15); padding-top: 8px;">Position Info</div>' +
        '<div style="display: flex; flex-direction: column; gap: 4px; font-size: 10px; background: rgba(255,255,255,0.04); padding: 6px 8px; border-radius: 4px; border: 1px solid rgba(255,255,255,0.1);">' +
          '<div style="display: flex; justify-content: space-between;"><span style="opacity: 0.7;">Time:</span><span class="midg__pos-ms" style="font-weight: bold; font-family: monospace;">0 ms</span></div>' +
          '<div style="display: flex; justify-content: space-between;"><span style="opacity: 0.7;">Bar / Beat:</span><span class="midg__pos-bar" style="font-weight: bold; font-family: monospace;">1 : 1.0</span></div>' +
        '</div>' +
        '<div style="font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; margin-top: 8px; margin-bottom: 4px; opacity: 0.8; border-top: 1px solid rgba(255,255,255,0.15); padding-top: 8px;">Curve Options</div>' +
        '<div style="display: flex; flex-direction: column; gap: 6px; margin-bottom: 4px;">' +
          '<label style="display: flex; align-items: center; gap: 8px; font-size: 9px; cursor: pointer; margin: 0;" class="midg__opt-label">' +
            '<input type="checkbox" class="midg__opt-note-curves" style="cursor: pointer; width: 12px; height: 12px; accent-color: #b79bff; margin: 0;">' +
            '<span>Note-Bound Curves (Black Line)</span>' +
          '</label>' +
          '<label style="display: flex; align-items: center; gap: 8px; font-size: 9px; cursor: pointer; margin: 0;" class="midg__opt-label">' +
            '<input type="checkbox" class="midg__opt-lane-curves" style="cursor: pointer; width: 12px; height: 12px; accent-color: #b79bff; margin: 0;">' +
            '<span>Lane-Shaded Curves (Opaque)</span>' +
          '</label>' +
          '<label style="display: flex; align-items: center; gap: 8px; font-size: 9px; cursor: pointer; margin: 0;" class="midg__opt-label">' +
            '<input type="checkbox" class="midg__opt-flight-path" style="cursor: pointer; width: 12px; height: 12px; accent-color: #b79bff; margin: 0;">' +
            '<span>Global Flight Path Overlay</span>' +
          '</label>' +
            '<div style="font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; margin-top: 6px; margin-bottom: 2px; opacity: 0.55;">Macro Automation Visualizer</div>' +
          '<div style="display: flex; flex-direction: column; gap: 6px; margin-bottom: 4px;">' +
            '<div style="display: flex; align-items: center; justify-content: space-between; font-size: 9px; background: rgba(255,255,255,0.04); padding: 4px 8px; border-radius: 4px; border: 1px solid rgba(255,255,255,0.1);">' +
              '<span style="opacity: 0.7;">Visualizer Layout</span>' +
              '<select class="midg__opt-vis-layout" style="background: #111; color: #fff; border: 1px solid rgba(255,255,255,0.3); font-size: 9px; padding: 2px 4px; border-radius: 4px; outline: none; cursor: pointer;">' +
                '<option value="disabled" style="background: #111; color: #fff;">Disabled</option>' +
                '<option value="sidebar" style="background: #111; color: #fff;">Sidebar Widget</option>' +
                '<option value="overlay" style="background: #111; color: #fff;">Overlay on Roll (Bottom)</option>' +
                '<option value="overlay_full" style="background: #111; color: #fff;">Overlay on Roll (Full Screen)</option>' +
              '</select>' +
            '</div>' +
            '<div class="midg__vis-readouts" style="display: none; flex-direction: column; gap: 4px; font-size: 9px; background: rgba(255,255,255,0.04); padding: 6px 8px; border-radius: 4px; border: 1px solid rgba(255,255,255,0.1); margin-top: 2px;">' +
              '<div style="display: flex; justify-content: space-between; align-items: center;"><span style="color: #4facfe; font-weight: bold;">Melody Dynamics:</span><span class="midg__cc1-val" style="font-weight: bold; font-family: monospace;">0</span></div>' +
              '<div style="display: flex; justify-content: space-between; align-items: center;"><span style="color: #00ff87; font-weight: bold;">Harmony Dynamics:</span><span class="midg__cc11-val" style="font-weight: bold; font-family: monospace;">0</span></div>' +
              '<div style="display: flex; justify-content: space-between; align-items: center;"><span style="color: #f7d070; font-weight: bold;">Bass Dynamics:</span><span class="midg__cc21-val" style="font-weight: bold; font-family: monospace;">0</span></div>' +
            '</div>' +
            '<div class="midg__vis-sliders" style="display: none; flex-direction: column; gap: 6px; margin-top: 4px;">' +
              '<div style="display: flex; flex-direction: column; gap: 2px;">' +
                '<div style="display: flex; justify-content: space-between; font-size: 8px; opacity: 0.8; text-transform: uppercase;"><span>Note-Bound Intensity</span><span class="midg__intensity-val">100%</span></div>' +
                '<input type="range" class="midg__opt-intensity-bias" min="0" max="200" value="100" style="width: 100%; height: 4px; cursor: pointer; accent-color: #b79bff;">' +
              '</div>' +
              '<div style="display: flex; flex-direction: column; gap: 2px;">' +
                '<div style="display: flex; justify-content: space-between; font-size: 8px; opacity: 0.8; text-transform: uppercase;"><span>Complexity Rate</span><span class="midg__complexity-val">100%</span></div>' +
                '<input type="range" class="midg__opt-complexity-rate" min="50" max="200" value="100" style="width: 100%; height: 4px; cursor: pointer; accent-color: #b79bff;">' +
              '</div>' +
            '</div>' +
            '<canvas class="midg__sidebar-cc-canvas" width="216" height="85" style="display: none; width: 100%; height: 85px; border-radius: 4px; border: 1px solid rgba(255,255,255,0.15); background: rgba(0,0,0,0.4); margin-top: 4px; box-sizing: border-box;"></canvas>' +
          '</div>' +
        '</div>' +
        '<div style="font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; margin-top: 8px; margin-bottom: 4px; opacity: 0.8; border-top: 1px solid rgba(255,255,255,0.15); padding-top: 8px;">Note Display</div>' +
        '<div style="display: flex; flex-direction: column; gap: 6px; margin-bottom: 4px;">' +
          '<label style="display: flex; align-items: center; gap: 8px; font-size: 9px; cursor: pointer; margin: 0;" class="midg__opt-label">' +
            '<input type="checkbox" class="midg__opt-show-notes" style="cursor: pointer; width: 12px; height: 12px; accent-color: #b79bff; margin: 0;">' +
            '<span>Show MIDI Notes</span>' +
          '</label>' +
          '<label style="display: flex; align-items: center; gap: 8px; font-size: 9px; cursor: pointer; margin: 0;" class="midg__opt-label">' +
            '<input type="checkbox" class="midg__opt-jitter-notes" style="cursor: pointer; width: 12px; height: 12px; accent-color: #b79bff; margin: 0;">' +
            '<span>Jitter Notes (Wobble Effect)</span>' +
          '</label>' +
          '<label style="display: flex; align-items: center; gap: 8px; font-size: 9px; cursor: pointer; margin: 0;" class="midg__opt-label">' +
            '<input type="checkbox" class="midg__opt-playhead" checked style="cursor: pointer; width: 12px; height: 12px; accent-color: #b79bff; margin: 0;">' +
            '<span>Show Playhead</span>' +
          '</label>' +
          '<label style="display: flex; align-items: center; gap: 8px; font-size: 9px; cursor: pointer; margin: 0;" class="midg__opt-label">' +
            '<input type="checkbox" class="midg__opt-seamless" style="cursor: pointer; width: 12px; height: 12px; accent-color: #b79bff; margin: 0;">' +
            '<span>Seamless Loop (for recording)</span>' +
          '</label>' +
          '<label style="display: flex; align-items: center; gap: 8px; font-size: 9px; cursor: pointer; margin: 0;" class="midg__opt-label">' +
            '<input type="checkbox" class="midg__opt-note-edges" style="cursor: pointer; width: 12px; height: 12px; accent-color: #b79bff; margin: 0;">' +
            '<span>Sketchy Note Edges</span>' +
          '</label>' +
        '</div>' +
        '<div style="font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; margin-top: 8px; margin-bottom: 4px; opacity: 0.8; border-top: 1px solid rgba(255,255,255,0.15); padding-top: 8px;">Ink Sketch</div>' +
        '<div style="display: flex; flex-direction: column; gap: 6px; margin-bottom: 4px;">' +
          '<label style="display: flex; align-items: center; gap: 8px; font-size: 9px; cursor: pointer; margin: 0;" class="midg__opt-label">' +
            '<input type="checkbox" class="midg__opt-stave" checked style="cursor: pointer; width: 12px; height: 12px; accent-color: #b79bff; margin: 0;">' +
            '<span>Staff Lines, Clefs &amp; Bars</span>' +
          '</label>' +
        '</div>' +
        '<div style="font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; margin-top: 8px; margin-bottom: 4px; opacity: 0.8; border-top: 1px solid rgba(255,255,255,0.15); padding-top: 8px;">Audio</div>' +
        '<div style="display: flex; flex-direction: column; gap: 6px; margin-bottom: 4px;">' +
          '<label style="display: flex; align-items: center; gap: 8px; font-size: 9px; cursor: pointer; margin: 0;" class="midg__opt-label">' +
            '<input type="checkbox" class="midg__opt-apply-auto" style="cursor: pointer; width: 12px; height: 12px; accent-color: #b79bff; margin: 0;">' +
            '<span>Apply Automation to Voice</span>' +
          '</label>' +
        '</div>' +
        '<div style="font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; margin-top: 8px; margin-bottom: 4px; opacity: 0.8; border-top: 1px solid rgba(255,255,255,0.15); padding-top: 8px;">Background</div>' +
        '<div style="display: flex; flex-direction: column; gap: 6px; margin-bottom: 4px;">' +
          '<label style="display: flex; align-items: center; gap: 8px; font-size: 9px; cursor: pointer; margin: 0;" class="midg__opt-label">' +
            '<input type="checkbox" class="midg__opt-bg-media" style="cursor: pointer; width: 12px; height: 12px; accent-color: #b79bff; margin: 0;">' +
            '<span>Background Image / Video</span>' +
          '</label>' +
          '<div class="midg__bg-controls" style="display: none; flex-direction: column; gap: 4px; padding: 2px 0 4px 20px;">' +
            '<input type="file" class="midg__bg-file" accept="image/*,video/*" style="font-size: 8px; color: inherit; width: 100%;">' +
            '<div style="display: flex; gap: 4px;">' +
              '<input type="text" class="midg__bg-url" placeholder="or paste an image / video URL" style="flex: 1 1 auto; min-width: 0; font-size: 8px; padding: 3px 5px; border-radius: 4px; border: 1px solid rgba(255,255,255,0.2); background: rgba(255,255,255,0.06); color: inherit;">' +
              '<button type="button" class="midg__bg-apply" style="font-size: 8px; padding: 3px 8px; border-radius: 4px; border: none; background: #b79bff; color: #07090a; font-weight: 700; cursor: pointer;">Set</button>' +
            '</div>' +
            '<div style="display: flex; gap: 4px; align-items: center;">' +
              '<span style="font-size: 8px; opacity: 0.6; white-space: nowrap;">Start</span>' +
              '<input type="number" class="midg__bg-start" min="0" step="1" value="0" style="width: 52px; font-size: 8px; padding: 3px 5px; border-radius: 4px; border: 1px solid rgba(255,255,255,0.2); background: rgba(255,255,255,0.06); color: inherit;">' +
              '<span style="font-size: 8px; opacity: 0.6;">s</span>' +
              '<select class="midg__bg-playmode" style="flex: 1 1 auto; min-width: 0; background: #111; color: #fff; border: 1px solid rgba(255,255,255,0.3); font-size: 8px; padding: 2px 4px; border-radius: 4px; outline: none; cursor: pointer;">' +
                '<option value="always">Always run</option>' +
                '<option value="transport">Play with MIDI</option>' +
              '</select>' +
            '</div>' +
            '<button type="button" class="midg__bg-clear" style="font-size: 8px; padding: 3px 8px; border-radius: 4px; border: 1px solid rgba(255,255,255,0.2); background: transparent; color: inherit; cursor: pointer;">Clear</button>' +
          '</div>' +
        '</div>' +
        '<div style="font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; margin-top: 8px; margin-bottom: 4px; opacity: 0.8; border-top: 1px solid rgba(255,255,255,0.15); padding-top: 8px;">Overlay Options</div>' +
        '<div style="display: flex; flex-direction: column; gap: 6px; margin-bottom: 4px;">' +
          '<label style="display: flex; align-items: center; gap: 8px; font-size: 9px; cursor: pointer; margin: 0;" class="midg__opt-label">' +
            '<input type="checkbox" class="midg__opt-chord" checked style="cursor: pointer; width: 12px; height: 12px; accent-color: #b79bff; margin: 0;">' +
            '<span>Show Chord Names</span>' +
          '</label>' +
          '<div style="display: flex; align-items: center; justify-content: space-between; font-size: 9px; background: rgba(255,255,255,0.04); padding: 4px 8px; border-radius: 4px; border: 1px solid rgba(255,255,255,0.1); margin-top: 2px;" class="midg__opt-chord-style-row">' +
            '<span style="opacity: 0.7;">Chord Style</span>' +
            '<select class="midg__opt-chord-style" style="background: #111; color: #fff; border: 1px solid rgba(255,255,255,0.3); font-size: 9px; padding: 2px 4px; border-radius: 4px; outline: none; cursor: pointer;">' +
              '<option value="classic" style="background: #111; color: #fff;">Classic</option>' +
              '<option value="classicplain" style="background: #111; color: #fff;">Classic (No Box)</option>' +
              '<option value="realbook" style="background: #111; color: #fff;">Real Book</option>' +
            '</select>' +
          '</div>' +
          '<div style="display: flex; align-items: center; justify-content: space-between; font-size: 9px; background: rgba(255,255,255,0.04); padding: 4px 8px; border-radius: 4px; border: 1px solid rgba(255,255,255,0.1); margin-top: 2px;">' +
            '<span style="opacity: 0.7;">Chord Timing</span>' +
            '<select class="midg__opt-chord-mode" style="background: #111; color: #fff; border: 1px solid rgba(255,255,255,0.3); font-size: 9px; padding: 2px 4px; border-radius: 4px; outline: none; cursor: pointer;">' +
              '<option value="realtime" style="background: #111; color: #fff;">Real Time</option>' +
              '<option value="settled" style="background: #111; color: #fff;">Settled</option>' +
            '</select>' +
          '</div>' +
        '</div>' +
      '</div>' +
      '<div class="midg__story-frame" style="display: flex; flex-direction: column; position: relative; overflow: hidden;">' +
        '<div class="midg__sheet-header" style="position: absolute; top: 0; left: 0; width: 100%; height: 220px; pointer-events: none; z-index: 5; font-family: \'Garamond\', \'Baskerville\', \'Georgia\', serif; color: #1a1a1a; display: none; box-sizing: border-box; padding: 40px 40px 0 40px; font-variant-numeric: lining-nums; font-feature-settings: \'lnum\';">' +
          '<div class="midg__sheet-title" style="font-size: 32px; font-weight: 500; text-align: center; margin-bottom: 2px; letter-spacing: 0.5px;">Carolling of the Birds</div>' +
          '<div class="midg__sheet-subtitle" style="font-size: 11px; letter-spacing: 3px; text-transform: uppercase; text-align: center; margin-bottom: 24px; opacity: 0.85;">INVOCATION</div>' +
          '<div class="midg__sheet-instrument" style="position: absolute; left: 40px; top: 130px; font-size: 11px; font-weight: bold; letter-spacing: 1.5px; opacity: 0.85; text-transform: uppercase;">PIANO</div>' +
          '<div class="midg__sheet-tempo" style="position: absolute; left: 40px; top: 150px; font-size: 10px; font-weight: bold; font-style: italic; line-height: 1.2; text-align: left; opacity: 0.95; font-variant-numeric: lining-nums; font-feature-settings: \'lnum\';">Moderato</div>' +
          '<div class="midg__sheet-composer" style="position: absolute; right: 40px; top: 130px; font-size: 10px; font-weight: bold; text-align: right; text-transform: uppercase; letter-spacing: 1px; opacity: 0.9;">DEMIBBY SOUNDS</div>' +
          '<div class="midg__sheet-editor" style="position: absolute; right: 40px; top: 150px; font-size: 9px; font-style: italic; line-height: 1.3; text-align: right; opacity: 0.9;">Edited and arranged by<br>DEMIBBY SOUNDS</div>' +
        '</div>' +
        '<img class="midg__bg-img" alt="" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: cover; display: none; z-index: 0; pointer-events: none;">' +
        '<video class="midg__bg-video" muted playsinline loop autoplay preload="auto" style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; object-fit: cover; display: none; z-index: 0; pointer-events: none;"></video>' +
        '<iframe class="midg__bg-embed" allow="autoplay; encrypted-media" frameborder="0" style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 178vh; height: 100vh; min-width: 100%; min-height: 100%; border: 0; display: none; z-index: 0; pointer-events: none;"></iframe>' +
        '<canvas class="midg__story-canvas" style="flex: 1 1 auto; height: 100%; display: block; position: relative;"></canvas>' +
        '<canvas class="midg__story-cc-canvas" style="display: none; flex: 0 0 135px; height: 135px; box-sizing: border-box;"></canvas>' +
        '<div class="midg__story-info">' +
          '<div class="midg__story-name"></div>' +
          '<div class="midg__story-sub" style="display: none;"></div>' +
          '<div class="midg__story-instr" style="font-size: 10px; text-transform: uppercase; letter-spacing: 2px; margin-top: 4px; color: rgba(255,255,255,0.65);"></div>' +
        '</div>' +
      '</div>';
    document.body.appendChild(storyEl);
    storyCanvasEl = storyEl.querySelector('.midg__story-canvas');
    storyCcCanvasEl = storyEl.querySelector('.midg__story-cc-canvas');
    sidebarCcCanvasEl = storyEl.querySelector('.midg__sidebar-cc-canvas');
    storyCanvasEl.width = 1080;
    storyCanvasEl.height = 1920;
    storyNameEl = storyEl.querySelector('.midg__story-name');
    storyFrameEl = storyEl.querySelector('.midg__story-frame');
    storyEl.querySelector('.midg__story-close').addEventListener('click', closeStory);

    var inTitle = storyEl.querySelector('.midg__input-title');
    var inSubtitle = storyEl.querySelector('.midg__input-subtitle');
    var inComposer = storyEl.querySelector('.midg__input-composer');
    var inEditor = storyEl.querySelector('.midg__input-editor');
    var inInstrument = storyEl.querySelector('.midg__input-instrument');
    var inTempo = storyEl.querySelector('.midg__input-tempo');

    updateSheetHeaderFromInputs = function () {
      var sHeader = storyEl.querySelector('.midg__sheet-header');
      if (!sHeader) { return; }

      var titleEl = sHeader.querySelector('.midg__sheet-title');
      var subtitleEl = sHeader.querySelector('.midg__sheet-subtitle');
      var composerEl = sHeader.querySelector('.midg__sheet-composer');
      var editorEl = sHeader.querySelector('.midg__sheet-editor');
      var instrEl = sHeader.querySelector('.midg__sheet-instrument');
      var tempoEl = sHeader.querySelector('.midg__sheet-tempo');

      if (titleEl) {
        titleEl.textContent = inTitle.value;
      }
      if (subtitleEl) {
        subtitleEl.textContent = inSubtitle.value;
        subtitleEl.style.display = inSubtitle.value ? 'block' : 'none';
      }
      if (composerEl) {
        composerEl.textContent = inComposer.value;
        composerEl.style.display = inComposer.value ? 'block' : 'none';
      }
      if (editorEl) {
        editorEl.innerHTML = inEditor.value.replace(/\n/g, '<br>');
        editorEl.style.display = inEditor.value ? 'block' : 'none';
      }
      if (instrEl) {
        instrEl.textContent = inInstrument.value.toUpperCase();
        instrEl.style.display = inInstrument.value ? 'block' : 'none';
      }
      if (tempoEl) {
        var bpmVal = currentBPM || (current ? (current._bpm || 120) : 120);
        var tempoText = inTempo.value;
        if (tempoText) {
          tempoEl.innerHTML = tempoText + '<br>&#9813; = ' + bpmVal;
        } else {
          tempoEl.innerHTML = 'Moderato<br>&#9813; = ' + bpmVal;
        }
      }
    };

    [inTitle, inSubtitle, inComposer, inEditor, inInstrument, inTempo].forEach(function (inp) {
      if (inp) {
        inp.addEventListener('input', function () {
          updateSheetHeaderFromInputs();

          var cleanComp = inComposer.value.trim().replace(/ddmibby/gi, 'DEMIBBY').replace(/d\s*d\s*m\s*i\s*b\s*b\s*y/gi, 'DEMIBBY');
          var cleanTitle = inTitle.value.trim();
          var cleanSub = inSubtitle.value.trim().toUpperCase();
          var currentBpm = current ? (current._bpm || 120) : 120;

          var formattedKey = cleanSub ? ' ' + cleanSub : '';
          var newRawName = cleanComp + ' - ' + cleanTitle + ' ' + currentBpm + ' BPM' + formattedKey + '.mid';

          if (current) {
            current._name = newRawName;
            var cardNameSpan = current.querySelector('.wf-card__name');
            if (cardNameSpan) {
              cardNameSpan.textContent = newRawName;
            }
          }

          if (storyNameEl) {
            if (currentFormat === 'ink') {
              storyNameEl.textContent = cleanTitle;
            } else {
              storyNameEl.textContent = newRawName;
            }
          }

          var instrEl = storyEl.querySelector('.midg__story-instr');
          if (instrEl && inInstrument) {
            instrEl.textContent = inInstrument.value.trim().toUpperCase();
          }
        });
      }
    });

    widgetRefs.push(storyEl);

    storyEl.querySelectorAll('.midg__voice').forEach(function (btn) {
      if (btn.classList.contains('midg__voice--eerie')) {
        btn.addEventListener('click', function (e) {
          e.stopPropagation();
          var sub = storyEl.querySelector('.midg__sub');
          if (sub) {
            sub.style.display = sub.style.display === 'none' ? 'flex' : 'none';
          }
        });
      } else {
        btn.addEventListener('click', function (e) {
          e.stopPropagation();
          selectVoice(storyEl, btn);
        });
      }
    });

    storyEl.querySelectorAll('.midg__sub-voice').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        selectVoice(storyEl, btn);
      });
    });

    storyEl.querySelectorAll('[data-step]').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var step = btn.getAttribute('data-step');
        var dir = parseInt(btn.getAttribute('data-dir'), 10);
        if (step === 'pitch') {
          stepPitch(dir);
        } else if (step === 'detune') {
          stepDetune(dir);
        } else if (step === 'tempo') {
          stepTempo(dir);
        }
      });
    });

    var noteChk = storyEl.querySelector('.midg__opt-note-curves');
    var laneChk = storyEl.querySelector('.midg__opt-lane-curves');
    var flightChk = storyEl.querySelector('.midg__opt-flight-path');
    var applyAutoChk = storyEl.querySelector('.midg__opt-apply-auto');
    var chordChk = storyEl.querySelector('.midg__opt-chord');

    if (noteChk) {
      noteChk.checked = showNoteCurves;
      noteChk.addEventListener('change', function () {
        showNoteCurves = noteChk.checked;
        renderStoryCanvases();
      });
    }

    if (laneChk) {
      laneChk.checked = showLaneCurves;
      laneChk.addEventListener('change', function () {
        showLaneCurves = laneChk.checked;
        renderStoryCanvases();
      });
    }

    // The parameter panel eats most of a phone screen, so it slides out of the
    // way. Transform rather than display, so nothing fights the format/theme
    // handlers that also set display on this panel.
    makeSectionsCollapsible();
    var panelToggle = storyEl.querySelector('.midg__panel-toggle');
    var metaPanelEl = storyEl.querySelector('.midg__meta-panel');
    if (panelToggle && metaPanelEl) {
      var panelOpen = true;
      var syncPanel = function () {
        metaPanelEl.style.transition = 'transform 0.25s ease, opacity 0.25s ease';
        metaPanelEl.style.transform = panelOpen ? 'translateX(0)' : 'translateX(-130%)';
        metaPanelEl.style.opacity = panelOpen ? '1' : '0';
        metaPanelEl.style.pointerEvents = panelOpen ? 'auto' : 'none';
        panelToggle.innerHTML = panelOpen ? '&#8249;' : '&#8250;';
        panelToggle.style.left = panelOpen ? '292px' : '12px';
      };
      syncPanel();
      panelToggle.addEventListener('click', function (e) {
        e.stopPropagation();
        panelOpen = !panelOpen;
        syncPanel();
      });
    }

    var staveChk = storyEl.querySelector('.midg__opt-stave');
    if (staveChk) {
      staveChk.checked = showStaveLines;
      staveChk.addEventListener('change', function () {
        showStaveLines = staveChk.checked;
        renderStoryCanvases();
      });
    }
    var seamlessChk = storyEl.querySelector('.midg__opt-seamless');
    if (seamlessChk) {
      seamlessChk.checked = seamlessLoop;
      seamlessChk.addEventListener('change', function () {
        seamlessLoop = seamlessChk.checked;
        renderStoryCanvases();
      });
    }
    var playheadChk = storyEl.querySelector('.midg__opt-playhead');
    if (playheadChk) {
      playheadChk.checked = showPlayhead;
      playheadChk.addEventListener('change', function () {
        showPlayhead = playheadChk.checked;
        renderStoryCanvases();
      });
    }
    var edgesChk = storyEl.querySelector('.midg__opt-note-edges');
    if (edgesChk) {
      edgesChk.checked = showNoteEdges;
      edgesChk.addEventListener('change', function () {
        showNoteEdges = edgesChk.checked;
        renderStoryCanvases();
      });
    }

    var bgChk = storyEl.querySelector('.midg__opt-bg-media');
    var bgControls = storyEl.querySelector('.midg__bg-controls');
    var bgFileEl = storyEl.querySelector('.midg__bg-file');
    var bgUrlEl = storyEl.querySelector('.midg__bg-url');
    var bgApplyEl = storyEl.querySelector('.midg__bg-apply');
    var bgClearEl = storyEl.querySelector('.midg__bg-clear');

    if (bgChk) {
      bgChk.checked = bgMediaOn;
      if (bgControls) { bgControls.style.display = bgMediaOn ? 'flex' : 'none'; }
      bgChk.addEventListener('change', function () {
        bgMediaOn = bgChk.checked;
        if (bgControls) { bgControls.style.display = bgMediaOn ? 'flex' : 'none'; }
        applyBgMedia();
      });
    }
    if (bgFileEl) {
      bgFileEl.addEventListener('change', function () {
        var f = bgFileEl.files && bgFileEl.files[0];
        if (!f) { return; }
        if (bgObjectUrl) { try { URL.revokeObjectURL(bgObjectUrl); } catch (e) { /* ignore */ } }
        bgObjectUrl = URL.createObjectURL(f);
        bgMediaUrl = bgObjectUrl;
        bgMediaType = (f.type && f.type.indexOf('video') === 0) ? 'video' : 'image';
        bgMediaOn = true;
        if (bgChk) { bgChk.checked = true; }
        if (bgControls) { bgControls.style.display = 'flex'; }
        applyBgMedia();
      });
    }
    if (bgApplyEl && bgUrlEl) {
      bgApplyEl.addEventListener('click', function () {
        var u = (bgUrlEl.value || '').trim();
        if (!u) { return; }
        bgMediaUrl = u;
        bgMediaType = bgEmbedUrl(u, 0) ? 'embed'
          : (/[.](mp4|webm|mov|m4v)([?#]|$)/i.test(u) ? 'video' : 'image');
        bgMediaOn = true;
        if (bgChk) { bgChk.checked = true; }
        applyBgMedia();
      });
    }
    var bgStartEl = storyEl.querySelector('.midg__bg-start');
    if (bgStartEl) {
      bgStartEl.value = bgStartSec;
      bgStartEl.addEventListener('change', function () {
        bgStartSec = Math.max(0, parseFloat(bgStartEl.value) || 0);
        applyBgMedia();
      });
    }
    var bgModeEl = storyEl.querySelector('.midg__bg-playmode');
    if (bgModeEl) {
      bgModeEl.value = bgPlayMode;
      bgModeEl.addEventListener('change', function () {
        bgPlayMode = bgModeEl.value;
        syncBgTransport();
      });
    }
    if (bgClearEl) {
      bgClearEl.addEventListener('click', function () {
        if (bgObjectUrl) { try { URL.revokeObjectURL(bgObjectUrl); } catch (e) { /* ignore */ } }
        bgObjectUrl = '';
        bgMediaUrl = '';
        bgMediaType = '';
        if (bgUrlEl) { bgUrlEl.value = ''; }
        if (bgFileEl) { bgFileEl.value = ''; }
        applyBgMedia();
      });
    }

    if (flightChk) {
      flightChk.checked = showFlightPath;
      flightChk.addEventListener('change', function () {
        showFlightPath = flightChk.checked;
        renderStoryCanvases();
      });
    }

    if (applyAutoChk) {
      applyAutoChk.checked = applyAutomationToVoice;
      applyAutoChk.addEventListener('change', function () {
        applyAutomationToVoice = applyAutoChk.checked;
        renderStoryCanvases();
      });
    }

    var showNotesChk = storyEl.querySelector('.midg__opt-show-notes');
    if (showNotesChk) {
      showNotesChk.checked = showMidiNotes;
      showNotesChk.addEventListener('change', function () {
        showMidiNotes = showNotesChk.checked;
        renderStoryCanvases();
      });
    }

    var jitterChk = storyEl.querySelector('.midg__opt-jitter-notes');
    if (jitterChk) {
      jitterChk.checked = jitterNotes;
      jitterChk.addEventListener('change', function () {
        jitterNotes = jitterChk.checked;
        renderStoryCanvases();
      });
    }

    if (chordChk) {
      chordChk.checked = showChordTranscriber;
      chordChk.addEventListener('change', function () {
        showChordTranscriber = chordChk.checked;
        document.querySelectorAll('.mid-card__chord-toggle').forEach(function (b) {
          b.textContent = showChordTranscriber ? 'Chord: ON' : 'Chord: OFF';
        });
        renderStoryCanvases();
      });
    }

    var chordModeSelect = storyEl.querySelector('.midg__opt-chord-mode');
    if (chordModeSelect) {
      chordModeSelect.value = chordMode;
      chordModeSelect.addEventListener('change', function () {
        chordMode = chordModeSelect.value;
        renderStoryCanvases();
      });
    }
    var chordStyleSelect = storyEl.querySelector('.midg__opt-chord-style');
    if (chordStyleSelect) {
      chordStyleSelect.value = currentChordStyle;
      chordStyleSelect.addEventListener('change', function () {
        currentChordStyle = chordStyleSelect.value;
        renderStoryCanvases();
      });
    }

    var visLayoutSelect = storyEl.querySelector('.midg__opt-vis-layout');
    if (visLayoutSelect) {
      visLayoutSelect.value = visualizerLayout;
      visLayoutSelect.addEventListener('change', function () {
        visualizerLayout = visLayoutSelect.value;
        renderStoryCanvases();
      });
    }

    var intensitySlider = storyEl.querySelector('.midg__opt-intensity-bias');
    if (intensitySlider) {
      intensitySlider.value = Math.round(intensityBias * 100);
      intensitySlider.addEventListener('input', function () {
        intensityBias = parseFloat(intensitySlider.value) / 100;
        var displayEl = storyEl.querySelector('.midg__intensity-val');
        if (displayEl) displayEl.textContent = intensitySlider.value + '%';
        renderStoryCanvases();
      });
    }

    var complexitySlider = storyEl.querySelector('.midg__opt-complexity-rate');
    if (complexitySlider) {
      complexitySlider.value = Math.round(complexityRate * 100);
      complexitySlider.addEventListener('input', function () {
        complexityRate = parseFloat(complexitySlider.value) / 100;
        var displayEl = storyEl.querySelector('.midg__complexity-val');
        if (displayEl) displayEl.textContent = complexitySlider.value + '%';
        renderStoryCanvases();
      });
    }

    storyEl.querySelectorAll('.midg__theme-btn').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var themeName = btn.getAttribute('data-theme');
        currentThemeName = themeName;
        syncThemeButtons();

        var theme = THEMES[themeName];
        var isInk = (currentFormat === 'ink');
        var isLight = isInk || (themeName === 'sketchbook' || themeName === 'watercolor');
        storyEl.style.backgroundColor = isInk ? 'rgba(245,243,238,0.98)' : theme.backdrop;
        if (storyFrameEl) {
          if (isInk) {
            storyFrameEl.style.backgroundColor = '#faf7f2';
            storyFrameEl.style.borderColor = 'rgba(26,26,26,0.1)';
          } else {
            // No theme carries the literal #faf7f2, so this comparison never matched
            // and every theme got the near-black frame. The two paper themes then put
            // dark text and dark notes on it, which is why they were unreadable.
            storyFrameEl.style.backgroundColor = theme.backdrop || '#07090a';
            storyFrameEl.style.borderColor = isLight ? 'rgba(0,0,0,0.1)' : '#28371d';
          }
        }
        var sheetHeader = storyEl.querySelector('.midg__sheet-header');
        if (sheetHeader) {
          sheetHeader.style.display = isInk ? 'block' : 'none';
        }
        var infoEl = storyEl.querySelector('.midg__story-info');
        if (infoEl) {
          infoEl.style.display = isInk ? 'none' : 'block';
        }
        var metaPanel = storyEl.querySelector('.midg__meta-panel');
        if (metaPanel) {
          metaPanel.style.display = 'flex';
          var sheetSec = metaPanel.querySelector('.midg__meta-sheet-section');
          if (sheetSec) {
            sheetSec.style.display = 'flex';
          }

          metaPanel.querySelectorAll('.midg__voice, .midg__sub-voice').forEach(function (vbtn) {
            var isBtnActive = vbtn.classList.contains('on');
            if (isLight) {
              vbtn.style.color = isBtnActive ? '#fff' : '#1a1a1a';
              vbtn.style.backgroundColor = isBtnActive ? '#1a1a1a' : 'rgba(0,0,0,0.05)';
              vbtn.style.borderColor = isBtnActive ? '#1a1a1a' : 'rgba(0,0,0,0.12)';
            } else {
              vbtn.style.color = isBtnActive ? '#07090a' : '#fff';
              vbtn.style.backgroundColor = isBtnActive ? '#fff' : 'rgba(255,255,255,0.05)';
              vbtn.style.borderColor = isBtnActive ? '#fff' : 'rgba(255,255,255,0.15)';
            }
          });
          metaPanel.querySelectorAll('.midg__step').forEach(function (sbtn) {
            sbtn.style.color = isLight ? '#1a1a1a' : '#fff';
          });

          if (isLight) {
            metaPanel.style.background = 'rgba(244,241,234,0.85)';
            metaPanel.style.borderColor = 'rgba(0,0,0,0.15)';
            metaPanel.style.color = '#1a1a1a';
            metaPanel.querySelectorAll('label').forEach(function (l) { l.style.color = '#1a1a1a'; });
            metaPanel.querySelectorAll('input, select').forEach(function (inp) {
              inp.style.background = 'rgba(0,0,0,0.04)';
              inp.style.borderColor = 'rgba(0,0,0,0.12)';
              inp.style.color = '#1a1a1a';
            });
          } else {
            metaPanel.style.background = 'rgba(7,9,10,0.8)';
            metaPanel.style.borderColor = 'rgba(255,255,255,0.15)';
            metaPanel.style.color = '#fff';
            metaPanel.querySelectorAll('label').forEach(function (l) { l.style.color = '#fff'; });
            metaPanel.querySelectorAll('input, select').forEach(function (inp) {
              inp.style.background = 'rgba(255,255,255,0.08)';
              inp.style.borderColor = 'rgba(255,255,255,0.15)';
              inp.style.color = '#fff';
            });
          }
        }

        var chordToggleBtn = storyEl.querySelector('.midg__chord-toggle');
        if (chordToggleBtn) {
          if (isLight) {
            chordToggleBtn.style.color = '#1a1a1a';
            chordToggleBtn.style.backgroundColor = 'rgba(0,0,0,0.05)';
            chordToggleBtn.style.borderColor = 'rgba(0,0,0,0.15)';
          } else {
            chordToggleBtn.style.color = '#fff';
            chordToggleBtn.style.backgroundColor = 'rgba(255,255,255,0.05)';
            chordToggleBtn.style.borderColor = 'rgba(255,255,255,0.15)';
          }
        }

        var infoEl2 = storyEl.querySelector('.midg__story-info');
        var closeBtn2 = storyEl.querySelector('.midg__story-close');
        var pickerLabel2 = storyEl.querySelector('.midg__theme-picker span');
        var formatLabel2 = storyEl.querySelector('.midg__format-picker span');

        storyEl.querySelectorAll('.midg__format-btn').forEach(function (b) {
          var isBtnActive = b.classList.contains('on');
          if (isLight) {
            b.style.color = isBtnActive ? '#f2f0f7' : '#1a1a1a';
            b.style.backgroundColor = isBtnActive ? '#1a1a1a' : 'rgba(0,0,0,0.05)';
            b.style.borderColor = isBtnActive ? '#1a1a1a' : 'transparent';
          } else {
            b.style.color = isBtnActive ? '#07090a' : 'rgba(255,255,255,0.6)';
            b.style.backgroundColor = isBtnActive ? '#fff' : 'rgba(255,255,255,0.05)';
            b.style.borderColor = isBtnActive ? '#fff' : 'transparent';
          }
        });

        if (infoEl2) {
          var nameEl2 = infoEl2.querySelector('.midg__story-name');
          var subEl2 = infoEl2.querySelector('.midg__story-sub');
          if (nameEl2) {
            nameEl2.style.color = isLight ? '#1a1a1a' : '#f2f0f7';
            nameEl2.style.fontFamily = isInk ? '"Garamond", "Baskerville", "Georgia", serif' : '';
            nameEl2.style.fontStyle = isInk ? 'normal' : '';
            nameEl2.style.letterSpacing = isInk ? '1.5px' : '';
            nameEl2.style.fontSize = isInk ? '24px' : '';
            nameEl2.style.textTransform = isInk ? 'none' : '';
            fitStoryName();
          }
          if (subEl2) {
            subEl2.style.color = isLight ? '#7b68ee' : '#b79bff';
            subEl2.style.fontFamily = isInk ? '"Garamond", "Baskerville", "Georgia", serif' : '';
            subEl2.style.fontStyle = isInk ? 'normal' : '';
            subEl2.style.letterSpacing = isInk ? '3.5px' : '';
            subEl2.style.fontSize = isInk ? '10px' : '';
            subEl2.style.textTransform = isInk ? 'uppercase' : '';
          }
          var instrEl2 = infoEl2.querySelector('.midg__story-instr');
          if (instrEl2) {
            instrEl2.style.color = isLight ? 'rgba(26,26,26,0.65)' : 'rgba(242,240,247,0.65)';
            instrEl2.style.fontFamily = isInk ? '"Garamond", "Baskerville", "Georgia", serif' : '';
            instrEl2.style.fontSize = isInk ? '10px' : '9px';
          }
        }
        if (closeBtn2) {
          closeBtn2.style.color = isLight ? '#1a1a1a' : '#e6e3f0';
          closeBtn2.style.backgroundColor = isLight ? '#f4f1ea' : '#11170c';
          closeBtn2.style.borderColor = isLight ? 'rgba(0,0,0,0.15)' : '#28371d';
        }
        if (pickerLabel2) { pickerLabel2.style.color = isLight ? '#1a1a1a' : '#fff'; }
        if (formatLabel2) { formatLabel2.style.color = isLight ? '#1a1a1a' : '#fff'; }

        if (current) {
          updateStoryMidiControls(current);
          renderStoryCanvases();
        }
      });
    });

    storyEl.querySelectorAll('.midg__format-btn').forEach(function (btn) {
      btn.addEventListener('click', function (e) {
        e.stopPropagation();
        var formatName = btn.getAttribute('data-format');
        currentFormat = formatName;
        if (formatName === 'ink') {
          currentThemeName = 'sketchbook';
        }
        syncThemeButtons();
        updateVoiceUI();

        storyEl.querySelectorAll('.midg__format-btn').forEach(function (b) {
          if (b === btn) {
            b.classList.add('on');
          } else {
            b.classList.remove('on');
          }
        });

        var themeName = currentThemeName;
        var theme = THEMES[themeName];
        var isInk = (currentFormat === 'ink');
        var isLight = isInk || (themeName === 'sketchbook' || themeName === 'watercolor');
        storyEl.style.backgroundColor = isInk ? 'rgba(245,243,238,0.98)' : theme.backdrop;
        if (storyFrameEl) {
          if (isInk) {
            storyFrameEl.style.backgroundColor = '#faf7f2';
            storyFrameEl.style.borderColor = 'rgba(26,26,26,0.1)';
          } else {
            storyFrameEl.style.backgroundColor = theme.backdrop || '#07090a';
            storyFrameEl.style.borderColor = isLight ? 'rgba(0,0,0,0.1)' : '#28371d';
          }
        }
        var sheetHeader = storyEl.querySelector('.midg__sheet-header');
        if (sheetHeader) {
          sheetHeader.style.display = isInk ? 'block' : 'none';
        }
        var infoEl = storyEl.querySelector('.midg__story-info');
        if (infoEl) {
          infoEl.style.display = isInk ? 'none' : 'block';
        }
        var metaPanel = storyEl.querySelector('.midg__meta-panel');
        if (metaPanel) {
          metaPanel.style.display = 'flex';
          var sheetSec = metaPanel.querySelector('.midg__meta-sheet-section');
          if (sheetSec) {
            sheetSec.style.display = 'flex';
          }
          if (isLight) {
            metaPanel.style.background = 'rgba(244,241,234,0.85)';
            metaPanel.style.borderColor = 'rgba(0,0,0,0.15)';
            metaPanel.style.color = '#1a1a1a';
            metaPanel.querySelectorAll('label').forEach(function (l) { l.style.color = '#1a1a1a'; });
            metaPanel.querySelectorAll('input[type="text"], select').forEach(function (inp) {
              inp.style.background = 'rgba(0,0,0,0.04)';
              inp.style.borderColor = 'rgba(0,0,0,0.12)';
              inp.style.color = '#1a1a1a';
            });
          } else {
            metaPanel.style.background = 'rgba(7,9,10,0.8)';
            metaPanel.style.borderColor = 'rgba(255,255,255,0.15)';
            metaPanel.style.color = '#fff';
            metaPanel.querySelectorAll('label').forEach(function (l) { l.style.color = '#fff'; });
            metaPanel.querySelectorAll('input[type="text"], select').forEach(function (inp) {
              inp.style.background = 'rgba(255,255,255,0.08)';
              inp.style.borderColor = 'rgba(255,255,255,0.15)';
              inp.style.color = '#fff';
            });
          }
        }

        var chordBtn = storyEl.querySelector('.midg__chord-toggle');
        if (chordBtn) {
          if (isLight) {
            chordBtn.style.color = '#1a1a1a';
            chordBtn.style.backgroundColor = 'rgba(0,0,0,0.05)';
            chordBtn.style.borderColor = 'rgba(0,0,0,0.15)';
          } else {
            chordBtn.style.color = '#fff';
            chordBtn.style.backgroundColor = 'rgba(255,255,255,0.05)';
            chordBtn.style.borderColor = 'rgba(255,255,255,0.15)';
          }
        }

        var curvesBtn = storyEl.querySelector('.midg__curves-toggle');
        if (curvesBtn) {
          if (isLight) {
            curvesBtn.style.color = '#1a1a1a';
            curvesBtn.style.backgroundColor = 'rgba(0,0,0,0.05)';
            curvesBtn.style.borderColor = 'rgba(0,0,0,0.15)';
          } else {
            curvesBtn.style.color = '#fff';
            curvesBtn.style.backgroundColor = 'rgba(255,255,255,0.05)';
            curvesBtn.style.borderColor = 'rgba(255,255,255,0.15)';
          }
        }

        var pickerLabel = storyEl.querySelector('.midg__theme-picker span');
        var formatLabel = storyEl.querySelector('.midg__format-picker span');
        var closeBtn = storyEl.querySelector('.midg__story-close');
        var infoEl2b = storyEl.querySelector('.midg__story-info');

        if (infoEl2b) {
          var nameEl = infoEl2b.querySelector('.midg__story-name');
          var subEl = infoEl2b.querySelector('.midg__story-sub');
          if (nameEl) {
            nameEl.style.color = isLight ? '#1a1a1a' : '#f2f0f7';
            nameEl.style.fontFamily = isInk ? '"Garamond", "Baskerville", "Georgia", serif' : '';
            nameEl.style.fontStyle = isInk ? 'normal' : '';
            nameEl.style.letterSpacing = isInk ? '1.5px' : '';
            nameEl.style.fontSize = isInk ? '24px' : '';
            nameEl.style.textTransform = isInk ? 'none' : '';
            fitStoryName();
          }
          if (subEl) {
            subEl.style.color = isLight ? '#7b68ee' : '#b79bff';
            subEl.style.fontFamily = isInk ? '"Garamond", "Baskerville", "Georgia", serif' : '';
            subEl.style.fontStyle = isInk ? 'normal' : '';
            subEl.style.letterSpacing = isInk ? '3.5px' : '';
            subEl.style.fontSize = isInk ? '10px' : '';
            subEl.style.textTransform = isInk ? 'uppercase' : '';
          }
          var instrEl = infoEl2b.querySelector('.midg__story-instr');
          if (instrEl) {
            instrEl.style.color = isLight ? 'rgba(26,26,26,0.65)' : 'rgba(242,240,247,0.65)';
            instrEl.style.fontFamily = isInk ? '"Garamond", "Baskerville", "Georgia", serif' : '';
            instrEl.style.fontSize = isInk ? '10px' : '9px';
          }
        }

        if (closeBtn) {
          closeBtn.style.color = isLight ? '#1a1a1a' : '#e6e3f0';
          closeBtn.style.backgroundColor = isLight ? '#f4f1ea' : '#11170c';
          closeBtn.style.borderColor = isLight ? 'rgba(0,0,0,0.15)' : '#28371d';
        }
        if (pickerLabel) { pickerLabel.style.color = isLight ? '#1a1a1a' : '#fff'; }
        if (formatLabel) { formatLabel.style.color = isLight ? '#1a1a1a' : '#fff'; }

        storyEl.querySelectorAll('.midg__format-btn').forEach(function (b) {
          var isBtnActive = b.classList.contains('on');
          if (isLight) {
            b.style.color = isBtnActive ? '#f2f0f7' : '#1a1a1a';
            b.style.backgroundColor = isBtnActive ? '#1a1a1a' : 'rgba(0,0,0,0.05)';
            b.style.borderColor = isBtnActive ? '#1a1a1a' : 'transparent';
          } else {
            b.style.color = isBtnActive ? '#07090a' : 'rgba(255,255,255,0.6)';
            b.style.backgroundColor = isBtnActive ? '#fff' : 'rgba(255,255,255,0.05)';
            b.style.borderColor = isBtnActive ? '#fff' : 'transparent';
          }
        });

        storyEl.querySelectorAll('.midg__theme-btn').forEach(function (b) {
          var t = b.getAttribute('data-theme');
          b.style.borderColor = t === currentThemeName ? '#fff' : 'transparent';
          b.style.boxShadow = t === currentThemeName ? '0 4px 10px rgba(0,0,0,0.3)' : 'none';
        });

        if (current) {
          updateStoryMidiControls(current);
          renderStoryCanvases();
        }
      });
    });
    var frame = storyEl.querySelector('.midg__story-frame');
    var isDragging = false;
    var startX = 0;
    var startFrac = 0;
    var hasDragged = false;
    var wasPlayingBeforeDrag = false;

    function getX(e) {
      if (e.touches && e.touches[0]) { return e.touches[0].clientX; }
      return e.clientX;
    }

    frame.addEventListener('mousedown', onDragStart);
    frame.addEventListener('touchstart', onDragStart, { passive: true });

    function onDragStart(e) {
      if (e.target.closest('.midg__story-close')) { return; }
      if (!current) { return; }
      isDragging = true;
      hasDragged = false;
      startX = getX(e);
      var curSec = isPlaying ? elapsedNow() : pausedAt;
      startFrac = curSec / (current._dur || 1);
      wasPlayingBeforeDrag = isPlaying;

      if (isPlaying) {
        pauseCurrent();
      }
    }

    window.addEventListener('mousemove', onDragMove);
    window.addEventListener('touchmove', onDragMove, { passive: false });

    function onDragMove(e) {
      if (!isDragging || !current) { return; }
      var curX = getX(e);
      var diffX = curX - startX;
      if (Math.abs(diffX) > 6) {
        hasDragged = true;
      }
      if (hasDragged) {
        e.preventDefault();
        var rect = frame.getBoundingClientRect();
        var deltaFrac = -diffX / (rect.width * 5.0);
        var targetFrac = Math.max(0, Math.min(0.99, startFrac + deltaFrac));

        pausedAt = targetFrac * (current._dur || 1);
        if (typeof syncBgTransport === 'function') { syncBgTransport(); }
        if (storyOpen) {
          renderStoryCanvases();
        }

        scheduleFrom(current, pausedAt);
        if (isPlaying) {
          pauseCurrent();
        }
      }
    }

    window.addEventListener('mouseup', onDragEnd);
    window.addEventListener('touchend', onDragEnd);

    function onDragEnd(e) {
      if (!isDragging) { return; }
      isDragging = false;
      if (!hasDragged) {
        if (wasPlayingBeforeDrag) {
          // Keep it paused (toggled!)
        } else {
          if (current) {
            current.classList.add('is-playing');
            isPlaying = true;
            scheduleFrom(current, pausedAt);
          }
        }
      } else {
        if (wasPlayingBeforeDrag && current) {
          current.classList.add('is-playing');
          isPlaying = true;
          scheduleFrom(current, pausedAt);
        }
      }
    }

    storyEl.addEventListener('click', function (e) {
      if (e.target === storyEl) {
        closeStory();
      }
    });

    // Style all checkboxes inside meta panel to ensure native rendering on initial build
    storyEl.querySelectorAll('.midg__meta-panel input[type="checkbox"]').forEach(function (chk) {
      chk.style.appearance = 'checkbox';
      chk.style.webkitAppearance = 'checkbox';
      chk.style.mozAppearance = 'checkbox';
      chk.style.accentColor = '#b79bff';
      chk.style.cursor = 'pointer';
      chk.style.display = 'inline-block';
    });
  }

  function getTempoName(bpm) {
    if (bpm >= 200) return 'Prestissimo';
    if (bpm >= 168) return 'Presto';
    if (bpm >= 120) return 'Allegro';
    if (bpm >= 108) return 'Allegretto';
    if (bpm >= 90)  return 'Moderato';
    if (bpm >= 76)  return 'Andante';
    if (bpm >= 66)  return 'Adagio';
    if (bpm >= 40)  return 'Largo';
    return 'Grave';
  }

  function parseCardName(name) {
    // Normalize branding typos immediately to DEMIBBY
    name = name.replace(/ddmibby/gi, 'DEMIBBY').replace(/d\s*d\s*m\s*i\s*b\s*b\s*y/gi, 'DEMIBBY');
    
    var clean = name.replace(/\.mid$/i, '').trim();

    var bpm = '';
    var bpmMatch = clean.match(/\b(\d+)\s*BPM\b/i);
    if (bpmMatch) {
      bpm = bpmMatch[1];
      clean = clean.replace(bpmMatch[0], '').trim();
    } else {
      var trailingNumMatch = clean.match(/\b(\d{2,3})$/);
      if (trailingNumMatch) {
        var val = parseInt(trailingNumMatch[1], 10);
        if (val >= 40 && val <= 250) {
          bpm = trailingNumMatch[1];
          clean = clean.replace(trailingNumMatch[0], '').trim();
        }
      }
    }

    var key = '';
    // Modes are listed before the shorter min/maj/m so they win the alternation.
    // Anchored to the end of the name: the convention is
    // 'creator - title - BPM - key'. A loose match lets a title such as
    // 'Am I Dreaming' get eaten as the key A minor.
    var MODE_ALT = 'mixolydian|phrygian|aeolian|locrian|lydian|dorian|ionian|minor|major|mix|phr|aeo|loc|lyd|dor|ion|min|maj|m';
    var keyMatch = clean.match(new RegExp('\\b([A-G][#b]?)\\s*(' + MODE_ALT + ')\\s*-?\\s*$', 'i'));
    if (!keyMatch) {
      keyMatch = clean.match(/\b([A-G][#b]?)\s*-?\s*$/);
    }
    if (keyMatch) {
      key = keyMatch[0];
      clean = clean.replace(keyMatch[0], '').trim();
    }

    clean = clean.replace(/\s*-\s*-\s*/g, ' - ')
                 .replace(/\s*-\s*$/, '')
                 .replace(/^\s*-\s*/, '')
                 .replace(/\s{2,}/g, ' ')
                 .trim();

    var parts = clean.split(/\s*-\s*/);
    var producer = 'DEMIBBY SOUNDS';
    var title = '';

    if (parts.length >= 2) {
      var firstPartLower = parts[0].toLowerCase();
      if (firstPartLower.includes('demibby') || firstPartLower.includes('sounds') || firstPartLower.includes('prod')) {
        // Rendered as written rather than shouted: Demibby, not DEMIBBY.
        producer = parts[0].trim().replace(/\S+/g, function (word) {
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        });
        title = parts.slice(1).join(' - ').trim();
      } else {
        title = parts.join(' - ').trim();
      }
    } else if (parts.length === 1) {
      title = parts[0].trim();
    } else {
      title = clean;
    }

    title = title.replace(/\s*-\s*$/, '').replace(/^\s*-\s*/, '').trim();

    if (key) {
      // Keys are stylised as the note letter plus a three-letter quality in
      // title case: 'B Min', 'E Dor'. A filename written out in full is
      // normalised to the same short form.
      var MODE_LABEL = {
        m: 'Min', min: 'Min', minor: 'Min', maj: 'Maj', major: 'Maj',
        mix: 'Mix', mixolydian: 'Mix',
        dor: 'Dor', dorian: 'Dor',
        phr: 'Phr', phrygian: 'Phr',
        lyd: 'Lyd', lydian: 'Lyd',
        loc: 'Loc', locrian: 'Loc',
        ion: 'Ion', ionian: 'Ion',
        aeo: 'Aeo', aeolian: 'Aeo'
      };
      var km = key.match(new RegExp('^([A-G])([#b])?\\s*(' + MODE_ALT + ')?', 'i'));
      if (km) {
        // Uppercase the letter but keep a flat lowercase so Bb does not render as BB.
        var note = km[1].toUpperCase() + (km[2] ? (km[2] === '#' ? '#' : 'b') : '');
        var mode = km[3] ? MODE_LABEL[km[3].toLowerCase()] : '';
        key = mode ? (note + ' ' + mode) : note;
      } else {
        key = key.toUpperCase();
      }
    }

    return {
      producer: producer,
      title: title,
      key: key,
      bpm: bpm
    };
  }

  // The name line is a fixed 22px (24px in Ink), so a long title plus a
  // spelled-out mode wraps onto a second line. Shrink to fit instead, so it
  // always reads as a single line regardless of how long the filename is.
  function fitStoryName() {
    if (!storyNameEl || !storyNameEl.parentNode) { return; }
    var base = (currentFormat === 'ink') ? 24 : 22;
    var minSize = 11;
    storyNameEl.style.whiteSpace = 'nowrap';
    storyNameEl.style.fontSize = base + 'px';
    var avail = storyNameEl.parentNode.clientWidth;
    if (!avail) { return; }
    var size = base;
    while (size > minSize && storyNameEl.scrollWidth > avail) {
      size -= 1;
      storyNameEl.style.fontSize = size + 'px';
    }
  }

  // Kept separate from updateStoryInfo so the transport can refresh the title
  // without also rewriting the Sheet Metadata inputs and losing manual edits.
  function renderStoryName(card) {
    if (!storyNameEl || !card) { return; }
    var rawName = (card._name || 'MIDI').replace(/d{1,2}e?mibby/gi, 'Demibby');
    var parsed = parseCardName(rawName);
    if (currentFormat === 'ink') {
      storyNameEl.textContent = parsed.title;
    } else {
      var formattedKey = parsed.key ? ' ' + parsed.key : '';
      storyNameEl.textContent = parsed.producer + ' - ' + parsed.title + ' ' + currentBPM + ' BPM' + formattedKey + '.mid';
    }
    fitStoryName();
  }

  function updateStoryInfo(card) {
    if (!storyNameEl) { return; }
    var rawName = card._name || 'MIDI';
    // Social handle doubles the D; the producer name does not. Catch every
    // spelling (demibby / ddemibby / ddmibby / dmibby) and render it one way.
    rawName = rawName.replace(/d{1,2}e?mibby/gi, 'Demibby');
    
    var parsed = parseCardName(rawName);

    renderStoryName(card);
    var subEl = storyEl.querySelector('.midg__story-sub');
    if (subEl) {
      // Footer removed: the composer name already appears in the title line,
      // so this was showing the same identity twice. Kept in the DOM (hidden)
      // because several restyle paths query it.
      subEl.textContent = '';
      subEl.style.display = 'none';
    }

    var inTitle = storyEl.querySelector('.midg__input-title');
    var inSubtitle = storyEl.querySelector('.midg__input-subtitle');
    var inComposer = storyEl.querySelector('.midg__input-composer');
    var inEditor = storyEl.querySelector('.midg__input-editor');
    var inInstrument = storyEl.querySelector('.midg__input-instrument');
    var inTempo = storyEl.querySelector('.midg__input-tempo');

    if (inTitle) inTitle.value = parsed.title;
    if (inSubtitle) inSubtitle.value = parsed.key;
    if (inComposer) inComposer.value = parsed.producer.replace('PROD. BY ', '');
    if (inEditor) inEditor.value = '';

    if (inInstrument) {
      var voiceDisplayNames = {
        'rhodes': 'RHODES',
        'strings': 'STRINGS',
        'vibes': 'VIBES',
        'triangle': 'TRIANGLE',
        'sine': 'SINE',
        'pluck': 'PLUCK',
        'horns': 'HORNS'
      };
      var activeInsts = [];
      if (currentVoices) {
        currentVoices.forEach(function (v) {
          if (voiceDisplayNames[v]) {
            activeInsts.push(voiceDisplayNames[v]);
          } else if (v.indexOf('eerie:') === 0) {
            var cleanName = v.replace('eerie:', '').replace(/-/g, ' ').toUpperCase();
            activeInsts.push(cleanName);
          }
        });
      }
      if (activeInsts.length === 1) {
        var singleVoice = currentVoices[0];
        if (singleVoice === 'rhodes') activeInsts[0] = 'RHODES PIANO';
        if (singleVoice === 'triangle') activeInsts[0] = 'TRIANGLE WAVE';
        if (singleVoice === 'sine') activeInsts[0] = 'SINE WAVE';
      }
      var separator = activeInsts.length > 1 ? ' + ' : ' / ';
      var defaultInst = activeInsts.length > 0 ? activeInsts.join(separator) : 'PIANO';
      inInstrument.value = defaultInst;
      var instrEl = storyEl.querySelector('.midg__story-instr');
      if (instrEl) {
        instrEl.textContent = defaultInst.toUpperCase();
      }
    }

    var bpmVal = card._bpm || 120;
    var tempoMark = getTempoName(bpmVal);
    if (inTempo) inTempo.value = tempoMark;

    var bpmValLabel = storyEl.querySelector('.midg__meta-bpm');
    if (bpmValLabel) bpmValLabel.textContent = bpmVal;

    if (updateSheetHeaderFromInputs) {
      updateSheetHeaderFromInputs();
    }
    updateStoryMidiControls(card);
  }

  function resumeAudioContext() {
    if (window.Tone && window.Tone.context && window.Tone.context.state !== 'running') {
      window.Tone.context.resume();
    }
  }

  // The active swatch was only updated inside the theme click handler, so any
  // programmatic switch (picking Ink defaults the theme to Sketchbook) left the
  // indicator pointing at the previous theme.
  // Shows or hides the media layer behind the note canvas. Video is muted,
  // looped and playsinline so it autoplays on iOS, where an unmuted or
  // fullscreen-defaulting video would be blocked.
  // YouTube and Vimeo cannot be fed to a <video> element - they only play
  // through their own player - so they get an iframe layer instead. Both are
  // asked to autoplay muted, loop, and hide their chrome, because an unmuted
  // embed is blocked from autoplaying by every current browser.
  function bgEmbedUrl(u, startSec) {
    var s = Math.max(0, Math.floor(startSec || 0));
    var yt = u.match(/(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
    if (yt) {
      var id = yt[1];
      ytVideoId = id;
      return 'https://www.youtube.com/embed/' + id +
             '?autoplay=1&mute=1&controls=0&loop=1&playlist=' + id +
             '&modestbranding=1&playsinline=1&rel=0&enablejsapi=1' +
             // cc_load_policy=0 keeps captions off, iv_load_policy=3 hides annotations.
             '&cc_load_policy=0&iv_load_policy=3&disablekb=1&start=' + s;
    }
    var vm = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    if (vm) {
      return 'https://player.vimeo.com/video/' + vm[1] +
             '?autoplay=1&muted=1&loop=1&background=1#t=' + s + 's';
    }
    return null;
  }

  // A YouTube iframe cannot be paused or reliably looped through URL parameters
  // alone: loop=1 restarts by reloading, which flashes the player's paused
  // chrome. Driving it through the IFrame API fixes both, and lets the clip
  // follow the MIDI transport the way a local file already can.
  function ensureYouTubeApi(cb) {
    if (window.YT && window.YT.Player) { cb(); return; }
    if (!ytApiLoading) {
      ytApiLoading = true;
      var s = document.createElement('script');
      s.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(s);
    }
    var tries = 0;
    var poll = setInterval(function () {
      if (window.YT && window.YT.Player) { clearInterval(poll); cb(); }
      else if (++tries > 120) { clearInterval(poll); }
    }, 100);
  }

  function destroyYtPlayer() {
    if (ytPlayer && ytPlayer.destroy) { try { ytPlayer.destroy(); } catch (e) { /* ignore */ } }
    ytPlayer = null;
  }

  function attachYtPlayer(iframeEl) {
    destroyYtPlayer();
    if (!iframeEl.id) { iframeEl.id = 'midg-yt-' + Math.random().toString(36).slice(2, 9); }
    ensureYouTubeApi(function () {
      try {
        ytPlayer = new window.YT.Player(iframeEl.id, {
          events: {
            onReady: function (e) {
              try {
                e.target.mute();
                e.target.unloadModule('captions');
                e.target.unloadModule('cc');
              } catch (err) { /* ignore */ }
              syncBgTransport();
            },
            onStateChange: function (e) {
              // Seek rather than reload, so the player's paused chrome never shows.
              if (e.data === window.YT.PlayerState.ENDED) {
                try {
                  e.target.seekTo(bgStartSec || 0, true);
                  e.target.playVideo();
                } catch (err) { /* ignore */ }
              }
            }
          }
        });
      } catch (err) { ytPlayer = null; }
    });
  }

  function applyBgMedia() {
    if (!storyEl) { return; }
    var imgEl = storyEl.querySelector('.midg__bg-img');
    var vidEl = storyEl.querySelector('.midg__bg-video');
    var embedEl = storyEl.querySelector('.midg__bg-embed');
    var active = bgMediaOn && !!bgMediaUrl;
    var embedSrc = active ? bgEmbedUrl(bgMediaUrl, bgStartSec) : null;

    if (embedEl) {
      if (embedSrc) {
        if (embedEl.getAttribute('src') !== embedSrc) { embedEl.setAttribute('src', embedSrc); }
        embedEl.style.display = 'block';
        if (/youtube\.com\/embed\//.test(embedSrc)) { attachYtPlayer(embedEl); }
        else { destroyYtPlayer(); }
      } else {
        destroyYtPlayer();
        if (embedEl.getAttribute('src')) { embedEl.removeAttribute('src'); }
        embedEl.style.display = 'none';
      }
    }

    if (imgEl) {
      if (active && !embedSrc && bgMediaType === 'image') {
        imgEl.onerror = function () {
          if (bgMediaType !== 'video') { bgMediaType = 'video'; applyBgMedia(); }
        };
        imgEl.src = bgMediaUrl;
        imgEl.style.display = 'block';
      } else {
        imgEl.style.display = 'none';
      }
    }

    if (vidEl) {
      if (active && !embedSrc && bgMediaType === 'video') {
        if (vidEl.getAttribute('src') !== bgMediaUrl) {
          vidEl.src = bgMediaUrl;
          vidEl.addEventListener('loadedmetadata', function () {
            try { vidEl.currentTime = bgStartSec || 0; } catch (e) { /* ignore */ }
          }, { once: true });
        }
        vidEl.muted = true;
        vidEl.loop = true;
        vidEl.playsInline = true;
        vidEl.style.display = 'block';
        syncBgTransport();
      } else {
        try { vidEl.pause(); } catch (e) { /* ignore */ }
        vidEl.style.display = 'none';
      }
    }
    renderStoryCanvases();
  }

  // With bgPlayMode 'transport' the background follows the MIDI transport, so a
  // clip can be cued to a bar rather than drifting against the take. Embeds
  // cannot be scrubbed without loading each provider's player API, so they only
  // honour the start offset, not play/pause.
  function syncBgTransport() {
    if (!storyEl) { return; }
    var vidEl = storyEl.querySelector('.midg__bg-video');
    var shouldRun = (bgPlayMode === 'always') || isPlaying;
    var trackPos = (bgPlayMode === 'transport');
    // In transport mode the clip is cued to the roll, so a stop parks it at the
    // current position rather than rewinding to the start.
    var target = trackPos ? ((bgStartSec || 0) + elapsedNow()) : (bgStartSec || 0);
    if (!isFinite(target)) { target = bgStartSec || 0; }

    if (vidEl && vidEl.style.display !== 'none') {
      if (trackPos && Math.abs(vidEl.currentTime - target) > 0.25) {
        try { vidEl.currentTime = target; } catch (e) { /* not seekable yet */ }
      }
      if (shouldRun) {
        var pr = vidEl.play();
        if (pr && pr.catch) { pr.catch(function () { /* autoplay refused; poster still shows */ }); }
      } else {
        try {
          vidEl.pause();
          if (!trackPos) { vidEl.currentTime = bgStartSec || 0; }
        } catch (e) { /* ignore */ }
      }
    }

    if (ytPlayer && ytPlayer.playVideo) {
      try {
        if (trackPos && ytPlayer.getCurrentTime &&
            Math.abs(ytPlayer.getCurrentTime() - target) > 0.35) {
          ytPlayer.seekTo(target, true);
        }
        if (shouldRun) { ytPlayer.playVideo(); }
        else {
          ytPlayer.pauseVideo();
          if (!trackPos) { ytPlayer.seekTo(bgStartSec || 0, true); }
        }
      } catch (e) { /* ignore */ }
    }
  }

  // The panel holds roughly three times the content it can show at once, so
  // every section header folds the block beneath it. Headers are identified by
  // their inline style rather than a class because the panel markup is built
  // as one concatenated string. Sections whose visibility is already driven by
  // other code (the MIDI track list) are left alone so nothing fights.
  function makeSectionsCollapsible() {
    if (!storyEl) { return; }
    var panel = storyEl.querySelector('.midg__meta-panel');
    if (!panel || panel.getAttribute('data-collapsible') === '1') { return; }
    panel.setAttribute('data-collapsible', '1');
    var kids = Array.prototype.slice.call(panel.children);
    var isHeader = function (el) {
      return !!el && el.style.textTransform === 'uppercase' &&
             el.style.fontWeight === '700' && !el.querySelector('input, select, button');
    };
    kids.forEach(function (el, i) {
      if (!isHeader(el)) { return; }
      if (el.className) { return; }
      var body = kids[i + 1];
      if (!body || isHeader(body) || body.className) { return; }
      var openDisplay = body.style.display || 'flex';
      // Start folded: the panel holds far more than it can show, so an open
      // accordion buries whatever you are actually looking for.
      var open = false;
      body.style.display = 'none';
      var caret = document.createElement('span');
      caret.style.cssText = 'float: right; opacity: 0.65; font-weight: 700;';
      caret.textContent = '+';
      el.appendChild(caret);
      el.style.cursor = 'pointer';
      el.style.userSelect = 'none';
      el.addEventListener('click', function (e) {
        e.stopPropagation();
        open = !open;
        body.style.display = open ? openDisplay : 'none';
        caret.textContent = open ? '\u2212' : '+';
      });
    });
  }

  function syncThemeButtons() {
    if (!storyEl) { return; }
    storyEl.querySelectorAll('.midg__theme-btn').forEach(function (b) {
      var isActive = b.getAttribute('data-theme') === currentThemeName;
      b.style.borderColor = isActive ? '#fff' : 'transparent';
      b.style.boxShadow = isActive ? '0 0 0 2px rgba(0,0,0,0.35), 0 4px 10px rgba(0,0,0,0.3)' : 'none';
      b.style.transform = isActive ? 'scale(1.3)' : 'scale(1)';
    });
  }

  function updateStoryMidiControls(card) {
    if (!storyOpen || !storyEl) return;
    
    // 1. Populate MIDI list with mini cards
    var listContainer = storyEl.querySelector('.midg__story-midi-list');
    if (listContainer) {
      listContainer.innerHTML = '';
      var cards = document.querySelectorAll('.mid-card');
      
      var isInk = (currentFormat === 'ink');
      var isLight = isInk || (currentThemeName === 'sketchbook' || currentThemeName === 'watercolor');
      var textColor = isLight ? '#1a1a1a' : '#ffffff';
      var activeBg = isLight ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.15)';
      var inactiveBg = isLight ? 'rgba(0, 0, 0, 0.03)' : 'rgba(255, 255, 255, 0.05)';
      var activeBorder = isLight ? '1.5px solid #1a1a1a' : '2px solid #a983ff';
      var inactiveBorder = isLight ? '1px solid rgba(0,0,0,0.18)' : '1px solid rgba(255,255,255,0.1)';
      
      cards.forEach(function (c) {
        var nameSpan = c.querySelector('.wf-card__name');
        var name = nameSpan ? nameSpan.textContent : 'MIDI';
        var isCurrent = (c === card);
        
        var miniCard = document.createElement('div');
        miniCard.style.display = 'flex';
        miniCard.style.alignItems = 'center';
        miniCard.style.gap = '6px';
        miniCard.style.padding = '4px';
        miniCard.style.borderRadius = '4px';
        miniCard.style.background = isCurrent ? activeBg : inactiveBg;
        miniCard.style.border = isCurrent ? activeBorder : inactiveBorder;
        miniCard.style.cursor = 'pointer';
        miniCard.style.transition = 'all 0.2s ease';
        miniCard.style.boxSizing = 'border-box';
        miniCard.style.width = '100%';
        
        // Thumbnail canvas container
        var canvasContainer = document.createElement('div');
        canvasContainer.style.width = '45px';
        canvasContainer.style.height = '26px';
        canvasContainer.style.borderRadius = '3px';
        canvasContainer.style.overflow = 'hidden';
        canvasContainer.style.background = isLight ? 'rgba(0,0,0,0.05)' : 'rgba(0,0,0,0.3)';
        canvasContainer.style.flexShrink = '0';
        
        var miniCanvas = document.createElement('canvas');
        miniCanvas.style.width = '100%';
        miniCanvas.style.height = '100%';
        miniCanvas.style.display = 'block';
        
        canvasContainer.appendChild(miniCanvas);
        
        // Title text container
        var titleContainer = document.createElement('div');
        titleContainer.style.flex = '1';
        titleContainer.style.minWidth = '0';
        
        var titleText = document.createElement('div');
        titleText.textContent = name.replace(/\.mid$/i, '').replace(/_/g, ' ');
        titleText.style.fontSize = '8px';
        titleText.style.fontWeight = isCurrent ? 'bold' : 'normal';
        titleText.style.color = textColor;
        titleText.style.whiteSpace = 'normal';
        titleText.style.wordBreak = 'break-word';
        titleText.style.lineHeight = '1.1';
        
        titleContainer.appendChild(titleText);
        
        miniCard.appendChild(canvasContainer);
        miniCard.appendChild(titleContainer);
        
        // Click behavior
        miniCard.addEventListener('click', function () {
          if (current !== c) {
            resumeAudioContext();
            startFresh(c, 0, false);
            setTimeout(function() {
              updateStoryMidiControls(c);
            }, 50);
          }
        });
        
        listContainer.appendChild(miniCard);
        
        // Draw the mini preview on the canvas!
        setTimeout(function() {
          var dpr = window.devicePixelRatio || 1;
          miniCanvas.width = 45 * dpr;
          miniCanvas.height = 26 * dpr;
          var g = miniCanvas.getContext('2d');
          if (c._notes && c._notes.length) {
            var notes = c._notes;
            var w = miniCanvas.width, h = miniCanvas.height;
            var dur = c._dur || 1;
            var minPitch = c._minPitch, maxPitch = c._maxPitch;
            var span = Math.max(1, maxPitch - minPitch);
            var padTop = h * 0.08, rollH = h * 0.84;
            var barH = Math.max(1, h * 0.05);
            var theme = THEMES[currentThemeName];
            
            g.clearRect(0, 0, w, h);
            for (var i = 0; i < notes.length; i++) {
              var n = notes[i];
              // Filter muted tracks
              if (!isTrackEnabled(c, n.trackIdx)) { continue; }
              var x = (n.time / dur) * w;
              var nw = Math.max(1, (n.duration / dur) * w - 0.5);
              var yFrac = 1 - ((n.midi - minPitch) / span);
              var y = padTop + yFrac * rollH - barH / 2;
              
              var color;
              if (isInk) {
                color = '#1a1a1a';
              } else if (theme.lit === 'multi') {
                var palette = currentThemeName === 'soul' ? ['#d89a27', '#4a7b82', '#b93b26', '#e0d8c3'] : ['#2b5c8f', '#a9332a', '#caa230', '#34383a'];
                color = palette[n.midi % palette.length];
              } else {
                color = theme.lit;
              }
              g.fillStyle = color;
              g.fillRect(x, y, nw, barH);
            }
          }
        }, 0);
      });
    }

    // 2. Populate Tracks list
    var tracksHeader = storyEl.querySelector('.midg__story-tracks-header');
    var tracksList = storyEl.querySelector('.midg__story-tracks-list');
    if (tracksList && tracksHeader) {
      tracksList.innerHTML = '';
      if (card && card._tracks && card._tracks.length > 0) {
        tracksHeader.style.display = 'block';
        tracksList.style.display = 'flex';
        card._tracks.forEach(function (track) {
          var row = document.createElement('label');
          row.style.display = 'flex';
          row.style.alignItems = 'center';
          row.style.gap = '8px';
          row.style.fontSize = '10px';
          row.style.cursor = 'pointer';
          row.style.padding = '3px 6px';
          row.style.borderRadius = '4px';
          row.style.backgroundColor = isLight ? 'rgba(0,0,0,0.03)' : 'rgba(255,255,255,0.04)';
          row.style.border = isLight ? '1px solid rgba(0,0,0,0.08)' : '1px solid rgba(255,255,255,0.08)';
          row.style.color = isLight ? '#1a1a1a' : '#fff';
          row.style.boxSizing = 'border-box';
          row.style.width = '100%';
          row.style.userSelect = 'none';
          
          var chk = document.createElement('input');
          chk.type = 'checkbox';
          chk.checked = track.enabled;
          chk.style.margin = '0';
          chk.style.cursor = 'pointer';
          
          var nameSpan = document.createElement('span');
          nameSpan.textContent = track.name;
          nameSpan.style.opacity = track.enabled ? '1' : '0.5';

          chk.addEventListener('change', function () {
            track.enabled = chk.checked;
            nameSpan.style.opacity = chk.checked ? '1' : '0.5';
            if (current === card && isPlaying) {
              scheduleFrom(card, elapsedNow());
            } else {
              draw(card, current === card ? pausedAt : 0);
            }
          });

          row.appendChild(chk);
          row.appendChild(nameSpan);
          tracksList.appendChild(row);
        });
      } else {
        tracksHeader.style.display = 'none';
        tracksList.style.display = 'none';
      }
    }
    // Style all checkboxes inside meta panel to ensure native rendering
    if (storyEl) {
      storyEl.querySelectorAll('.midg__meta-panel input[type="checkbox"]').forEach(function (chk) {
        chk.style.appearance = 'checkbox';
        chk.style.webkitAppearance = 'checkbox';
        chk.style.mozAppearance = 'checkbox';
        chk.style.accentColor = '#b79bff';
        chk.style.cursor = 'pointer';
        chk.style.display = 'inline-block';
      });
    }
  }

  function openStory() {
    buildStoryOverlay();
    storyOpen = true;
    storyEl.classList.add('is-open');
    updateStoryMidiControls(current);
    if (storyEl) {
      var theme = THEMES[currentThemeName];
      var isInk = (currentFormat === 'ink');
      var isLight = isInk || (currentThemeName === 'sketchbook' || currentThemeName === 'watercolor');
      storyEl.style.backgroundColor = isInk ? 'rgba(245,243,238,0.98)' : theme.backdrop;
      if (storyFrameEl) {
        if (isInk) {
          storyFrameEl.style.backgroundColor = '#faf7f2';
          storyFrameEl.style.borderColor = 'rgba(26,26,26,0.1)';
        } else {
          storyFrameEl.style.backgroundColor = theme.backdrop || '#07090a';
          storyFrameEl.style.borderColor = isLight ? 'rgba(0,0,0,0.1)' : '#28371d';
        }
      }
      var sheetHeader = storyEl.querySelector('.midg__sheet-header');
      if (sheetHeader) {
        sheetHeader.style.display = isInk ? 'block' : 'none';
      }
      var infoEl = storyEl.querySelector('.midg__story-info');
      if (infoEl) {
        infoEl.style.display = isInk ? 'none' : 'block';
      }
      var metaPanel = storyEl.querySelector('.midg__meta-panel');
      if (metaPanel) {
        metaPanel.style.display = 'flex';
        var sheetSec = metaPanel.querySelector('.midg__meta-sheet-section');
        if (sheetSec) {
          sheetSec.style.display = 'flex';
        }
        if (isLight) {
          metaPanel.style.background = 'rgba(244,241,234,0.85)';
          metaPanel.style.borderColor = 'rgba(0,0,0,0.15)';
          metaPanel.style.color = '#1a1a1a';
          metaPanel.querySelectorAll('label').forEach(function (l) { l.style.color = '#1a1a1a'; });
          metaPanel.querySelectorAll('input[type="text"], select').forEach(function (inp) {
            inp.style.background = 'rgba(0,0,0,0.04)';
            inp.style.borderColor = 'rgba(0,0,0,0.12)';
            inp.style.color = '#1a1a1a';
          });
        } else {
          metaPanel.style.background = 'rgba(7,9,10,0.8)';
          metaPanel.style.borderColor = 'rgba(255,255,255,0.15)';
          metaPanel.style.color = '#fff';
          metaPanel.querySelectorAll('label').forEach(function (l) { l.style.color = '#fff'; });
          metaPanel.querySelectorAll('input[type="text"], select').forEach(function (inp) {
            inp.style.background = 'rgba(255,255,255,0.08)';
            inp.style.borderColor = 'rgba(255,255,255,0.15)';
            inp.style.color = '#fff';
          });
        }
      }
      storyEl.querySelectorAll('.midg__theme-btn').forEach(function (b) {
        var t = b.getAttribute('data-theme');
        b.style.borderColor = t === currentThemeName ? '#fff' : 'transparent';
        b.style.boxShadow = t === currentThemeName ? '0 4px 10px rgba(0,0,0,0.3)' : 'none';
      });

      var chordBtn = storyEl.querySelector('.midg__chord-toggle');
      if (chordBtn) {
        chordBtn.textContent = 'Chord: ' + (showChordTranscriber ? 'ON' : 'OFF');
        if (isLight) {
          chordBtn.style.color = '#1a1a1a';
          chordBtn.style.backgroundColor = 'rgba(0,0,0,0.05)';
          chordBtn.style.borderColor = 'rgba(0,0,0,0.15)';
        } else {
          chordBtn.style.color = '#fff';
          chordBtn.style.backgroundColor = 'rgba(255,255,255,0.05)';
          chordBtn.style.borderColor = 'rgba(255,255,255,0.15)';
        }
      }

      var curvesBtn = storyEl.querySelector('.midg__curves-toggle');
      if (curvesBtn) {
        curvesBtn.textContent = 'Curves Option';
        if (isLight) {
          curvesBtn.style.color = '#1a1a1a';
          curvesBtn.style.backgroundColor = 'rgba(0,0,0,0.05)';
          curvesBtn.style.borderColor = 'rgba(0,0,0,0.15)';
        } else {
          curvesBtn.style.color = '#fff';
          curvesBtn.style.backgroundColor = 'rgba(255,255,255,0.05)';
          curvesBtn.style.borderColor = 'rgba(255,255,255,0.15)';
        }
      }

      var infoEl2 = storyEl.querySelector('.midg__story-info');
      var closeBtn = storyEl.querySelector('.midg__story-close');
      var pickerLabel = storyEl.querySelector('.midg__theme-picker span');
      var formatLabel = storyEl.querySelector('.midg__format-picker span');

      storyEl.querySelectorAll('.midg__format-btn').forEach(function (b) {
        var f = b.getAttribute('data-format');
        if (f === currentFormat) {
          b.classList.add('on');
        } else {
          b.classList.remove('on');
        }
        var isBtnActive = b.classList.contains('on');
        if (isLight) {
          b.style.color = isBtnActive ? '#f2f0f7' : '#1a1a1a';
          b.style.backgroundColor = isBtnActive ? '#1a1a1a' : 'rgba(0,0,0,0.05)';
          b.style.borderColor = isBtnActive ? '#1a1a1a' : 'transparent';
        } else {
          b.style.color = isBtnActive ? '#07090a' : 'rgba(255,255,255,0.6)';
          b.style.backgroundColor = isBtnActive ? '#fff' : 'rgba(255,255,255,0.05)';
          b.style.borderColor = isBtnActive ? '#fff' : 'transparent';
        }
      });

      if (infoEl2) {
        var nameEl = infoEl2.querySelector('.midg__story-name');
        var subEl = infoEl2.querySelector('.midg__story-sub');
        if (nameEl) {
          nameEl.style.color = isLight ? '#1a1a1a' : '#f2f0f7';
          nameEl.style.fontFamily = isInk ? '"Garamond", "Baskerville", "Georgia", serif' : '';
          nameEl.style.fontStyle = isInk ? 'normal' : '';
          nameEl.style.letterSpacing = isInk ? '1.5px' : '';
          nameEl.style.fontSize = isInk ? '24px' : '';
          nameEl.style.textTransform = isInk ? 'none' : '';
          fitStoryName();
        }
        if (subEl) {
          subEl.style.color = isLight ? '#7b68ee' : '#b79bff';
          subEl.style.fontFamily = isInk ? '"Garamond", "Baskerville", "Georgia", serif' : '';
          subEl.style.fontStyle = isInk ? 'normal' : '';
          subEl.style.letterSpacing = isInk ? '3.5px' : '';
          subEl.style.fontSize = isInk ? '10px' : '';
          subEl.style.textTransform = isInk ? 'uppercase' : '';
        }
        var instrEl = infoEl2.querySelector('.midg__story-instr');
        if (instrEl) {
          instrEl.style.color = isLight ? 'rgba(26,26,26,0.65)' : 'rgba(242,240,247,0.65)';
          instrEl.style.fontFamily = isInk ? '"Garamond", "Baskerville", "Georgia", serif' : '';
          instrEl.style.fontSize = isInk ? '10px' : '9px';
        }
      }
      if (closeBtn) {
        closeBtn.style.color = isLight ? '#1a1a1a' : '#e6e3f0';
        closeBtn.style.backgroundColor = isLight ? '#f4f1ea' : '#11170c';
        closeBtn.style.borderColor = isLight ? 'rgba(0,0,0,0.15)' : '#28371d';
      }
      if (pickerLabel) {
        pickerLabel.style.color = isLight ? '#1a1a1a' : '#fff';
      }
      if (formatLabel) {
        formatLabel.style.color = isLight ? '#1a1a1a' : '#fff';
      }
    }
    if (!current) {
      var first = document.querySelector('.mid-card[data-midi-url]');
      if (first) { seekPlay(first, 0); }
    } else {
      updateStoryInfo(current);
      if (current._notes) { renderStoryCanvases(); }
    }
    updateVoiceUI();
  }

  function closeStory() {
    storyOpen = false;
    if (storyEl) { storyEl.classList.remove('is-open'); }
  }

  /* ---- playback ---- */
  var current = null, isPlaying = false, pausedAt = 0;
  var timers = [], raf = null, activeOffset = 0, activeWall = 0, activeRate = 1;
  var scheduleEpoch = 0;

  function clearTimers() {
    timers.forEach(function (id) { clearTimeout(id); });
    timers = [];
    if (raf) { cancelAnimationFrame(raf); raf = null; }
  }

  function releaseAllVoices() {
    Object.keys(voiceCache).forEach(function (k) {
      var v = voiceCache[k];
      if (v && v.releaseAll) { v.releaseAll(); }
    });
  }

  function elapsedNow() {
    return activeOffset + ((performance.now() - activeWall) / 1000) * activeRate;
  }

  function fullStop() {
    scheduleEpoch++;
    clearTimers();
    releaseAllVoices();
    if (current) { current.classList.remove('is-playing'); drawOverviewOn(current.querySelector('.mid-card__canvas'), current, 0); }
    current = null; isPlaying = false; pausedAt = 0;
  }

  function pauseCurrent() {
    if (!current || !isPlaying) { return; }
    pausedAt = elapsedNow();
    scheduleEpoch++;
    clearTimers();
    releaseAllVoices();
    current.classList.remove('is-playing');
    isPlaying = false;
    if (typeof syncBgTransport === 'function') { syncBgTransport(); }
    drawOverviewOn(current.querySelector('.mid-card__canvas'), current, pausedAt / (current._dur || 1));
    if (storyOpen) { renderStoryCanvases(); }
  }

  function tick(card, fromOffset, fromWall, rate) {
    var dur = card._dur || 1;
    var elapsed = fromOffset + ((performance.now() - fromWall) / 1000) * rate;
    if (elapsed >= dur) { elapsed = dur; }
    draw(card, elapsed);
    raf = requestAnimationFrame(function () { tick(card, fromOffset, fromWall, rate); });
  }

  function scheduleFrom(card, offsetSec) {
    scheduleEpoch++;
    var myEpoch = scheduleEpoch;
    clearTimers();
    if (currentVoices.length === 0 || !window.Tone) { return; }
    var rate = rateFor(card);
    var wallStart = performance.now();
    activeOffset = offsetSec; activeWall = wallStart; activeRate = rate;
    isPlaying = true;
    if (typeof syncBgTransport === 'function') { syncBgTransport(); }
    tick(card, offsetSec, wallStart, rate);

    window.Tone.start().then(function () {
      if (myEpoch !== scheduleEpoch) { return; }
      var notes = card._notes || [];
      notes.forEach(function (n) {
        if (n.time < offsetSec) { return; }
        // Playback ignored the track toggles entirely: muting a track hid its
        // notes but kept scheduling them, so it carried on sounding.
        if (!isTrackEnabled(card, n.trackIdx)) { return; }
        var delayMs = ((n.time - offsetSec) / rate) * 1000;
        var scaledDur = Math.max(0.05, n.duration / rate);
        var id = setTimeout(function () {
          if (myEpoch !== scheduleEpoch) { return; }
          var freq = window.Tone.Frequency(n.midi + pitchOffset, 'midi').toFrequency();
          var detunedFreq = freq * Math.pow(2, detuneCents / 1200);
          var freqArg = detunedFreq.toFixed(3) + "hz";
          currentVoices.forEach(function (vName) {
            var v = getVoice(vName);
            if (v) {
              try {
                v.triggerAttackRelease(freqArg, scaledDur, undefined, Math.min(1, n.velocity || 0.7), n);
              } catch (e) { /* ignore */ }
            }
          });
        }, delayMs);
        timers.push(id);
      });
      var loopMs = ((card._dur - offsetSec) / rate) * 1000;
      var loopId = setTimeout(function () {
        if (myEpoch === scheduleEpoch && current === card && isPlaying) { scheduleFrom(card, 0); }
      }, Math.max(60, loopMs));
      timers.push(loopId);
    });
  }

  function startFresh(card, fracOrOffset, isFrac) {
    fullStop();
    current = card;
    isPlaying = true;
    if (typeof syncBgTransport === 'function') { syncBgTransport(); }
    card.classList.add('is-playing');
    syncTransportTo(card);
    updateStoryInfo(card);
    var go = function () {
      syncTransportTo(card);
      updateStoryInfo(card);
      var offset = isFrac ? fracOrOffset * (card._dur || 1) : fracOrOffset;
      scheduleFrom(card, offset);
    };
    if (card._notes) { go(); }
    else { load(card); loadLibs().then(function () { var t2 = setTimeout(function () { if (current === card) { go(); } }, 50); timers.push(t2); }); }
  }

  function seekPlay(card, frac) {
    if (current === card && isPlaying) {
      scheduleFrom(card, frac * (card._dur || 1));
      return;
    }
    startFresh(card, frac, true);
  }

  function togglePlayPause(card) {
    if (current === card && isPlaying) { pauseCurrent(); return; }
    if (current === card && !isPlaying && card._notes) { current.classList.add('is-playing'); scheduleFrom(card, pausedAt); return; }
    startFresh(card, 0, false);
  }

  function fractionFromEvent(el, e) {
    var rect = el.getBoundingClientRect();
    var x = (e.touches && e.touches[0] ? e.touches[0].clientX : e.clientX);
    var f = (x - rect.left) / rect.width;
    if (f < 0) { f = 0; }
    if (f > 1) { f = 1; }
    return f;
  }

  function selectVoice(widget, btn) {
    var voice = btn.getAttribute('data-voice');

    btn.classList.toggle('on');

    var activeButtons = widget.querySelectorAll('.midg__voice.on, .midg__sub-voice.on');
    if (activeButtons.length === 0) {
      btn.classList.add('on');
      activeButtons = [btn];
    }

    currentVoices = [];
    activeButtons.forEach(function (b) {
      currentVoices.push(b.getAttribute('data-voice'));
    });

    updateVoiceUI();

    if (storyOpen && storyEl) {
      var inInstrument = storyEl.querySelector('.midg__input-instrument');
      if (inInstrument) {
        var voiceDisplayNames = {
          'rhodes': 'RHODES',
          'strings': 'STRINGS',
          'vibes': 'VIBES',
          'triangle': 'TRIANGLE',
          'sine': 'SINE',
          'pluck': 'PLUCK',
          'horns': 'HORNS'
        };
        var activeInsts = [];
        if (currentVoices) {
          currentVoices.forEach(function (v) {
            if (voiceDisplayNames[v]) {
              activeInsts.push(voiceDisplayNames[v]);
            } else if (v.indexOf('eerie:') === 0) {
              var cleanName = v.replace('eerie:', '').replace(/-/g, ' ').toUpperCase();
              activeInsts.push(cleanName);
            }
          });
        }
        if (activeInsts.length === 1) {
          var singleVoice = currentVoices[0];
          if (singleVoice === 'rhodes') activeInsts[0] = 'RHODES PIANO';
          if (singleVoice === 'triangle') activeInsts[0] = 'TRIANGLE WAVE';
          if (singleVoice === 'sine') activeInsts[0] = 'SINE WAVE';
        }
        var separator = activeInsts.length > 1 ? ' + ' : ' / ';
        var defaultInst = activeInsts.length > 0 ? activeInsts.join(separator) : 'PIANO';
        inInstrument.value = defaultInst;
        if (updateSheetHeaderFromInputs) { updateSheetHeaderFromInputs(); }
      }
    }

    if (current && isPlaying) {
      scheduleFrom(current, elapsedNow());
    }

    // Build the voice now rather than on the first note, and show the button as
    // loading until its samples are in, so a silent stretch reads as 'loading'
    // rather than 'broken'.
    try {
      var vName = btn.getAttribute('data-voice');
      if (vName && window.Tone) {
        getVoice(vName);
        if (!voiceReady(vName)) {
          btn.classList.add('is-loading');
          var waited = 0;
          var poll = setInterval(function () {
            waited += 250;
            if (voiceReady(vName) || waited > 30000) {
              clearInterval(poll);
              btn.classList.remove('is-loading');
            }
          }, 250);
        }
      }
    } catch (e) { /* voice will still build on first note */ }
  }

  function stepPitch(dir) {
    pitchOffset = Math.max(PITCH_MIN, Math.min(PITCH_MAX, pitchOffset + dir));
    updateTransportUI();
    if (current && isPlaying) { scheduleFrom(current, elapsedNow()); }
  }

  function stepDetune(dir) {
    detuneCents = Math.max(DETUNE_MIN, Math.min(DETUNE_MAX, detuneCents + dir * DETUNE_STEP));
    updateTransportUI();
    if (current && isPlaying) { scheduleFrom(current, elapsedNow()); }
  }

  function stepTempo(dir) {
    currentBPM = Math.max(BPM_MIN, Math.min(BPM_MAX, currentBPM + dir * BPM_STEP));
    updateTransportUI();
    if (storyOpen && storyEl) {
      var bpmValLabel = storyEl.querySelector('.midg__meta-bpm');
      if (bpmValLabel) { bpmValLabel.textContent = currentBPM; }
      var inTempo = storyEl.querySelector('.midg__input-tempo');
      if (inTempo) { inTempo.value = getTempoName(currentBPM); }
      if (updateSheetHeaderFromInputs) { updateSheetHeaderFromInputs(); }
    }
    if (current && isPlaying) { scheduleFrom(current, elapsedNow()); }
  }

  function initVoiceBar(widget) {
    var bar = widget.querySelector('.midg__voices');
    if (!bar) { return; }
    widgetRefs.push(widget);
    widget.querySelectorAll('.midg__sub-voice[data-url]').forEach(function (btn) {
      eerieUrls[btn.getAttribute('data-voice')] = btn.getAttribute('data-url');
    });
    updateTransportUI();

    widget.querySelectorAll('.midg__mixer-dry').forEach(function (el) {
      el.addEventListener('input', function (e) {
        var v = parseFloat(e.target.value);
        if (dryGain && window.Tone) { dryGain.gain.setValueAtTime(v, window.Tone.now()); }
        var label = widget.querySelector('.midg__mixer-val-dry');
        if (label) { label.textContent = Math.round(v * 100) + '%'; }
      });
    });
    widget.querySelectorAll('.midg__mixer-er').forEach(function (el) {
      el.addEventListener('input', function (e) {
        var v = parseFloat(e.target.value);
        if (jmcSend && window.Tone) { jmcSend.gain.setValueAtTime(v, window.Tone.now()); }
        var label = widget.querySelector('.midg__mixer-val-er');
        if (label) { label.textContent = Math.round(v * 100) + '%'; }
      });
    });
    widget.querySelectorAll('.midg__mixer-kiri').forEach(function (el) {
      el.addEventListener('input', function (e) {
        var v = parseFloat(e.target.value);
        if (kiriSend && window.Tone) { kiriSend.gain.setValueAtTime(v, window.Tone.now()); }
        var label = widget.querySelector('.midg__mixer-val-kiri');
        if (label) { label.textContent = Math.round(v * 100) + '%'; }
      });
    });

    widget.addEventListener('click', function (e) {
      var storyBtn = e.target.closest('.midg__story-btn');
      if (storyBtn) { showPasswordModal(); return; }
      var eerieBtn = e.target.closest('.midg__voice--eerie');
      if (eerieBtn) {
        var sub = widget.querySelector('.midg__sub');
        if (sub) { sub.classList.toggle('is-open'); eerieBtn.classList.toggle('is-open'); }
        return;
      }
      var subVoiceBtn = e.target.closest('.midg__sub-voice');
      if (subVoiceBtn) { selectVoice(widget, subVoiceBtn); return; }
      var voiceBtn = e.target.closest('.midg__voice[data-voice]');
      if (voiceBtn) { selectVoice(widget, voiceBtn); return; }
      var stepBtn = e.target.closest('.midg__step');
      if (stepBtn) {
        var kind = stepBtn.getAttribute('data-step');
        var dir = parseInt(stepBtn.getAttribute('data-dir'), 10);
        if (kind === 'pitch') { stepPitch(dir); }
        else if (kind === 'detune') { stepDetune(dir); }
        else if (kind === 'tempo') { stepTempo(dir); }
        return;
      }
    });
  }

  function init() {
    var cards = document.querySelectorAll('.mid-card');
    document.querySelectorAll('.midg').forEach(initVoiceBar);
    if (!cards.length) { return; }
    var io = null;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { if (en.isIntersecting) { load(en.target); io.unobserve(en.target); } });
      }, { rootMargin: '200px' });
    }
    cards.forEach(function (card) {
      var cv = card.querySelector('.mid-card__canvas');
      if (cv && cv.hasAttribute('data-fake')) { renderFake(card); return; }
      if (!card.getAttribute('data-midi-url')) { return; }
      if (io) { io.observe(card); } else { load(card); }

      var wave = card.querySelector('.mid-card__cv');
      if (wave) {
        var isCardDragging = false;
        var cardStartX = 0;
        var cardStartFrac = 0;
        var cardHasDragged = false;
        var cardWasPlayingBeforeDrag = false;

        function getCardX(e) {
          if (e.touches && e.touches[0]) { return e.touches[0].clientX; }
          return e.clientX;
        }

        wave.addEventListener('mousedown', onCardDragStart);
        wave.addEventListener('touchstart', onCardDragStart, { passive: true });

        function onCardDragStart(e) {
          if (isPlaying) { return; }

          var isNewCard = (current !== card);
          isCardDragging = true;
          cardHasDragged = false;
          cardStartX = getCardX(e);

          if (isNewCard) {
            fullStop();
            current = card;
            syncTransportTo(card);
            updateStoryInfo(card);
            pausedAt = 0;
            if (typeof syncBgTransport === 'function') { syncBgTransport(); }
            cardStartFrac = 0;
          } else {
            cardStartFrac = pausedAt / (card._dur || 1);
          }
        }

        window.addEventListener('mousemove', onCardDragMove);
        window.addEventListener('touchmove', onCardDragMove, { passive: false });

        function onCardDragMove(e) {
          if (!isCardDragging || current !== card || isPlaying) { return; }
          var curX = getCardX(e);
          var diffX = curX - cardStartX;
          if (Math.abs(diffX) > 6) {
            cardHasDragged = true;
          }
          if (cardHasDragged) {
            e.preventDefault();
            var rect = wave.getBoundingClientRect();
            var canvas = wave.querySelector('canvas');
            if (canvas) {
            var deltaFrac = diffX / (rect.width * 5.0);
            var targetFrac = Math.max(0, Math.min(0.99, cardStartFrac + deltaFrac));
              pausedAt = targetFrac * (card._dur || 1);
              if (typeof syncBgTransport === 'function') { syncBgTransport(); }
              drawOverviewOn(canvas, card, targetFrac);
            }
          }
        }

        window.addEventListener('mouseup', onCardDragEnd);
        window.addEventListener('touchend', onCardDragEnd);

        function onCardDragEnd(e) {
          if (!isCardDragging) { return; }
          isCardDragging = false;

          var canvas = wave.querySelector('canvas');
          if (!canvas) { return; }

          if (!cardHasDragged) {
            var rect = wave.getBoundingClientRect();
            var frac = (cardStartX - rect.left) / rect.width;
            frac = Math.max(0, Math.min(0.99, frac));
            pausedAt = frac * (card._dur || 1);
            if (typeof syncBgTransport === 'function') { syncBgTransport(); }
            drawOverviewOn(canvas, card, frac);
          }
        }
      }
      var playBtn = card.querySelector('.mid-card__play');
      if (playBtn) {
        playBtn.addEventListener('click', function (e) {
          e.stopPropagation();
          togglePlayPause(card);
        });
      }
      
      var chordToggle = card.querySelector('.mid-card__chord-toggle');
      if (chordToggle) {
        chordToggle.textContent = 'Chord: ' + (showChordTranscriber ? 'ON' : 'OFF');
        chordToggle.addEventListener('click', function (e) {
          e.stopPropagation();
          showChordTranscriber = !showChordTranscriber;
          document.querySelectorAll('.mid-card__chord-toggle').forEach(function (b) {
            b.textContent = 'Chord: ' + (showChordTranscriber ? 'ON' : 'OFF');
          });
          var chordChk = document.querySelector('.midg__opt-chord');
          if (chordChk) {
            chordChk.checked = showChordTranscriber;
          }
          if (current) {
            renderStoryCanvases();
          }
        });
      }
    });
  }

  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', init); } else { init(); }
})();
